import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import TradeMarginReviews from "./TradeMarginReviews";
import { useTabParam } from "../../lib/useTabParam";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal, TeamMember, TeamRole } from "../../lib/types";
import { fmtUSD, fmtInt } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { mergeRates, ratesMapFromRows } from "../../lib/commissionRates";
import { fetchCanvasserByDeal, type CanvasserRef } from "../../lib/canvasserCredit";
import { calcScope, calcNonCollectDeduction } from "../../lib/scopeCalc";
import {
  PAYMENT_PLAN_LABELS, commissionFirstPaymentHold, fetchCurrentPlans, type BuildPaymentPlan,
} from "../../lib/buildPaymentPlan";
import { isPmPayRow, pmPayLabel, splitPmPay } from "../../lib/pmPay";
import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import CommissionEditModal from "./CommissionEditModal";
import RoofCrewSection from "./RoofCrewSection";
import RoofCrewApprovedSection from "./RoofCrewApprovedSection";
import RSPoolSection from "./RSPoolSection";
import CanvasserPayoutsSection from "./CanvasserPayoutsSection";
import TLMonthlyBonusSection from "./TLMonthlyBonusSection";
import PaidProvenanceChip from "./PaidProvenanceChip";
import CommissionStatementsCard from "./CommissionStatementsCard";
import CommissionReportCard from "./CommissionReportCard";
import {
  EMPTY_STORED,
  SALES_LINE_TYPES,
  fetchCommissionPayments,
  fetchMemberNames,
  indexPayments,
  lineFromRow,
  payeeIdsNotIn,
  payeeName,
  salesPaidFromStore,
  storedRsPoolLines,
  type CommissionPaymentRow,
  type StoredPayments,
} from "../../lib/commissionPayments";
import {
  BILL_CHIP_CLASS,
  EMPTY_BILL_INDEX,
  billChipText,
  billChipTone,
  fetchBooksPayoutBills,
  liveBillFor,
  pullResultText,
  type BillIndex,
  type BooksPayoutBill,
} from "../../lib/booksPayoutBills";
import { RS_POOL_RATE_LIVE, rsPoolLinesFor } from "../../lib/rsPoolLines";
import { FIVE_STAR_REVIEW_SPIFF } from "../../lib/weeklySpiffs";
// Oct 29, 2026 — the floor math and labels for the pending card's trade strip (tradeText, re-exported by lib/trades).
import { BILLED_TO_LABELS, WORK_LABELS, fmtMoney2, isAddendumOpen, priceAtMargin, type BilledTo, type MarginReview, type TradeWork } from "../../lib/trades";

/**
 * Commission Approval Board — shared view for Chad, Jennifer, Shannon, Greg.
 *
 * Tabs:
 *   Pending    — Chad approves RM/TL/ASM commissions; Jennifer approves RS pool
 *   Approved   — Grouped by person with expandable breakdown; Shannon marks paid
 *   Paid       — Grouped by person, historical
 *
 * Flow (Oct 8, 2026 — approvals become Books bills; migration
 * 20261008_books_payout_bills, Books 0053):
 *
 *   1. A deal enters the queue — `commission_ready` stage (old single-track
 *      path: the stage advances with the flags) or `ready_for_supps`
 *      (supplement lifecycle: the stage never changes) — and
 *      commission_ready_date is stamped.
 *   2. Chad approves → the `commission_approve` RPC stores the per-person
 *      lines (the SAME lines payLinesFor() builds for mark-paid), sets
 *      commission_approved=true (+ stage for the old path) and makes ONE
 *      Books bill per person for the approval batch. A deal under the pay
 *      hold (credits ≥ $501 until the balance is collected) gets a `held`
 *      bill, sent when the hold clears. A trigger sends each bill to Books.
 *      "Un-approve…" (`commission_unapprove`) withdraws the bill — refused
 *      once Books has paid it.
 *   3. Shannon pays the bill IN BOOKS (Banking → Transactions, from the bank
 *      feed; draws are netted there). Books reports back; the CRM's pull
 *      (`books_payout_pull`, cron every 5 min, or "Check Books now" on this
 *      board) writes the commission_payments rows (source 'books_bill_paid')
 *      and flips commission_paid=true (+ stage for the old path).
 *      The Approved tab shows a chip per person group — "Bill in Books — $X ·
 *      sent <date>" / "held: …" / "paid in Books on …" / "Books refused: …" —
 *      and hides Mark paid / Pay All for those groups.
 *   4. Deals approved BEFORE Oct 8, 2026 have no bill: Shannon's Mark paid /
 *      Pay All (`commission_mark_paid`) stays for those, and for the 5★
 *      true-ups and rate catch-ups, which are not part of the bills.
 *
 *   Bucketing is driven by the boolean flags, not stage:
 *     Pending  = commission_ready_date IS NOT NULL AND commission_approved = false
 *     Approved = commission_approved = true AND commission_paid = false
 *     Paid     = commission_paid = true
 */

// Stages that belong to the old single-track commission flow.
// For these, approving/paying also advances the stage.
const OLD_COMMISSION_STAGES = new Set([
  "commission_ready",
  "commission_approved",
  "commission_paid",
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CommissionDeal = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "claim_number"
  | "stage"
  | "owner_id"
  | "split_rm_id"
  | "commissionable_revenue"
  | "rm_commission"
  | "misc_credit"
  | "misc_credit_penalty"
  | "non_collect_split_rm_pct"
  | "non_collect_rm_amount"
  | "non_collect_tl_amount"
  | "non_collect_asm_amount"
  | "non_collect_acknowledged"
  | "non_collect_deduct_active"
  | "acv_amount"
  | "deductible"
  | "build_completed_date"
  | "commission_ready_date"
  | "deal_type"
  | "total_squares"
  // Ship 5 gates + Roof Crew section dependencies
  | "roof_crew_payment_approved"
  | "pm_workbook"
  // Boolean commission status flags (source of truth for bucketing)
  | "commission_approved"
  | "commission_approved_at"
  | "commission_paid"
  | "commission_paid_at"
  // May 15, 2026 — scopeCalc inputs. Used so getDealRev / getRSDealRev /
  // approvalBlockReason can derive firstPaymentToBardin + commissionable
  // from the real scope rather than the old `acv + deductible` proxy that
  // ignored credits, supplements, PWI, etc.
  | "rcv_total"
  | "depreciation_total"
  | "supplements"
  | "supplement_invoice_amount"
  | "supplement_approved_amount"
  | "pwi_code"
  | "upgrades"
  | "retail_profit"
  | "at_cost_retail"
  | "acv_credit"
  | "dep_credit"
  | "non_commissionable"
  | "trade_margin_loss"
  | "punch_list_items"
  | "acv_only"
  | "balance_written_off"
  // Oct 22, 2026 — the first-payment hold reads cleared and still-clearing money.
  | "pre_build_amount_received"
  | "post_build_amount_received"
  | "pre_build_amount_in_flight"
  | "post_build_amount_in_flight"
  // Jun 11, 2026 — retail option. Pool = contract − sq × base; split per job.
  | "is_retail"
  | "retail_contract_total"
  | "retail_squares"
  | "retail_base_per_square"
  | "retail_at_cost_total"
  // Jul 16, 2026 — new commission structure (per-deal RM rate for install
  // agreements signed on/after Jul 20, 2026). Signed date keys the structure
  // switch; 5★ fields drive the 12/11% tiers + true-up; balance drives the
  // ≥$501-credit pay hold ("paid upon closure" = collected in full).
  | "install_agreement_signed_date"
  | "five_star_review_verified"
  | "five_star_review_verified_at"
  | "five_star_trueup_paid_at"
  | "post_build_balance_remaining"
  // Jul 23, 2026 — age chips (days in stage / since created) on cards.
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
> & {
  rs_pool_approved?: boolean | null;
  rs_pool_approved_at?: string | null;
  rs_pool_paid?: boolean | null;
  rs_pool_paid_at?: string | null;
  assigned_rs_id?: string | null;
  // Sep 30, 2026 — one-time 1-pt catch-up on full-scope deals paid at 10%
  // before the 11% pre-review rule.
  full_scope_catchup_paid_at?: string | null;
  // Sep 30, 2026 — Chad's per-person edits ({"<member_id>:rm|tl|asm": gross $}).
  commission_line_overrides?: Record<string, number> | null;
  // Oct 6, 2026 — Chad released the ≥$501-credit pay hold (audited RPC).
  commission_hold_released_at?: string | null;
  commission_hold_released_by?: string | null;
  commission_hold_release_reason?: string | null;
  // Oct 28, 2026 — Chad's override of the first-payment hold (audited RPC).
  commission_first_payment_override_at?: string | null;
  commission_first_payment_override_by?: string | null;
  commission_first_payment_override_reason?: string | null;
  // Oct 29, 2026 — the trade money on the deal row, kept by trade_sync_deal (migration 20261029f). Greg: a
  // homeowner's price above the 30% floor "works just like retail commissions". trade_upside is the pool the
  // board pays as the trade_upside line (insurance deals); trade_ho_total / trade_ho_floor / trade_ho_lines are
  // calcScope's inputs (the homeowner-billed trades join the invoice and leave commissionable revenue; on a retail
  // deal the upside joins the retail pool). Declared here rather than picked from Deal so this file type-checks
  // with or without the Deal fields in types.ts.
  trade_upside?: number | null;
  trade_ho_total?: number | null;
  trade_ho_floor?: number | null;
  trade_ho_lines?: unknown;
};

// Single source of truth for the deals columns the commission math needs.
// Used by the finance CommissionBoard load AND the self-service MyCommissionsTab
// so groupByPerson() receives identical inputs in both places (no drift).
export const COMMISSION_DEAL_SELECT =
  "id,homeowner_name,property_street,claim_number,stage,owner_id,split_rm_id,commissionable_revenue,rm_commission,misc_credit,misc_credit_penalty,non_collect_split_rm_pct,non_collect_rm_amount,non_collect_tl_amount,non_collect_asm_amount,non_collect_acknowledged,non_collect_deduct_active,acv_amount,deductible,build_completed_date,commission_ready_date,commission_approved,commission_approved_at,commission_paid,commission_paid_at,deal_type,total_squares,rs_pool_approved,rs_pool_approved_at,rs_pool_paid,rs_pool_paid_at,assigned_rs_id,roof_crew_payment_approved,pm_workbook,rcv_total,depreciation_total,supplements,supplement_invoice_amount,supplement_approved_amount,pwi_code,upgrades,retail_profit,at_cost_retail,acv_credit,dep_credit,non_commissionable,trade_margin_loss,trade_upside,trade_ho_total,trade_ho_floor,trade_ho_lines,punch_list_items,acv_only,balance_written_off,is_retail,retail_contract_total,retail_squares,retail_base_per_square,retail_at_cost_total,install_agreement_signed_date,five_star_review_verified,five_star_review_verified_at,five_star_trueup_paid_at,full_scope_catchup_paid_at,commission_line_overrides,commission_hold_released_at,commission_hold_released_by,commission_hold_release_reason,commission_first_payment_override_at,commission_first_payment_override_by,commission_first_payment_override_reason,post_build_balance_remaining,pre_build_amount_received,post_build_amount_received,pre_build_amount_in_flight,post_build_amount_in_flight,stage_entered_at,phase_entered_at,created_at";

// Approval gate result: non-null disables Approve and says why. Both gates are
// hard: the roof crew payment, and since Oct 22, 2026 the first payment (it was
// a soft "Approve anyway" from Jul 15). `firstPayment` marks the second.
type ApprovalGate = { message: string; firstPayment?: boolean } | null;

type TabKey = "pending" | "approved" | "paid";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "paid", label: "Paid" },
];

// Category filter — slices the commission view into payment buckets:
//   sales    = RM / TL / ASM commissions on deals
//   roofteam = RS Pool (drone/inspector team — 1.5% per deal split)
//   crew     = Installer crew vendor payments (Risol, etc. — roof_crew_payments table)
//   pm       = the PM's pay (Ramiro's $100 per build + biweekly base). It rides the
//              Risol rail in roof_crew_payments too, but Bardin pays it separately
//              and sends Risol its own email (Oct 20, 2026, Greg) — isPmPayRow
//              (lib/pmPay) keeps it out of Crew Pay. Since Oct 21 it goes to Books as one
//              bill of kind 'pm' every other Friday (pay emails, Ship 2).
//   all      = everything stacked (legacy default)
type CategoryKey = "all" | "sales" | "roofteam" | "crew" | "pm" | "canvassers";

const CATEGORIES: Array<{ key: CategoryKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "crew", label: "Crew Pay" },
  { key: "pm", label: "PM Pay" },
  { key: "roofteam", label: "Roof Team Pay" },
  { key: "sales", label: "Sales Pay" },
  { key: "canvassers", label: "Canvassers" },
];

// Date range presets for the Paid tab. "custom" is reserved for user-chosen
// start/end dates entered via the calendar inputs (state lives in PaidView).
type DateRangeKey = "all" | "this_month" | "last_month" | "last_30" | "last_90" | "this_year" | "last_year" | "custom";

const DATE_RANGE_OPTIONS: Array<{ key: DateRangeKey; label: string }> = [
  { key: "all",        label: "All time" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30",    label: "Last 30 days" },
  { key: "last_90",    label: "Last 90 days" },
  { key: "this_year",  label: "This year" },
  { key: "last_year",  label: "Last year" },
];

const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

function dateRangeFor(key: DateRangeKey, customFrom?: string, customTo?: string): { from: Date | null; to: Date | null } {
  const now = new Date();
  switch (key) {
    case "all":        return { from: null, to: null };
    case "this_month": return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last  = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    case "last_30":    return { from: startOfDay(new Date(now.getTime() - 30 * 86400_000)), to: endOfDay(now) };
    case "last_90":    return { from: startOfDay(new Date(now.getTime() - 90 * 86400_000)), to: endOfDay(now) };
    case "this_year":  return { from: startOfDay(new Date(now.getFullYear(), 0, 1)), to: endOfDay(now) };
    case "last_year":  return { from: startOfDay(new Date(now.getFullYear() - 1, 0, 1)), to: endOfDay(new Date(now.getFullYear() - 1, 11, 31)) };
    case "custom": {
      const from = customFrom ? startOfDay(new Date(customFrom + "T00:00:00")) : null;
      const to   = customTo   ? endOfDay(new Date(customTo + "T00:00:00"))   : null;
      return { from, to };
    }
  }
}

// Commission rates
const RM_BASE_RATE = 0.10;
const TL_OVERRIDE_RATE = 0.01;
const ASM_OVERRIDE_RATE = 0.04;
// RS pool rate lives at RS_POOL_RATE_LIVE below — single source of truth for the paid view.

// ---------------------------------------------------------------------------
// Permission helpers
// ---------------------------------------------------------------------------

function canApprove(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "sales_director" || role === "admin";
}

function canMarkPaid(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "finance" || role === "admin";
}

// ---------------------------------------------------------------------------
// Revenue / commission helpers
// ---------------------------------------------------------------------------

function getDealRev(d: CommissionDeal): number {
  // Prefer Shannon's explicit `commissionable_revenue` override; otherwise
  // derive from scopeCalc. The old fallback (`acv + deductible`) ignored
  // credits, supplements, PWI, upgrades, etc. — wrong on any non-trivial
  // scope. May 15, 2026: replaced the Ship-5 proxy with calcScope().
  return Number(d.commissionable_revenue) || calcScope(d).commissionable;
}

function isRetailDeal(d: CommissionDeal): boolean {
  return d.is_retail === true;
}

// The RM's personal rate. Per-person from team_roster.commission_rate (e.g.
// James/Rico/Alex at 12%), falling back to RM_BASE_RATE (10%) when unset.
// Jun 29, 2026 — the rate was previously hardcoded 10% for everyone.
export function rmRateFor(m: TeamMember | undefined): number {
  const r = Number(m?.commission_rate);
  return Number.isFinite(r) && r > 0 ? r : RM_BASE_RATE;
}

function getRMComm(d: CommissionDeal, rate: number = RM_BASE_RATE): number {
  // Retail (Jun 11, 2026): the over-base pool is the commission, split per job
  // by Chad. Until Chad sets an explicit rm_commission, the full pool defaults
  // to the RM line (the common no-TL/ASM-share case). Chad reallocates via
  // ✎ Edit (flat amounts) when TL/ASM get a cut.
  if (isRetailDeal(d)) {
    return Number(d.rm_commission) || Math.max(0, getDealRev(d));
  }
  return Number(d.rm_commission) || getDealRev(d) * rate;
}

// ---------------------------------------------------------------------------
// Jul 16, 2026 — NEW COMMISSION STRUCTURE
// (install agreements signed on/after Mon Jul 20, 2026 ET — Greg's whiteboard)
//
// The RM rate becomes per-DEAL, not per-person:
//   12%  ≥ $20k commissionable · ZERO credits ("full insurance scope") · 5★ verified
//   11%  ≥ $20k · full insurance scope, no verified 5★ yet (Greg, Sep 30, 2026 —
//        was 10%; deals already commission-paid before FULL_SCOPE_11_SINCE keep
//        the 10% they were paid at so history and true-ups stay honest)
//   11%  ≥ $20k commissionable · credits ≤ $500 · 5★ verified
//   11%  < $20k commissionable · credits ≤ $500 · 5★ verified (Greg, Oct 19,
//        2026: "even if job is under $20k, a five star review 1% is still
//        achievable" — it was 10% flat under $20k)
//   10%  everything else (credits ≤ $500 with no verified 5★ yet)
//   12% is the ceiling — a review never lifts a line above it, so a rep on an
//   achieved 12% rate gains nothing from one.
//   credits ≥ $501 → 10% for EVERYONE (managers too) AND the deal's commission
//   payment is HELD until the balance is collected in full (payHoldReason).
//
// "Credits" = misc_credit + non_commissionable (non-collect). ACV credits are
// deliberately EXCLUDED (Greg, Jul 16). Reps who have ACHIEVED a higher personal
// rate (team_roster.commission_rate ≥ 0.12 — currently James/Rico/Alex; an
// earned status, NOT a manager designation) keep that achieved rate flat on
// their own deals with only the credit rule.
// Splits: the deal's size/credits/review set the tier; each half is rated by who
// holds it (achieved-rate half → flat achieved rate, base half → tier table).
// The deal's non-collect bucket deduction (non-collect + the trade shortfalls
// Chad deducted, over the first $500 — getNonCollectDeductionBreakdown) STACKS
// on top of the rate drop. 5★ = Chad-verified flag on the deal (manual Google check).
// Oct 19, 2026 (Greg: "keep the same pay out. we just want to recognize it
// weekly and for it to pay out with spiffs"): for a review verified on or after
// FIVE_STAR_REVIEW_SPIFF.startsAt the line stays at the pre-review rate and the
// point pays as the 5-Star Review SPIFF in the Tuesday run (its amount comes
// from five_star_review_point() — the SQL mirror of this table; change both
// together). A review verified before that keeps the point here, and a deal
// paid at the pre-review rate before such a review gets the delta trued up on
// the next payout (ReviewTrueUpPanel).
//
// Legacy deals (signed before the cutoff, unsigned, or retail) keep the
// per-person team_roster.commission_rate via rmRateFor.
// ---------------------------------------------------------------------------

// Mon Jul 20, 2026 00:00 ET (EDT = UTC−4).
const NEW_COMM_STRUCTURE_CUTOFF_MS = Date.parse("2026-07-20T04:00:00Z");
const NEW_STRUCTURE_BIG_DEAL_MIN = 20000; // floor for the 12% tier and the 11% full-scope pre-review rate
const NEW_STRUCTURE_CREDIT_LIMIT = 500;   // credits above this → 10% + pay hold
const NEW_STRUCTURE_TOP_RATE = 0.12;
const NEW_STRUCTURE_MID_RATE = 0.11;
// Sep 30, 2026: full-insurance-scope $20k+ deals pay 11% before the 5★ review
// (12% after). A deal commission-paid before this moment was paid at 10%.
const FULL_SCOPE_11_SINCE_MS = Date.parse("2026-09-30T14:00:00Z");
// A rep whose ACHIEVED personal rate (team_roster.commission_rate) is at or
// above this keeps that rate flat under the new structure (credit rule only).
const NEW_STRUCTURE_ACHIEVED_MIN = 0.12;
// Oct 19, 2026: a 5★ review verified at/after this pays its point as the
// 5-Star Review SPIFF, not on the commission line (Mon Oct 19 00:00 ET).
const FIVE_STAR_SPIFF_SINCE_MS = Date.parse(FIVE_STAR_REVIEW_SPIFF.startsAt);

/** Is this deal's verified 5★ review paid inside the commission — the old way?
 *  True only for a review verified before the 5-Star Review SPIFF started (or
 *  verified before verified_at was recorded). Later reviews pay the point as
 *  the SPIFF, and an unverified review will be paid that way when it lands. */
export function reviewInCommission(d: CommissionDeal): boolean {
  if (d.five_star_review_verified !== true) return false;
  if (!d.five_star_review_verified_at) return true; // verified pre-feature
  return Date.parse(d.five_star_review_verified_at) < FIVE_STAR_SPIFF_SINCE_MS;
}

/** Has this rep achieved the higher personal rate (12%+ — an earned status,
 *  not a manager designation; currently James/Rico/Alex)? */
function hasAchievedRate(m: TeamMember | undefined): boolean {
  return rmRateFor(m) >= NEW_STRUCTURE_ACHIEVED_MIN;
}

export function isNewCommStructure(d: CommissionDeal): boolean {
  if (isRetailDeal(d)) return false; // retail keeps the pool model
  if (!d.install_agreement_signed_date) return false;
  const t = Date.parse(d.install_agreement_signed_date);
  return Number.isFinite(t) && t >= NEW_COMM_STRUCTURE_CUTOFF_MS;
}

/** misc credit + non-collect. ACV credits deliberately excluded. */
export function dealCreditsForCommission(d: CommissionDeal): number {
  const misc = Math.max(0, Number(d.misc_credit) || 0);
  const nc = Math.max(0, Number(d.non_commissionable) || 0);
  return misc + nc;
}

export interface DealRateInfo {
  newStructure: boolean;
  /** The rate this person's line pays at today. */
  rate: number;
  /** The rate once a 5★ review is verified (=== rate when review-independent). */
  fullRate: number;
  /** True → paying preReviewRate now; true-up to fullRate when the 5★ verifies pre-closure. */
  reviewPending: boolean;
  /** What the line pays without a verified 5★ (the true-up baseline). */
  preReviewRate: number;
  /** credits ≥ $501 → payment held until the balance is collected in full. */
  heldToClosure: boolean;
  credits: number;
  /** Rep has achieved 12%+ personal-rate status → flat achieved rate, no tiers. */
  achievedRate: boolean;
  /** Oct 19, 2026 — the review point (fullRate − preReviewRate) is paid as the
   *  5-Star Review SPIFF, not on this line: the line pays preReviewRate even
   *  once the review is verified. False when there is no point, or when an
   *  older review keeps it inside the commission. */
  reviewPaysAsSpiff: boolean;
}

/** Per-deal, per-person RM rate. `m` is the RM whose half is being rated
 *  (owner or split — an achieved-rate half and a base-rate half rate independently). */
export function dealRateInfo(d: CommissionDeal, m: TeamMember | undefined): DealRateInfo {
  if (!isNewCommStructure(d)) {
    const r = rmRateFor(m);
    return { newStructure: false, rate: r, fullRate: r, reviewPending: false, preReviewRate: r, heldToClosure: false, credits: 0, achievedRate: false, reviewPaysAsSpiff: false };
  }
  const credits = dealCreditsForCommission(d);
  const achieved = hasAchievedRate(m);
  if (credits > NEW_STRUCTURE_CREDIT_LIMIT) {
    // ≥$501 credits: floor rate for everyone (achieved-rate reps included) + pay hold.
    return { newStructure: true, rate: RM_BASE_RATE, fullRate: RM_BASE_RATE, reviewPending: false, preReviewRate: RM_BASE_RATE, heldToClosure: true, credits, achievedRate: achieved, reviewPaysAsSpiff: false };
  }
  if (achieved) {
    // Earned status: keep the achieved personal rate flat — no size/review tiers.
    const r = rmRateFor(m);
    return { newStructure: true, rate: r, fullRate: r, reviewPending: false, preReviewRate: r, heldToClosure: false, credits, achievedRate: true, reviewPaysAsSpiff: false };
  }
  // $20k+ full scope: 11% → 12% with the review; every other job with $500 or
  // less in credits — under $20k included (Greg, Oct 19, 2026) — 10% → 11%.
  const fullScopeBig = getDealRev(d) >= NEW_STRUCTURE_BIG_DEAL_MIN && credits <= 0;
  const full = fullScopeBig ? NEW_STRUCTURE_TOP_RATE : NEW_STRUCTURE_MID_RATE;
  const verified = d.five_star_review_verified === true;
  const paidAt = d.commission_paid && d.commission_paid_at ? Date.parse(d.commission_paid_at) : NaN;
  // Paid at 10% under the old rule — unless the 1-pt rate catch-up has since
  // been paid, which brings the line up to the 11% baseline.
  const paidUnderOldRule = Number.isFinite(paidAt) && paidAt < FULL_SCOPE_11_SINCE_MS && !d.full_scope_catchup_paid_at;
  const pre = fullScopeBig && !paidUnderOldRule ? NEW_STRUCTURE_MID_RATE : RM_BASE_RATE;
  // Oct 19, 2026: the point is on this line only for an older review; a
  // review verified since (or still to come) pays it as the SPIFF.
  const inCommission = reviewInCommission(d);
  return { newStructure: true, rate: inCommission ? full : pre, fullRate: full, reviewPending: !verified, preReviewRate: pre, heldToClosure: false, credits, achievedRate: false, reviewPaysAsSpiff: !inCommission && full > pre };
}

/** Non-null when this deal's commission payment is held to closure (new
 *  structure, credits ≥ $501, balance not yet collected in full). Balance =
 *  post_build_balance_remaining (derived, cleared money; write-offs count;
 *  NULL = final invoice not set yet → still held). */
export function payHoldReason(d: CommissionDeal): string | null {
  // Oct 6, 2026 — Chad can release the hold (mirrored in commission_pay_hold()).
  if (d.commission_hold_released_at) return null;
  if (!isNewCommStructure(d)) return null;
  if (dealCreditsForCommission(d) <= NEW_STRUCTURE_CREDIT_LIMIT) return null;
  const raw = d.post_build_balance_remaining;
  const bal = raw === null || raw === undefined ? null : Number(raw);
  if (bal !== null && bal <= 0.005) return null; // collected in full
  return bal === null
    ? "Credits ≥ $501 — payment is held until the balance is collected in full (final invoice not set yet)."
    : `Credits ≥ $501 — payment is held until the balance is collected in full (${fmtUSD(bal)} still outstanding).`;
}

// --- 5★ review true-up ------------------------------------------------------
// A deal commission-paid at 10% before its 5★ review was verified owes the
// 1–2pt delta once Chad verifies the review — provided the deal hasn't closed.
// Oct 19, 2026: only for reviews verified before the 5-Star Review SPIFF
// started (reviewInCommission). A later review's point pays as that SPIFF, so
// it never shows here — and commission_trueup_mark_paid refuses it.

const TRUEUP_CLOSED_STAGES = new Set(["closed_won", "closed_lost", "cancelled"]);

export interface TrueUpLine {
  personId: string;
  name: string;
  delta: number; // (fullRate − 10%) × rev × share
}

/** The per-person true-up deltas a verified 5★ review unlocks on this deal.
 *  Empty when the formula doesn't apply (explicit rm_commission, achieved-rate
 *  reps, held-to-closure, legacy). */
export function trueUpDeltas(d: CommissionDeal, members: Record<string, TeamMember>): TrueUpLine[] {
  const out: TrueUpLine[] = [];
  if (Number(d.rm_commission)) return out; // Chad-set gross — formula hidden, no true-up
  const isSplit = !!d.split_rm_id;
  const rev = getDealRev(d);
  const halves: Array<string | null> = isSplit ? [d.owner_id, d.split_rm_id] : [d.owner_id];
  for (const pid of halves) {
    if (!pid || !members[pid]) continue;
    const info = dealRateInfo(d, members[pid]);
    if (!info.newStructure || info.heldToClosure || info.achievedRate) continue;
    if (lineOverride(d, pid, "rm") !== null) continue; // Chad set this person's amount by hand
    const delta = (info.fullRate - info.preReviewRate) * rev * (isSplit ? 0.5 : 1);
    if (delta > 0.005) out.push({ personId: pid, name: members[pid].full_name, delta });
  }
  return out;
}

/** Was this deal commission-paid BEFORE its 5★ review was verified?
 *  (verified_at after paid_at, or not verified at all) */
function paidBeforeReview(d: CommissionDeal): boolean {
  if (!d.commission_paid || !d.commission_paid_at) return false;
  if (!d.five_star_review_verified) return true;
  if (!d.five_star_review_verified_at) return false; // verified pre-feature — treat as paid at full rate
  return Date.parse(d.five_star_review_verified_at) > Date.parse(d.commission_paid_at);
}

export type TrueUpStatus = "owed" | "awaiting_review";

/** Sep 30, 2026 — full-scope $20k+ deals commission-paid at 10% before the
 *  11% pre-review rule, owed the 1-pt difference once (Greg). Per non-achieved
 *  half. Skipped when the review is already verified (the review true-up
 *  covers the full 2 pts from 10%) or an explicit Chad-set amount was used. */
export function rateCatchups(
  deals: CommissionDeal[],
  members: Record<string, TeamMember>,
): Array<{ deal: CommissionDeal; lines: TrueUpLine[] }> {
  const out: Array<{ deal: CommissionDeal; lines: TrueUpLine[] }> = [];
  for (const d of deals) {
    if (!d.commission_paid || !d.commission_paid_at || !isNewCommStructure(d)) continue;
    if (d.full_scope_catchup_paid_at || d.five_star_review_verified || Number(d.rm_commission)) continue;
    if (Date.parse(d.commission_paid_at) >= FULL_SCOPE_11_SINCE_MS) continue;
    if (dealCreditsForCommission(d) > 0) continue;
    const rev = getDealRev(d);
    if (rev < NEW_STRUCTURE_BIG_DEAL_MIN) continue;
    const isSplit = !!d.split_rm_id && d.split_rm_id !== d.owner_id;
    const lines: TrueUpLine[] = [];
    for (const pid of isSplit ? [d.owner_id, d.split_rm_id] : [d.owner_id]) {
      if (!pid || !members[pid] || hasAchievedRate(members[pid])) continue;
      if (lineOverride(d, pid, "rm") !== null) continue; // amount set by hand
      const delta = (NEW_STRUCTURE_MID_RATE - RM_BASE_RATE) * rev * (isSplit ? 0.5 : 1);
      if (delta > 0.005) lines.push({ personId: pid, name: members[pid].full_name, delta });
    }
    if (lines.length) out.push({ deal: d, lines });
  }
  return out;
}

/** Deals with a live review true-up angle: paid at 10%, and either the review
 *  has since verified (delta OWED, not yet paid out) or it hasn't verified yet
 *  and the deal is still open (worklist for Chad). */
export function reviewTrueUps(
  deals: CommissionDeal[],
  members: Record<string, TeamMember>,
): Array<{ deal: CommissionDeal; status: TrueUpStatus; lines: TrueUpLine[] }> {
  const out: Array<{ deal: CommissionDeal; status: TrueUpStatus; lines: TrueUpLine[] }> = [];
  for (const d of deals) {
    if (!d.commission_paid || !isNewCommStructure(d)) continue;
    // Oct 19, 2026: a review verified since — or one not verified yet — pays
    // its point as the 5-Star Review SPIFF, never as a true-up.
    if (!reviewInCommission(d)) continue;
    if (!paidBeforeReview(d)) continue;
    const lines = trueUpDeltas(d, members);
    if (lines.length === 0) continue;
    if (d.five_star_review_verified) {
      if (!d.five_star_trueup_paid_at) out.push({ deal: d, status: "owed", lines });
    } else if (!TRUEUP_CLOSED_STAGES.has(d.stage ?? "")) {
      out.push({ deal: d, status: "awaiting_review", lines });
    }
  }
  return out;
}

// The revenue base a TL/ASM override applies to. (Jun 17, 2026 — Greg: Dale
// Heard, a split deal — Andrew Church (owner) + James Kessler (split RM), and
// James is also Andrew's team_lead. James already earns the RM rate on his own
// half, so his TL override must come ONLY from Andrew's portion, not the full
// deal.) Rule: when the override recipient is one of the split RMs on this deal,
// drop their own RM portion (50% on a split) from the base. Same for ASM when
// the ASM is the split. When the recipient isn't an RM on the deal (the normal
// case), the base is the full revenue.
function overrideBaseRev(d: CommissionDeal, recipientId: string | null | undefined): number {
  // Jul 21, 2026 (Greg): no beneficiary → no override. A null recipient used to
  // fall through to full revenue, which made the pending card show a TL (1%)
  // figure on sole-owner deals of reps with no team lead above them (Rico,
  // James). Nobody earns that line — show $0.
  if (!recipientId) return 0;
  const rev = getDealRev(d);
  if (!d.split_rm_id) return rev; // not a split deal → full rev
  // 50/50 split. Subtract the recipient's own RM share if they're an RM here.
  let fraction = 1;
  if (recipientId === d.owner_id) fraction -= 0.5;
  if (recipientId === d.split_rm_id) fraction -= 0.5;
  return rev * fraction;
}

// ---------------------------------------------------------------------------
// Canvasser carve-out (May 22, 2026 — Ship 2)
//
// On a canvasser lead (which deals count: lib/canvasserCredit, Oct 19, 2026),
// the RM rate drops 5 percentage points
// (10→5, 11→6, 12→7). Those 5 points of commissionable revenue are deducted from
// the RM line (split 50/50 across owner + split RM) and feed the captured-revenue
// pool credited to the canvasser. TL override, ASM override, and the RS pool are
// NOT affected. The deduction is capped at the RM's gross so it never books more
// than the RM actually gave up. Pct mirrors finance_settings.canvasser_carveout_pct.
// Spec: memory/features/canvasser-program.md
// ---------------------------------------------------------------------------
const CANVASSER_CARVEOUT_PCT = 0.05;

export type { CanvasserRef };

/** The carve-out for a deal, or null when it isn't canvasser-sourced. */
function getCanvasserCarveout(
  d: CommissionDeal,
  canvasserByDeal: Record<string, CanvasserRef>,
): { canvasser: CanvasserRef; captured: number } | null {
  const c = canvasserByDeal[d.id];
  if (!c) return null;
  const captured = Math.min(getDealRev(d) * CANVASSER_CARVEOUT_PCT, getRMComm(d));
  return { canvasser: c, captured: Math.max(0, captured) };
}

// ---------------------------------------------------------------------------
// The non-collect bucket (May 15, 2026; split Jun 17, 2026; blended Oct 29, 2026)
//
// One bucket per deal = non-collect revenue (deals.non_commissionable, insurance
// only, when its gate non_collect_deduct_active is on) + the trade price
// shortfalls Chad deducted (deals.trade_margin_loss). The first $500 of the
// bucket is forgiven; what is over comes off the commissions. Oct 29, 2026
// (Greg): "On the commission board, let it blend into non-collect revenue. If
// they have not used the $500 and this is $300 below 30%, it deducts from
// there." Before that the $500 forgave only the non-collect part and a
// shortfall came off dollar for dollar. See getNonCollectBucket.
//
// Jun 17, 2026 (Greg): the overage splits across RM + TL + ASM as explicit
// per-deal dollar amounts (deals.non_collect_rm_amount / _tl_amount / _asm_amount),
// set via the Commission board editor and required to total the overage. When no
// explicit amounts are set, falls back to the legacy RM/TL percentage split
// (deals.non_collect_split_rm_pct, default 0.75 = 75% RM / 25% TL, ASM = 0; 100%
// RM when no TL). RS pool is still NOT impacted. The gate (nonCollectPolicyApplies)
// is the dedicated non_collect_deduct_active flag (default true) so the deduction
// applies regardless of HO acknowledgement going forward; already-paid
// grandfathered deals carry false and stay gross on the non-collect part — a
// shortfall Chad deducted still counts against the $500 on them (he chose it here).
// ---------------------------------------------------------------------------

// Does the OWNER have a TL? Kept for the legacy non-collect 75/25 default only
// (that default was designed around the owner's TL). Everything else uses the
// per-half resolution below.
function ownerHasTl(d: CommissionDeal, members: Record<string, TeamMember>): boolean {
  const tlId = d.owner_id ? members[d.owner_id]?.team_lead_id : null;
  return !!(tlId && members[tlId]);
}

// TL override bases, per RM half. (Jul 21, 2026 — Greg: each half of the deal
// pays 1% to THAT half's RM's own team lead. Hanley — owner Alex Poma has no
// TL → $0 on his half; split Briggs Clayton rolls up to James Kessler → James
// earns 1% of Briggs's half only.) A half whose RM has no TL contributes
// nothing (Rico/James sole-owner deals stay TL $0). A TL never earns on their
// own RM half (their team_lead_id is null — TLs roll up via asm_id), which
// also preserves the Jun 17 Dale Heard rule. Returns tlId → base revenue;
// both halves under the same TL combine into one base.
function dealTlBases(d: CommissionDeal, members: Record<string, TeamMember>): Record<string, number> {
  const rev = getDealRev(d);
  const isSplit = !!d.split_rm_id;
  const halves: Array<[string | null | undefined, number]> = [
    [d.owner_id, isSplit ? 0.5 : 1],
    [d.split_rm_id, isSplit ? 0.5 : 0],
  ];
  const out: Record<string, number> = {};
  for (const [rmId, frac] of halves) {
    if (!rmId || frac <= 0) continue;
    const tlId = members[rmId]?.team_lead_id;
    if (!tlId || !members[tlId] || tlId === rmId) continue;
    out[tlId] = (out[tlId] ?? 0) + rev * frac;
  }
  return out;
}

function dealHasTl(d: CommissionDeal, members: Record<string, TeamMember>): boolean {
  return Object.keys(dealTlBases(d, members)).length > 0;
}

// The deal's ASM. Resolves from the owner's asm_id, falling back to the split
// RM's asm_id when the owner has none. (Jul 21, 2026 — Greg: Hanley — owner
// Alex Poma IS the ASM so his own asm_id is null, split Briggs Clayton rolls up
// to Alex. The ASM override must resolve to Alex and, via overrideBaseRev
// dropping his own RM half, pay 4% of the half he doesn't own.) TL resolution
// deliberately stays owner-only.
function dealAsmId(d: CommissionDeal, members: Record<string, TeamMember>): string | null {
  const ownerAsm = d.owner_id ? members[d.owner_id]?.asm_id : null;
  if (ownerAsm && members[ownerAsm]) return ownerAsm;
  const splitAsm = d.split_rm_id ? members[d.split_rm_id]?.asm_id : null;
  if (splitAsm && members[splitAsm]) return splitAsm;
  return null;
}

function dealHasAsm(d: CommissionDeal, members: Record<string, TeamMember>): boolean {
  return dealAsmId(d, members) !== null;
}

// Sep 30, 2026 — per-person commission edits (CommissionEditModal). A key
// "<member_id>:rm|tl|asm|upside" on deals.commission_line_overrides replaces THAT
// person's formula gross for that line on this deal. The payout engine
// (groupByPerson) is the only reader, so every board / payment agrees.
// Oct 29, 2026: "upside" is the trade upside pool line (CommType trade_upside).
export type CommLineKind = "rm" | "tl" | "asm" | "upside";
export function lineOverrideKey(personId: string, kind: CommLineKind): string {
  return `${personId}:${kind}`;
}
export function lineOverride(d: CommissionDeal, personId: string, kind: CommLineKind): number | null {
  const o = d.commission_line_overrides;
  if (!o || typeof o !== "object") return null;
  const v = numOrNull((o as Record<string, unknown>)[lineOverrideKey(personId, kind)]);
  return v === null ? null : Math.max(0, v);
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Oct 29, 2026 (Greg) — the trade money on the board.
//
// SHORTFALLS: the trade price shortfalls Chad deducted on the "Trade prices to
// settle" list below (deals.trade_margin_loss, kept current by trade_sync_deal)
// join the deal's non-collect BUCKET: "let it blend into non-collect revenue. If
// they have not used the $500 and this is $300 below 30%, it deducts from
// there." They count on retail deals too; the non-collect part itself stays
// insurance-only. See getNonCollectBucket.
//
// UPSIDE: what the homeowner pays ABOVE the 30% floor (sub cost ÷ 0.7) on a
// homeowner-billed trade is a pool paid as commission — "all that works just
// like retail commissions". deals.trade_upside = Σ max(0, customer − floor) over
// the live homeowner-billed priced trades (trade_sync_deal, migration 20261029f).
// On an INSURANCE deal groupByPerson pays it as the `trade_upside` line: the RM
// by default (half each on a split), reallocated by Chad with "<member>:upside"
// overrides. On a RETAIL deal it is already inside the retail pool (scopeCalc:
// retailPool += tradeUpside), which the RM line carries — so no line (D4).
// Insurance-billed trades sit inside the insurance scope (already commissioned at
// the RM rate) and never enter the pool; Bardin-paid trades bill nobody (D1).
// ---------------------------------------------------------------------------
function getTradeMarginLoss(d: CommissionDeal): number {
  return Math.max(0, Number((d as { trade_margin_loss?: number | null }).trade_margin_loss) || 0);
}

/** deals.trade_upside as stored, whatever the deal type. A row read without the column but with the two sums
 *  (a calcScope column list) gets trade_ho_total − trade_ho_floor — the same number by construction: a trade
 *  short of the floor counts its customer price in both sums, so it adds 0. */
function rawTradeUpside(d: CommissionDeal): number {
  const x = d as { trade_upside?: number | string | null; trade_ho_total?: number | string | null; trade_ho_floor?: number | string | null };
  const direct = numOrNull(x.trade_upside);
  if (direct !== null) return Math.max(0, direct);
  const total = numOrNull(x.trade_ho_total);
  const floor = numOrNull(x.trade_ho_floor);
  return total !== null && floor !== null ? Math.max(0, round2(total - floor)) : 0;
}

/** The trade upside pool the board pays as its own line — insurance deals only; a retail deal's upside is in
 *  its retail pool, which the RM line already carries. */
export function getTradeUpside(d: CommissionDeal): number {
  if (isRetailDeal(d)) return 0;
  return rawTradeUpside(d);
}

/** Who is paid the trade upside pool on an insurance deal, one entry per PERSON (a person can hold two roles —
 *  the owner who is also the split RM's TL, the owner who is the split RM's ASM (Hanley), an Alex self-split —
 *  and the override key "<member>:upside" is per person, so the pool is never paid twice to one of them).
 *  - The RMs hold the default: the whole pool to the owner, or half each on a split (the owner's half rounded
 *    to the cent, the split RM the rest, so the halves add up to the pool). A member off the roster earns no
 *    line (the Rico rule), as on the RM lines.
 *  - A TL (dealTlBases) or the ASM (dealAsmId) earns only when Chad set an override — the retail TL / ASM rule.
 *  - An override replaces that person's default. An RM's $0 override keeps a $0 line, so the Edit modal still
 *    shows it as set by hand (and payLinesFor drops $0 lines); a TL / ASM with nothing gets no line.
 *  `base` is the pool the person's line is drawn from: their default share for an RM, the whole pool otherwise. */
export function upsidePoolShares(
  d: CommissionDeal,
  members: Record<string, TeamMember>,
): Array<{ personId: string; base: number; gross: number; overridden: boolean }> {
  if (isRetailDeal(d)) return [];
  const upside = getTradeUpside(d);
  const o = d.commission_line_overrides;
  const anyOverride = !!o && typeof o === "object" && Object.keys(o).some((k) => k.endsWith(":upside"));
  if (upside <= 0.005 && !anyOverride) return []; // most deals: no pool, nothing set by hand
  const shares = new Map<string, number>(); // person → default gross (RMs only)
  const ownerShare = d.split_rm_id ? round2(upside / 2) : upside;
  if (d.owner_id && members[d.owner_id]) shares.set(d.owner_id, ownerShare);
  if (d.split_rm_id && members[d.split_rm_id]) {
    shares.set(d.split_rm_id, round2((shares.get(d.split_rm_id) ?? 0) + (upside - ownerShare)));
  }
  const out: Array<{ personId: string; base: number; gross: number; overridden: boolean }> = [];
  for (const [personId, share] of shares) {
    const ov = lineOverride(d, personId, "upside");
    if (ov === null && share <= 0.005) continue;
    out.push({ personId, base: share, gross: ov ?? share, overridden: ov !== null });
  }
  if (!anyOverride) return out; // a TL / ASM earns only by an override
  const asmId = dealAsmId(d, members);
  for (const personId of new Set([...Object.keys(dealTlBases(d, members)), ...(asmId ? [asmId] : [])])) {
    if (shares.has(personId) || !members[personId]) continue;
    const ov = lineOverride(d, personId, "upside");
    if (ov === null || ov <= 0.005) continue;
    out.push({ personId, base: upside, gross: ov, overridden: true });
  }
  return out;
}

/** The first $500 of the bucket is forgiven (Greg, May 15 and Oct 29, 2026). */
export const NON_COLLECT_ALLOWANCE = 500;

export interface NonCollectBucket {
  /** deals.non_commissionable (0 on a retail deal), whether or not it counts. */
  nc: number;
  /** The non-collect part counts: an insurance deal whose non_collect_deduct_active is on (grandfathered: no). */
  ncCounts: boolean;
  /** The trade price shortfalls Chad deducted. */
  tradeLoss: number;
  /** What is in the bucket: (ncCounts ? nc : 0) + tradeLoss. */
  bucket: number;
  /** The part of the bucket the $500 allowance covers. */
  forgiven: number;
  /** What comes off the commissions: max(0, bucket − 500). */
  overage: number;
  /** The deduction can apply: the non-collect gate is on, or Chad deducted a shortfall (the D13 rule, unchanged). */
  applies: boolean;
}

/** D3: bucket = max(0, ncPart + trade_margin_loss − 500), ncPart = non_commissionable on an insurance deal whose
 *  non_collect_deduct_active is true, else 0. Retail and grandfathered deals: the shortfalls alone, first $500
 *  forgiven. The ≥$501 credits rule (dealCreditsForCommission: misc + non-collect) is separate and unchanged. */
export function getNonCollectBucket(d: CommissionDeal): NonCollectBucket {
  const tradeLoss = getTradeMarginLoss(d);
  const retail = isRetailDeal(d);
  const nc = retail ? 0 : Math.max(0, Number((d as { non_commissionable?: number | null }).non_commissionable) || 0);
  const ncCounts = !retail && nonCollectPolicyApplies(d);
  const bucket = round2((ncCounts ? nc : 0) + tradeLoss);
  const overage = Math.max(0, round2(bucket - NON_COLLECT_ALLOWANCE));
  // applies = the non-collect gate is on, or Chad deducted a shortfall (D13: kept as it was).
  return { nc, ncCounts, tradeLoss, bucket, forgiven: Math.min(bucket, NON_COLLECT_ALLOWANCE), overage, applies: nonCollectPolicyApplies(d) || tradeLoss > 0 };
}

/** The banner's sentence: "Non-collect $A + trade shortfalls $B = $C · first $500 forgiven · $D deducted from
 *  commissions". A $0 part is left out, and so is non-collect that doesn't count (grandfathered). */
export function nonCollectHeadline(b: NonCollectBucket): string {
  const parts: string[] = [];
  if (b.ncCounts && b.nc > 0) parts.push(`Non-collect ${fmtUSD(b.nc)}`);
  if (b.tradeLoss > 0) parts.push(`${parts.length ? "trade" : "Trade"} shortfalls ${fmtUSD(b.tradeLoss)}`);
  const sum = parts.length > 1 ? `${parts.join(" + ")} = ${fmtUSD(b.bucket)}` : parts[0] ?? `Bucket ${fmtUSD(b.bucket)}`;
  const taken = b.overage > 0 ? `${fmtUSD(b.overage)} deducted from commissions` : "nothing deducted";
  return `${sum} · first $${NON_COLLECT_ALLOWANCE} forgiven · ${taken}`;
}

/**
 * Returns true when the over-$500 non-collect overage deduction applies to this
 * deal's commissions. Gate: the dedicated deals.non_collect_deduct_active flag
 * (default true — no HO acknowledgement required as of Jun 17, 2026). Already-paid
 * grandfathered deals carry false and stay gross. This is decoupled from
 * non_collect_acknowledged (the HO's §4a disclosure record drawn on the PDF).
 */
function nonCollectPolicyApplies(d: CommissionDeal): boolean {
  return (d as { non_collect_deduct_active?: boolean | null }).non_collect_deduct_active === true;
}

/** Does this deal carry explicit per-role dollar deduction amounts (set via the
 *  Commission board editor)? When true, those amounts win over the legacy split. */
function hasExplicitNonCollectAmounts(d: CommissionDeal): boolean {
  return numOrNull(d.non_collect_rm_amount) !== null
    || numOrNull(d.non_collect_tl_amount) !== null
    || numOrNull(d.non_collect_asm_amount) !== null;
}

function getNonCollectDeductionBreakdown(
  d: CommissionDeal,
  members: Record<string, TeamMember>,
): { overage: number; rm: number; tl: number; asm: number; applies: boolean } {
  // Oct 29, 2026: ONE bucket (getNonCollectBucket) — the non-collect part behind its own gate (grandfathered
  // deals stay gross on it) plus the trade shortfalls Chad deducted, which count whatever the flag says (he
  // chose them on this board); the first $500 of the bucket is forgiven and the rest splits below.
  const b = getNonCollectBucket(d);
  const overage = b.overage;
  const applies = b.applies;
  if (!applies || overage <= 0) {
    return { overage, rm: 0, tl: 0, asm: 0, applies };
  }
  // Retail (Oct 29, 2026): the only pool is a trade shortfall, and the TL / ASM lines on a retail deal are flat
  // amounts Chad sets by hand (no formula override to take from), so it all comes off the RM.
  if (isRetailDeal(d)) return { overage, rm: overage, tl: 0, asm: 0, applies };
  const hasTl = dealHasTl(d, members); // any TL on either half (Jul 21, 2026)
  const hasAsm = dealHasAsm(d, members);
  // Legacy split: RM/TL percentage, ASM untouched. The 75/25 default stays keyed
  // to the OWNER's TL (its original design) — a TL who only leads the split half
  // isn't defaulted a deduction share; Chad allocates explicitly via the banner
  // editor if he should absorb one. (Jul 21, 2026)
  const split = Number(d.non_collect_split_rm_pct);
  const splitPct = Number.isFinite(split) && split >= 0 && split <= 1 ? split : 0.75;
  // Explicit per-role dollar amounts (Jun 17, 2026) take precedence. Roles not
  // present on the deal are zeroed regardless of what's stored.
  if (hasExplicitNonCollectAmounts(d)) {
    let rm = Math.max(0, numOrNull(d.non_collect_rm_amount) ?? 0);
    let tl = hasTl ? Math.max(0, numOrNull(d.non_collect_tl_amount) ?? 0) : 0;
    let asm = hasAsm ? Math.max(0, numOrNull(d.non_collect_asm_amount) ?? 0) : 0;
    // The amounts were entered against the pool as it stood (at the agreement's approval, or here). Whatever
    // joined it since (a trade shortfall Chad deducted) splits on top by the legacy rule, and whatever left it
    // (a deduction reversed) comes off the RM first, so the amounts always total the pool (delta review of
    // PR #76, Oct 29, 2026: a later shortfall was shown as deducted and reached nobody).
    const left = Math.round((overage - (rm + tl + asm)) * 100) / 100;
    if (left > 0.005) {
      const extra = calcNonCollectDeduction(left, splitPct, ownerHasTl(d, members));
      rm += extra.rm;
      tl += extra.tl;
    } else if (left < -0.005) {
      let over = -left;
      const take = (n: number) => { const t = Math.min(n, over); over -= t; return n - t; };
      rm = take(rm); tl = take(tl); asm = take(asm);
    }
    return { overage, rm, tl, asm, applies };
  }
  const ded = calcNonCollectDeduction(overage, splitPct, ownerHasTl(d, members));
  return { overage: ded.total, rm: ded.rm, tl: ded.tl, asm: 0, applies };
}

// ---------------------------------------------------------------------------
// Grouped data structure for Approved / Paid tabs
// ---------------------------------------------------------------------------

// "trueup" (Sep 25, 2026): the 5★ review true-up paid on top of an rm line. It
// only ever comes from the stored ledger (commission_payments) — the live
// formula never emits it, so groupByPerson() never produces one.
// "trade_upside" (Oct 29, 2026): the trade upside pool — the homeowner's price
// above the 30% floor on the homeowner-billed trades, paid like the retail pool
// (the RM by default; Chad reallocates). Insurance deals only; a retail deal's
// upside is inside its retail pool. Its SQL side (the commission_payments CHECK,
// commission_approve / commission_mark_paid / books_pay_line_rows allow-lists,
// the Books key cogs_commissions, label and rank) is migration 20261029f.
export type CommType = "rm" | "rm_split" | "trade_upside" | "tl_override" | "asm_override" | "trueup";
/** The formula-produced lines a mark-paid click can store (never a true-up). */
export type SalesCommType = Exclude<CommType, "trueup">;
export const COMM_TYPE_ORDER: CommType[] = ["rm", "rm_split", "trade_upside", "tl_override", "asm_override", "trueup"];

export interface CommFee {
  label: string;
  amount: number;
}

export interface CommLineItem {
  type: CommType;
  deal: CommissionDeal;
  amount: number; // net (after fees) — unchanged from before
  // Display breakdown (Jun 29, 2026) — lets the self-service tab show how each
  // deal's number was derived: rev × rate = gross, minus fees = amount.
  rev: number;     // commissionable revenue / override base attributed to this line
  rate: number;    // 0.10 RM · 0.01 TL · 0.04 ASM (0 = retail / explicit override, formula hidden)
  gross: number;   // commission before fee deductions
  fees: CommFee[]; // non-collect deduction, canvasser carve-out, etc.
  // Sep 25, 2026 (Books item 3) — paid-line provenance. `stored` = this line IS
  // the commission_payments row written when it was marked paid (frozen truth);
  // `legacyPaid` = paid before the ledger existed, so it is the live recompute.
  // Neither is set on pending / approved lines.
  stored?: boolean;
  legacyPaid?: boolean;
  /** Chad set this person's amount by hand (commission_line_overrides). */
  overridden?: boolean;
  /** The ledger row's own payout date (a true-up pays after the deal's commission_paid_at). */
  paidAt?: string | null;
}

export interface CommTypeGroup {
  type: CommType;
  label: string;
  total: number;
  items: CommLineItem[];
}

export interface PersonGroup {
  personId: string;
  name: string;
  totalComm: number;
  /** Breakdown by commission type (RM deals, TL overrides, ASM overrides) */
  typeGroups: CommTypeGroup[];
  /** All unique deal IDs across all types — for Pay All */
  allDealIds: string[];
}

// Sep 25, 2026 (Books Ship 2b) — the shape the commission_mark_paid RPC stores
// per line, and what it hands back. Amounts are the NET a person is paid.
export interface PayLine {
  deal_id: string;
  member_id: string;
  line_type: SalesCommType;
  amount: number;
  gross: number;
  rate: number;
  rev: number;
  fees: CommFee[];
}

interface MarkPaidResult {
  batch_id?: string;
  paid_at?: string;
  paid_deal_ids?: string[];
  lines_inserted?: number;
  total_amount?: number;
  deals_without_lines?: string[];
}

// Oct 8, 2026 — what commission_approve hands back: the batch, the bills it
// made (one per person) and the deals that yielded no line (no bill).
interface ApproveResult {
  batch_id?: string;
  approved_at?: string;
  approved_deal_ids?: string[];
  bills?: Array<{ bill_id: string; member_id: string | null; total: number; status: string }>;
  deals_without_lines?: string[];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export const COMM_TYPE_LABELS: Record<CommType, string> = {
  rm: "RM Commission",
  rm_split: "RM Split Commission (50%)",
  trade_upside: "Trade upside pool",
  tl_override: "TL Override (1%)",
  asm_override: "ASM Override (4%)",
  trueup: "5★ Review True-up",
};

export const COMM_TYPE_COLORS: Record<CommType, string> = {
  rm: "bg-bardin-teal/10 text-bardin-teal",
  rm_split: "bg-indigo-50 text-indigo-600",
  trade_upside: "bg-teal-50 text-teal-700",
  tl_override: "bg-amber-50 text-amber-700",
  asm_override: "bg-purple-50 text-purple-700",
  trueup: "bg-sky-50 text-sky-700",
};

export function groupByPerson(
  deals: CommissionDeal[],
  members: Record<string, TeamMember>,
  canvasserByDeal: Record<string, CanvasserRef> = {},
): PersonGroup[] {
  // Accumulate line items per person
  const lines: Record<string, CommLineItem[]> = {};

  function addLine(personId: string, item: CommLineItem) {
    if (!lines[personId]) lines[personId] = [];
    lines[personId].push(item);
  }

  for (const d of deals) {
    const isSplit = !!d.split_rm_id;
    // May 15, 2026 — non-collect-revenue deduction. Computed live from source
    // data (deals.non_commissionable + deals.non_collect_split_rm_pct) so the
    // breakdown stays reproducible across pending / approved / paid views.
    // Chad-tier edits to rm_commission via CommissionEditModal should be the
    // GROSS amount — the system applies the non-collect deduction on top.
    const dedBreakdown = getNonCollectDeductionBreakdown(d, members);
    // Canvasser carve-out (Ship 2): 5 pts off the RM line on canvasser-sourced
    // deals, split 50/50 across owner + split RM (same shape as the non-collect
    // deduction). Stacks with the non-collect deduction; clamped at 0.
    const carveTotal = getCanvasserCarveout(d, canvasserByDeal)?.captured ?? 0;

    // RM commission (deductions split 50/50 across owner + split RM). Each RM's
    // half uses THEIR OWN rate: legacy deals → team_roster.commission_rate;
    // new-structure deals (signed ≥ Jul 20, 2026) → the per-deal tier via
    // dealRateInfo (manager half vs regular-RM half rate independently).
    if (d.owner_id && members[d.owner_id]) {
      const share = isSplit ? 0.5 : 1;
      const ownerRate = dealRateInfo(d, members[d.owner_id]).rate;
      const rev = getDealRev(d) * share;
      const ov = lineOverride(d, d.owner_id, "rm");
      const gross = ov ?? getRMComm(d, ownerRate) * share;
      const dedForOwner = dedBreakdown.rm * share;
      const carveForOwner = carveTotal * share;
      const fees: CommFee[] = [];
      if (dedForOwner > 0) fees.push({ label: "Non-collect deduction", amount: dedForOwner });
      if (carveForOwner > 0) fees.push({ label: "Canvasser carve-out", amount: carveForOwner });
      addLine(d.owner_id, { type: isSplit ? "rm_split" : "rm", deal: d, rev, rate: isRetailDeal(d) || ov !== null ? 0 : ownerRate, gross, fees, amount: Math.max(0, gross - dedForOwner - carveForOwner), overridden: ov !== null });
    }

    // Split RM commission
    if (d.split_rm_id && members[d.split_rm_id]) {
      const splitRate = dealRateInfo(d, members[d.split_rm_id]).rate;
      const rev = getDealRev(d) * 0.5;
      const ov = lineOverride(d, d.split_rm_id, "rm");
      const gross = ov ?? getRMComm(d, splitRate) * 0.5;
      const dedForSplit = dedBreakdown.rm * 0.5;
      const carveForSplit = carveTotal * 0.5;
      const fees: CommFee[] = [];
      if (dedForSplit > 0) fees.push({ label: "Non-collect deduction", amount: dedForSplit });
      if (carveForSplit > 0) fees.push({ label: "Canvasser carve-out", amount: carveForSplit });
      addLine(d.split_rm_id, { type: "rm_split", deal: d, rev, rate: isRetailDeal(d) || ov !== null ? 0 : splitRate, gross, fees, amount: Math.max(0, gross - dedForSplit - carveForSplit), overridden: ov !== null });
    }

    // Retail (Jun 11, 2026): no automatic TL/ASM overrides — the pool split is
    // set per job by Chad (TL/ASM shares are carved out of the RM line via the
    // ✎ Edit modal's flat amounts). Skip the formula lines entirely.
    // Sep 30, 2026: a retail TL/ASM line exists only when Chad set one by hand.
    const retail = isRetailDeal(d);

    // TL override — per RM half (Jul 21, 2026, Greg): each half pays 1% to that
    // half's RM's own team lead (see dealTlBases). The single bd.tl deduction
    // amount apportions across TL lines by base share (one TL = full amount).
    {
      const tlBases = dealTlBases(d, members);
      const tlTotalBase = Object.values(tlBases).reduce((s, b) => s + b, 0);
      for (const [tlId, baseTl] of Object.entries(tlBases)) {
        const ov = lineOverride(d, tlId, "tl");
        if (retail && ov === null) continue;
        const grossTl = ov ?? baseTl * TL_OVERRIDE_RATE;
        const dedTl = !retail && tlTotalBase > 0 ? dedBreakdown.tl * (baseTl / tlTotalBase) : 0;
        const feesTl: CommFee[] = [];
        if (dedTl > 0) feesTl.push({ label: "Non-collect deduction", amount: dedTl });
        const netTl = Math.max(0, grossTl - dedTl);
        addLine(tlId, { type: "tl_override", deal: d, rev: baseTl, rate: ov !== null ? 0 : TL_OVERRIDE_RATE, gross: grossTl, fees: feesTl, amount: netTl, overridden: ov !== null });
      }
    }

    // ASM override — the deal's ASM (owner's asm_id, falling back to the split
    // RM's asm_id — Jul 21, 2026, see dealAsmId). Jun 17, 2026: ASM absorbs its
    // explicit share of the non-collect deduction (was previously exempt).
    {
      const asmId = dealAsmId(d, members);
      const ov = asmId ? lineOverride(d, asmId, "asm") : null;
      if (asmId && !(retail && ov === null)) {
        // Same as TL — exclude the ASM's own RM share (owner or split half) when
        // the ASM is also an RM on this deal.
        const baseAsm = overrideBaseRev(d, asmId);
        const grossAsm = ov ?? baseAsm * ASM_OVERRIDE_RATE;
        const dedAsm = retail ? 0 : dedBreakdown.asm;
        const feesAsm: CommFee[] = [];
        if (dedAsm > 0) feesAsm.push({ label: "Non-collect deduction", amount: dedAsm });
        const netAsm = Math.max(0, grossAsm - dedAsm);
        addLine(asmId, { type: "asm_override", deal: d, rev: baseAsm, rate: ov !== null ? 0 : ASM_OVERRIDE_RATE, gross: grossAsm, fees: feesAsm, amount: netAsm, overridden: ov !== null });
      }
    }

    // Trade upside pool (Oct 29, 2026, Greg: "If the customer is charged above the 30% floor, all that works
    // just like retail commissions"). See upsidePoolShares: deals.trade_upside goes to the RM by default — half
    // each on a split, the retail pool's rule — and Chad moves it with "<member>:upside" overrides (✎ Edit); a TL /
    // ASM line exists only when he set one, like the retail TL / ASM rows. Rate 0 (a pool, not a % of revenue).
    // No non-collect fee and no canvasser carve-out on it — the bucket comes off the RM / TL / ASM lines above,
    // and bookNonCollectOverageDraws skips this line. Retail: no line (the upside is in the retail pool).
    if (!retail) {
      for (const p of upsidePoolShares(d, members)) {
        addLine(p.personId, { type: "trade_upside", deal: d, rev: p.base, rate: 0, gross: p.gross, fees: [], amount: p.gross, overridden: p.overridden });
      }
    }
  }

  // Build PersonGroup for each person
  const groups: PersonGroup[] = [];
  for (const [personId, items] of Object.entries(lines)) {
    const m = members[personId];
    if (!m) continue;

    // Group items by type
    const byType: Partial<Record<CommType, CommLineItem[]>> = {};
    for (const item of items) {
      if (!byType[item.type]) byType[item.type] = [];
      byType[item.type]!.push(item);
    }

    const typeGroups: CommTypeGroup[] = [];
    for (const t of COMM_TYPE_ORDER) {
      const tItems = byType[t];
      if (!tItems || tItems.length === 0) continue;
      typeGroups.push({
        type: t,
        label: COMM_TYPE_LABELS[t],
        total: tItems.reduce((s, i) => s + i.amount, 0),
        items: tItems,
      });
    }

    const totalComm = items.reduce((s, i) => s + i.amount, 0);
    const allDealIds = [...new Set(items.map((i) => i.deal.id))];

    groups.push({ personId, name: m.full_name, totalComm, typeGroups, allDealIds });
  }

  return groups.sort((a, b) => b.totalComm - a.totalComm);
}

/**
 * Sep 25, 2026 (Books item 3) — the PAID-history engine every money surface
 * reads through. Same output shape as groupByPerson(), with one rule: a deal
 * whose commission was paid through the ledger (commission_paid_at at/after
 * COMMISSION_PAYMENTS_SINCE) renders the STORED commission_payments rows —
 * the exact per-person lines that went out, frozen at the click, including a
 * payee who has since left the roster and any 5★ true-up paid later — instead
 * of a recompute from today's roster/rates/attribution. Everything else
 * (pending, approved, and deals paid before the ledger existed) goes through
 * groupByPerson() unchanged; pre-ledger paid lines are flagged `legacyPaid` so
 * the UI can say "computed". A stored-era deal with no visible row for a person
 * means that person was paid nothing on it — there is deliberately no fallback
 * to the formula (the Rico rule: an inactive earner records no line).
 *
 * `stored` is whatever the viewer may read (RLS: finance tier = all rows,
 * TL/ASM = self + downline, else self), so each surface substitutes exactly
 * the lines its viewer is allowed to see.
 */
/** Sep 30, 2026 — one deal's commission lines, itemized per person, straight
 *  from the payout engine. The pending card, the edit modal, draw paydown and
 *  the non-collect draw booking all read this, so they can't drift. */
export interface DealCommLine extends CommLineItem { personId: string; name: string }
export function dealCommLines(
  d: CommissionDeal,
  members: Record<string, TeamMember>,
  canvasserByDeal: Record<string, CanvasserRef> = {},
): DealCommLine[] {
  const out: DealCommLine[] = [];
  for (const g of groupByPerson([d], members, canvasserByDeal)) {
    for (const tg of g.typeGroups) for (const it of tg.items) out.push({ ...it, personId: g.personId, name: g.name });
  }
  const order: Record<string, number> = { rm: 0, rm_split: 0, trade_upside: 1, tl_override: 2, asm_override: 3, trueup: 4 };
  const ownerFirst = (l: DealCommLine) => (l.personId === d.owner_id ? 0 : 1);
  return out.sort((a, b) => order[a.type] - order[b.type] || ownerFirst(a) - ownerFirst(b));
}
export function commLineKind(t: CommType): CommLineKind {
  return t === "tl_override" ? "tl" : t === "asm_override" ? "asm" : t === "trade_upside" ? "upside" : "rm";
}

/** Sep 30, 2026 — the rows the ✎ Edit modal shows: one per person per line,
 *  each with its formula default (per-person rate on their own base) and the
 *  amount currently paying (formula or Chad's edit). Retail adds TL/ASM rows
 *  at $0 so Chad can carve them a flat share of the pool. */
export interface CommissionEditLine {
  personId: string;
  name: string;
  kind: CommLineKind;
  role: string;
  base: number;          // revenue this person's % applies to (their half, or excl. their own half)
  formulaRate: number;   // 0 on retail
  formulaGross: number;
  currentGross: number;
  overridden: boolean;
  fees: CommFee[];       // deductions applied after the gross (non-collect, canvasser)
  /** Oct 29, 2026 — Upside rows only: the deal's whole trade upside pool (an RM row's base is their share of it). */
  pool?: number;
}
export function commissionEditLines(
  d: CommissionDeal,
  members: Record<string, TeamMember>,
  canvasserByDeal: Record<string, CanvasserRef> = {},
): CommissionEditLine[] {
  const formula = dealCommLines({ ...d, commission_line_overrides: null, rm_commission: null }, members, canvasserByDeal);
  const current = dealCommLines(d, members, canvasserByDeal);
  const roleOf = (t: CommType) => (t === "tl_override" ? "TL" : t === "asm_override" ? "ASM" : t === "trade_upside" ? "Upside" : "RM");
  const rows: CommissionEditLine[] = formula.map((f) => {
    const kind = commLineKind(f.type);
    const cur = current.find((c) => c.personId === f.personId && commLineKind(c.type) === kind);
    return {
      personId: f.personId, name: f.name, kind, role: roleOf(f.type),
      base: f.rev, formulaRate: f.rate, formulaGross: f.gross,
      currentGross: cur?.gross ?? f.gross, overridden: !!cur?.overridden, fees: cur?.fees ?? f.fees,
      ...(kind === "upside" ? { pool: getTradeUpside(d) } : {}),
    };
  });
  if (isRetailDeal(d)) {
    const pool = Math.max(0, getDealRev(d));
    const extra: Array<[string, CommLineKind]> = [
      ...Object.keys(dealTlBases(d, members)).map((id) => [id, "tl"] as [string, CommLineKind]),
      ...(dealAsmId(d, members) ? [[dealAsmId(d, members) as string, "asm"] as [string, CommLineKind]] : []),
    ];
    for (const [id, kind] of extra) {
      if (!members[id] || rows.some((r) => r.personId === id && r.kind === kind)) continue;
      const cur = current.find((c) => c.personId === id && commLineKind(c.type) === kind);
      rows.push({
        personId: id, name: members[id].full_name, kind, role: kind === "tl" ? "TL" : "ASM",
        base: pool, formulaRate: 0, formulaGross: 0,
        currentGross: cur?.gross ?? 0, overridden: !!cur?.overridden, fees: cur?.fees ?? [],
      });
    }
  } else {
    // Oct 29, 2026 — the trade upside pool: the formula rows above give the RM(s) the pool; the TL / ASM rows
    // start at $0 so Chad can carve them a flat share (the retail rule, same semantics). Anyone holding an
    // ":upside" override on a deal whose pool has since gone to $0 keeps a row so he can see and clear it.
    const upside = getTradeUpside(d);
    const asmId = dealAsmId(d, members);
    const people = [
      ...(d.owner_id ? [d.owner_id] : []),
      ...(d.split_rm_id ? [d.split_rm_id] : []),
      ...Object.keys(dealTlBases(d, members)),
      ...(asmId ? [asmId] : []),
    ];
    for (const id of [...new Set(people)]) {
      if (!members[id] || rows.some((r) => r.personId === id && r.kind === "upside")) continue;
      const cur = current.find((c) => c.personId === id && commLineKind(c.type) === "upside");
      if (upside <= 0 && !cur) continue;
      rows.push({
        personId: id, name: members[id].full_name, kind: "upside", role: "Upside",
        base: upside, formulaRate: 0, formulaGross: 0,
        currentGross: cur?.gross ?? 0, overridden: !!cur?.overridden, fees: cur?.fees ?? [], pool: upside,
      });
    }
  }
  return rows;
}

export function groupByPersonStored(
  deals: CommissionDeal[],
  members: Record<string, TeamMember>,
  canvasserByDeal: Record<string, CanvasserRef>,
  stored: StoredPayments,
  payeeNames: Record<string, string> = {},
): PersonGroup[] {
  const live: CommissionDeal[] = [];
  const fromStore: CommissionDeal[] = [];
  for (const d of deals) (salesPaidFromStore(d) ? fromStore : live).push(d);

  const groups = groupByPerson(live, members, canvasserByDeal);
  // Paid before the ledger existed: computed from today's roster — say so.
  for (const g of groups) for (const tg of g.typeGroups) for (const it of tg.items) {
    if (it.deal.commission_paid) it.legacyPaid = true;
  }
  if (fromStore.length === 0) return groups;

  const byPerson = new Map<string, PersonGroup>(groups.map((g) => [g.personId, g]));
  for (const d of fromStore) {
    for (const r of stored.byDeal.get(d.id) ?? []) {
      if (!SALES_LINE_TYPES.has(r.line_type)) continue; // rs_pool rows have their own view
      const item = lineFromRow(r, d);
      let g = byPerson.get(r.member_id);
      if (!g) {
        g = { personId: r.member_id, name: payeeName(r.member_id, members, payeeNames), totalComm: 0, typeGroups: [], allDealIds: [] };
        byPerson.set(r.member_id, g);
        groups.push(g);
      }
      let tg = g.typeGroups.find((t) => t.type === item.type);
      if (!tg) {
        tg = { type: item.type, label: COMM_TYPE_LABELS[item.type], total: 0, items: [] };
        g.typeGroups.push(tg);
      }
      tg.items.push(item);
      tg.total += item.amount;
      g.totalComm += item.amount;
      if (!g.allDealIds.includes(d.id)) g.allDealIds.push(d.id);
    }
  }
  for (const g of groups) {
    g.typeGroups.sort((a, b) => COMM_TYPE_ORDER.indexOf(a.type) - COMM_TYPE_ORDER.indexOf(b.type));
  }
  return groups.sort((a, b) => b.totalComm - a.totalComm);
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function CommissionBoard({ embedded = false }: { embedded?: boolean } = {}) {
  const { role, isSuperUser, member } = useAuth();
  const [deals, setDeals] = useState<CommissionDeal[] | null>(null);
  const [members, setMembers] = useState<Record<string, TeamMember>>({});
  // Map of deal_id → the canvasser who set the originating app (role='canvasser').
  // Drives the Ship-2 carve-out: 5 pts off the RM line on these deals.
  const [canvasserByDeal, setCanvasserByDeal] = useState<Record<string, CanvasserRef>>({});
  // Standalone: URL-backed; embedded (inside Finance/MyPipeline): local-only
  // so we don't collide with the parent page's `?tab=` param.
  const [tabLocal, setTabLocal] = useState<TabKey>("pending");
  const [tabUrl, setTabUrl] = useTabParam<TabKey>("ctab", "pending", ["pending", "approved", "paid"] as const);
  const tab = embedded ? tabLocal : tabUrl;
  const setTab = embedded ? setTabLocal : setTabUrl;
  // Category filter — pill bar at the top of the Commissions view.
  // URL param `cat` namespaced so it doesn't collide with the parent `?tab=`.
  const [categoryUrl, setCategoryUrl] = useTabParam<CategoryKey>("cat", "all", ["all", "sales", "roofteam", "crew", "pm", "canvassers"] as const);
  const [categoryLocal, setCategoryLocal] = useState<CategoryKey>("all");
  const category = embedded ? categoryLocal : categoryUrl;
  const setCategory = embedded ? setCategoryLocal : setCategoryUrl;
  // Paid-tab filters — local-only, don't pollute URL because they're scoped to the Paid view.
  const [paidDateRange, setPaidDateRange] = useState<DateRangeKey>("all");
  const [paidPersonId, setPaidPersonId] = useState<string>("all");
  // Custom date range — strings in yyyy-mm-dd format from <input type="date">.
  // When either is set, dateRange is treated as "custom" regardless of preset.
  const [paidCustomFrom, setPaidCustomFrom] = useState<string>("");
  const [paidCustomTo, setPaidCustomTo] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState<Set<string>>(new Set());
  // Oct 22, 2026 — the first payment plan recorded when each pending deal's
  // build was submitted (build_payment_plans). Shown on a card the first-payment
  // hold is holding, so Chad sees what was promised. A failed read leaves it off.
  const [plans, setPlans] = useState<Record<string, BuildPaymentPlan>>({});
  // Oct 22, 2026 — finance_settings.commission_first_payment_hold, the same
  // switch the database reads: "off" lifts the first-payment hold here and
  // there, no deploy. Anything else, a failed read included, keeps it on.
  const [firstPaymentHoldOn, setFirstPaymentHoldOn] = useState(true);
  // Ship 5: which deal is being edited via the CommissionEditModal
  const [editingDealId, setEditingDealId] = useState<string | null>(null);
  // Reload trigger — bumps when a Roof Crew approval lands so gates update
  const [reloadKey, setReloadKey] = useState(0);
  // Jun 17, 2026 — per-member outstanding draw balance (approved+paid draws minus
  // repayments), summed by team_member_id. Drives the draw-paydown control on
  // pending commission cards. Refreshed on reloadKey.
  const [drawBalances, setDrawBalances] = useState<Record<string, number>>({});
  // Sep 25, 2026 (Books item 3) — the stored payout ledger. The Paid tab (sales
  // + RS pool) renders THESE rows for anything paid since the ledger went live;
  // only pre-ledger payouts are still recomputed (flagged "≈ computed").
  const [storedPayments, setStoredPayments] = useState<StoredPayments>(EMPTY_STORED);
  // Names for payees who have since left the active roster — the members map is
  // active-only, but a stored payout must keep showing WHO was paid.
  const [payeeNames, setPayeeNames] = useState<Record<string, string>>({});
  // Oct 8, 2026 — the Books bills behind the approved deals (sales + RS pool):
  // which deal × person is "in Books" and what Books answered. Reloaded when
  // the deals change and when a bill row changes (books_payout_bills is in the
  // realtime publication).
  const [bills, setBills] = useState<BillIndex>(EMPTY_BILL_INDEX);
  const [billsKey, setBillsKey] = useState(0);
  // "Check Books now" — the pull's one-line answer.
  const [booksCheck, setBooksCheck] = useState<string | null>(null);
  const [checkingBooks, setCheckingBooks] = useState(false);

  // Realtime: bump reload when roof_crew_payments changes (Shannon marks paid,
  // a new vendor payment is approved, etc.) so the Paid view's crew section
  // refreshes without manual refresh.
  useRealtimeTable("roof_crew_payments", () => setReloadKey((k) => k + 1));
  // Refresh draw balances when draws or repayments change.
  useRealtimeTable("commission_draws", () => setReloadKey((k) => k + 1));
  useRealtimeTable("commission_draw_repayments", () => setReloadKey((k) => k + 1));
  // Live per-person commission rate: re-pull when team_roster changes (rate
  // edits, membership/attribution). team_roster is in supabase_realtime as of
  // Jun 29, 2026. getRMComm reads members[owner].commission_rate via rmRateFor,
  // so a rate change reflects without a manual refresh.
  useRealtimeTable("team_roster", () => setReloadKey((k) => k + 1));
  // A mark-paid / un-pay from any session lands in the ledger → re-pull.
  useRealtimeTable("commission_payments", () => setReloadKey((k) => k + 1));
  // A bill sent / confirmed / paid / withdrawn → re-pull the bills (and the
  // deals: the pull flips commission_paid when Books pays).
  useRealtimeTable("books_payout_bills", () => { setBillsKey((k) => k + 1); setReloadKey((k) => k + 1); });

  // Oct 8, 2026 — the Books bills behind the approved (sales) and RS-pool-
  // approved deals. One lines query + one bills query. A failed read is
  // FATAL like the ledger read: a silent "no bill" would put Mark paid back
  // on a deal Shannon must pay in Books.
  useEffect(() => {
    const ids = (deals ?? [])
      .filter((d) => (d.commission_approved && !d.commission_paid) || (d.rs_pool_approved && !d.rs_pool_paid))
      .map((d) => d.id);
    if (ids.length === 0) { setBills(EMPTY_BILL_INDEX); return; }
    let cancelled = false;
    fetchBooksPayoutBills({ sourceTable: "deals", sourceIds: ids })
      .then((ix) => { if (!cancelled) setBills(ix); })
      .catch((e: unknown) => { if (!cancelled) setErr(`Couldn't read the Books bills: ${formatSupabaseError(e)}`); });
    return () => { cancelled = true; };
  }, [deals, billsKey]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("finance_settings")
      .select("value_jsonb")
      .eq("key", "commission_first_payment_hold")
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setFirstPaymentHoldOn(data?.value_jsonb !== "off"); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Oct 22, 2026 — the payment plans behind the pending deals (see `plans`).
  const pendingIdsKey = useMemo(
    () => (deals ?? []).filter((d) => !d.commission_approved && !d.commission_paid).map((d) => d.id).sort().join(","),
    [deals],
  );
  useEffect(() => {
    const ids = pendingIdsKey ? pendingIdsKey.split(",") : [];
    if (ids.length === 0) { setPlans({}); return; }
    let cancelled = false;
    fetchCurrentPlans(ids)
      .then((p) => { if (!cancelled) setPlans(p); })
      .catch((e) => {
        console.warn("[CommissionBoard] payment plans lookup failed:", e);
        if (!cancelled) setPlans({});
      });
    return () => { cancelled = true; };
  }, [pendingIdsKey]);

  // Oct 29, 2026 — the pending deals' priced trades for the card's trade strip
  // (DealTradesStrip): one read per board load, again when a trade changes
  // (trade_items is in the realtime publication) or the deals reload (a settle
  // on Chad's list bumps reloadKey). RLS: someone kept off prices gets no rows
  // and sees no strip; a failed read only hides it — the money itself comes
  // from deals.trade_upside / trade_margin_loss, which every viewer reads.
  const [tradeRows, setTradeRows] = useState<Record<string, BoardTradeRow[]>>({});
  const [tradesKey, setTradesKey] = useState(0);
  useRealtimeTable("trade_items", () => setTradesKey((k) => k + 1));
  useEffect(() => {
    const ids = pendingIdsKey ? pendingIdsKey.split(",") : [];
    if (ids.length === 0) { setTradeRows({}); return; }
    let cancelled = false;
    fetchBoardTradeRows(ids)
      .then((rows) => { if (!cancelled) setTradeRows(rows); })
      .catch((e) => {
        console.warn("[CommissionBoard] trade prices lookup failed:", e);
        if (!cancelled) setTradeRows({});
      });
    return () => { cancelled = true; };
  }, [pendingIdsKey, tradesKey, reloadKey]);

  // Load per-member outstanding draw balances (approved + paid − repayments).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: draws } = await supabase
        .from("commission_draws")
        .select("id, team_member_id, approved_amount")
        .eq("approval_status", "approved")
        .not("paid_at", "is", null);
      const drawIds = (draws ?? []).map((d: { id: string }) => d.id);
      const repaidByDraw: Record<string, number> = {};
      if (drawIds.length > 0) {
        const { data: repays } = await supabase
          .from("commission_draw_repayments")
          .select("draw_id, amount")
          .in("draw_id", drawIds);
        for (const r of (repays ?? []) as Array<{ draw_id: string; amount: number }>) {
          repaidByDraw[r.draw_id] = (repaidByDraw[r.draw_id] || 0) + Number(r.amount);
        }
      }
      const bal: Record<string, number> = {};
      for (const d of (draws ?? []) as Array<{ id: string; team_member_id: string | null; approved_amount: number | null }>) {
        if (!d.team_member_id) continue;
        const outstanding = Math.max(0, Number(d.approved_amount ?? 0) - (repaidByDraw[d.id] || 0));
        bal[d.team_member_id] = (bal[d.team_member_id] || 0) + outstanding;
      }
      if (!cancelled) setDrawBalances(bal);
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [{ data: d, error: e1 }, { data: m, error: e2 }, { data: rates, error: e3 }, stored] = await Promise.all([
        supabase
          .from("deals")
          .select(COMMISSION_DEAL_SELECT)
          // Fetch all deals that have entered the commission queue (either path).
          // Bucketing is driven by the boolean flags below, not by stage.
          .not("commission_ready_date", "is", null)
          .order("commission_ready_date", { ascending: true }),
        supabase.from("team_roster").select("*").eq("is_active", true),
        // Real rates live in team_roster_comp (Sep 9, 2026) — the roster
        // column is neutralized. A rates failure is FATAL here: silently
        // computing a money board at the 10% base is the silent-empty-money
        // bug class.
        supabase.rpc("visible_commission_rates"),
        // Sep 25, 2026 — the stored payout ledger (RLS: finance tier reads all).
        // Fatal on error too: the Paid tab must never silently fall back to a
        // recompute of history it no longer trusts.
        fetchCommissionPayments()
          .then((rows) => ({ rows, error: null as { message: string } | null }))
          .catch((e: unknown) => ({ rows: [] as CommissionPaymentRow[], error: { message: e instanceof Error ? e.message : String(e) } })),
      ]);
      const error = e1 ?? e2 ?? e3 ?? stored.error;
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) {
        setDeals((d ?? []) as CommissionDeal[]);
        const byId: Record<string, TeamMember> = {};
        (m ?? []).forEach((x) => { byId[x.id] = x as TeamMember; });
        mergeRates(byId, ratesMapFromRows(rates));
        setMembers(byId);
        const ledger = indexPayments(stored.rows);
        setStoredPayments(ledger);
        // Best-effort: a failed name lookup just renders "Former team member".
        const missingPayees = payeeIdsNotIn(ledger, byId);
        if (missingPayees.length > 0) {
          fetchMemberNames(missingPayees)
            .then((names) => { if (!cancelled) setPayeeNames(names); })
            .catch(() => { if (!cancelled) setPayeeNames({}); });
        } else {
          setPayeeNames({});
        }

        const dealIds = (d ?? []).map((x: { id: string }) => x.id);
        if (dealIds.length > 0) {
          // Canvasser carve-out: one shared rule for every money surface
          // (lib/canvasserCredit, Oct 19, 2026). A failed read is FATAL:
          // paying an RM without the carve-out on a canvasser lead would
          // overpay them.
          try {
            const cmap = await fetchCanvasserByDeal(dealIds);
            if (!cancelled) setCanvasserByDeal(cmap);
          } catch (e) {
            if (!cancelled) setErr(formatSupabaseError(e));
          }
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Ship 5: gate logic. Returns null when the commission can be approved.
  function approvalBlockReason(deal: CommissionDeal): ApprovalGate {
    // Gate 1: roof crew payment approved?
    if (!deal.roof_crew_payment_approved) {
      return { message: "Roof crew payment not yet approved — see Roof Crew section below." };
    }
    // Gate 1b (Oct 30, 2026, review of the trade release): a homeowner-billed trade whose add-on waits on the
    // homeowner's signature isn't on the deal's money yet; signed after approval, its price and upside would land
    // after the lines were stored (D12), so the RM's upside would never be paid.
    const addendumWait = openAddendumBlock(tradeRows[deal.id] ?? []);
    if (addendumWait) return { message: addendumWait };
    // Gate 2: has all of the first payment cleared? Oct 22, 2026 (Greg: "Hold
    // at approval") — hard again, and enforced in the database too
    // (commission_first_payment_hold), so the note on the build submission form
    // ("Commissions will not go out until all of the first payment has
    // cleared") holds. From Jul 15 it was a soft "Approve anyway". What counts
    // is unchanged: the first payment from the real scope (May 15, Brian
    // George), cleared money in both slots (Jun 17, Derrick Baxter), plus
    // non-collect up to the first payment and the write-off (Jul 1, Marco Poma).
    // Money still clearing doesn't count; the 50-cent allowance matches the
    // build gate. A small true residual waits for a payment or a write-off.
    if (!firstPaymentHoldOn) return null;
    const hold = commissionFirstPaymentHold(deal as unknown as Record<string, unknown>);
    return hold ? { message: hold.message, firstPayment: true } : null;
  }

  // Split deals by status
  const { pending, approved, paid, counts, totals } = useMemo(() => {
    if (!deals) return { pending: [], approved: [], paid: [], counts: { pending: 0, approved: 0, paid: 0 }, totals: { pending: 0, approved: 0, paid: 0 } };
    const pending: CommissionDeal[] = [];
    const approved: CommissionDeal[] = [];
    const paid: CommissionDeal[] = [];
    const counts = { pending: 0, approved: 0, paid: 0 };
    const totals = { pending: 0, approved: 0, paid: 0 };

    for (const d of deals) {
      // The RM line plus (Oct 29, 2026) the trade upside pool, so the tiles carry the pool once it exists.
      const rmComm = getRMComm(d, dealRateInfo(d, d.owner_id ? members[d.owner_id] : undefined).rate) + getTradeUpside(d);
      // Bucket by boolean flags — works for both old-flow (stage-based) and
      // new supplement-lifecycle deals where the stage never moves to commission_*.
      if (d.commission_paid) {
        paid.push(d);
        counts.paid++;
        totals.paid += rmComm;
      } else if (d.commission_approved) {
        approved.push(d);
        counts.approved++;
        totals.approved += rmComm;
      } else {
        pending.push(d);
        counts.pending++;
        totals.pending += rmComm;
      }
    }
    return { pending, approved, paid, counts, totals };
  }, [deals, members]);

  // Grouped data for approved / paid
  const approvedGroups = useMemo(() => groupByPerson(approved, members, canvasserByDeal), [approved, members, canvasserByDeal]);
  // Paid = the stored ledger where it exists (Sep 25, 2026), live recompute only
  // for payouts that predate it.
  const paidGroups = useMemo(
    () => groupByPersonStored(paid, members, canvasserByDeal, storedPayments, payeeNames),
    [paid, members, canvasserByDeal, storedPayments, payeeNames],
  );

  // RS pool deals needing Jennifer's approval (Chad approved, RS not yet confirmed)
  const rsPoolPending = useMemo(
    () => approved.filter((d) => !d.rs_pool_approved),
    [approved],
  );

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async function approveCommission(dealId: string) {
    setActing((prev) => new Set(prev).add(dealId));
    // Oct 8, 2026 — the commission_approve RPC flags the deal (old-flow deals
    // also advance the stage; supplement-lifecycle deals keep theirs), stores
    // the per-person lines and makes the Books bill(s) in one transaction.
    // The lines are payLinesFor()'s — exactly what mark-paid used to send.
    let now = new Date().toISOString();
    const deal = (deals ?? []).find((d) => d.id === dealId);
    const isOldFlow = deal ? OLD_COMMISSION_STAGES.has(deal.stage ?? "") : false;
    try {
      const { data, error: approveErr } = await supabase.rpc("commission_approve", {
        p_deal_ids: [dealId],
        p_lines: payLinesFor([dealId]),
        p_source: "commission_board_approve",
      });
      if (approveErr) throw approveErr;
      const res = (data ?? {}) as ApproveResult;
      now = res.approved_at ?? now;
      warnDealsWithoutBill(res);

      // Jun 17, 2026 (Greg): only AUTO-BOOK the non-collect overage shortfall as
      // a draw. Paying down / reconciling draws is done MANUALLY by Shannon/Chad
      // when they process commissions — the system no longer auto-applies
      // commission against outstanding draws on approval.
      if (deal) {
        await bookNonCollectOverageDraws(deal);
      }

      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d, commission_approved: true, commission_approved_at: now,
                ...(isOldFlow ? { stage: "commission_approved" as const } : {}) }
            : d,
        ) ?? null,
      );
      setBillsKey((k) => k + 1);
    } catch (err) {
      alert(`Error: ${formatSupabaseError(err)}`);
    }
    setActing((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
  }

  // Oct 8, 2026 — take an approval back: the deal's Books bill is withdrawn
  // (refused once Books has paid it) and the deal returns to Pending.
  async function unapproveCommission(dealId: string) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    const reason = window.prompt(
      `Un-approve ${deal?.homeowner_name ?? "this deal"}? The Books bill is withdrawn and the deal goes back to Pending.\n\nReason (5+ characters):`,
      "",
    );
    if (reason === null) return;
    if (reason.trim().length < 5) { alert("A reason of at least 5 characters is required."); return; }
    setActing((prev) => new Set(prev).add(`unapprove-${dealId}`));
    try {
      const { error } = await supabase.rpc("commission_unapprove", { p_deal_ids: [dealId], p_reason: reason.trim() });
      if (error) throw error;
      const isOldFlow = deal ? deal.stage === "commission_approved" : false;
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d, commission_approved: false, commission_approved_at: null,
                ...(isOldFlow ? { stage: "commission_ready" as const } : {}) }
            : d,
        ) ?? null,
      );
      setBillsKey((k) => k + 1);
    } catch (err) {
      alert(`Couldn't un-approve: ${formatSupabaseError(err)}`);
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`unapprove-${dealId}`); return n; });
  }

  // Oct 8, 2026 — "Check Books now": confirm what Books answered to the bills
  // sent, pull the paid / taken-back notices and apply them (the cron does the
  // same every 5 minutes). Finance or the approval tier.
  async function checkBooksNow() {
    setCheckingBooks(true);
    setBooksCheck(null);
    const { data, error } = await supabase.rpc("books_payout_pull", { p_how: "board" });
    if (error) {
      setBooksCheck(`Couldn't check Books: ${formatSupabaseError(error)}`);
    } else {
      setBooksCheck(pullResultText(data));
      setBillsKey((k) => k + 1);
      setReloadKey((k) => k + 1);
    }
    setCheckingBooks(false);
  }

  // Jun 17, 2026: when a role's assigned non-collect overage deduction exceeds
  // their commission on the deal, book the excess as an outstanding draw for
  // that person. Created approved + paid_at so it immediately counts toward the
  // outstanding-draw balance. Paying it down is done MANUALLY by finance when
  // processing commissions (no auto-FIFO). Idempotent per (deal, member, slot)
  // via a partial unique index + a pre-check.
  async function bookNonCollectOverageDraws(deal: CommissionDeal) {
    // A retail deal has no non-collect overage, but a trade shortfall Chad deducted books the same way (Oct 29, 2026).
    if (isRetailDeal(deal) && getTradeMarginLoss(deal) <= 0) return;
    const bd = getNonCollectDeductionBreakdown(deal, members);
    if (!bd.applies || bd.overage <= 0) return;
    const actorId = member?.id;
    if (!actorId) return; // requested_by / approved_by are NOT NULL

    // Per-person gross + non-collect deduction straight from the payout engine
    // (honors Chad's per-person edits). (Sep 30, 2026) The trade upside pool line
    // carries no deduction and has no slot here — never booked (Oct 29, 2026).
    const rows: Array<{ memberId: string; slot: string; gross: number; ded: number }> = dealCommLines(deal, members).filter((l) => l.type !== "trade_upside").map((l) => ({
      memberId: l.personId,
      slot: l.type === "tl_override" ? "tl_override" : l.type === "asm_override" ? "asm_override" : "rm",
      gross: l.gross,
      ded: l.fees.filter((f) => f.label === "Non-collect deduction").reduce((s2, f) => s2 + f.amount, 0),
    }));

    // Dedupe: skip (member, slot) already booked for this deal.
    const { data: existing } = await supabase
      .from("commission_draws")
      .select("team_member_id, comm_slot")
      .eq("deal_id", deal.id)
      .eq("source", "non_collect_overage");
    const seen = new Set(
      (existing ?? []).map((r: { team_member_id: string | null; comm_slot: string | null }) => `${r.team_member_id}:${r.comm_slot}`),
    );

    const now = new Date().toISOString();
    for (const r of rows) {
      const shortfall = Math.round((r.ded - r.gross) * 100) / 100;
      if (shortfall <= 0.005) continue;
      if (seen.has(`${r.memberId}:${r.slot}`)) continue;
      const slotLabel = r.slot === "rm" ? "RM" : r.slot === "tl_override" ? "TL override" : "ASM override";
      const { error: insErr } = await supabase.from("commission_draws").insert({
        recipient_kind: "team_member",
        team_member_id: r.memberId,
        requested_amount: shortfall,
        requested_by: actorId,
        request_reason: `Non-collect overage shortfall on ${deal.homeowner_name ?? deal.id} — ${slotLabel} deduction ${fmtUSD(r.ded)} exceeded ${fmtUSD(r.gross)} commission`,
        approval_status: "approved",
        approved_amount: shortfall,
        approved_by: actorId,
        approved_at: now,
        paid_at: now,
        paid_by: actorId,
        paid_method: "non_collect_overage",
        source: "non_collect_overage",
        comm_slot: r.slot,
        deal_id: deal.id,
        notes: "Auto-booked at commission approval; reconciles against future commission (FIFO).",
      });
      if (!insErr) {
        await supabase.from("finance_audit_log").insert({
          deal_id: deal.id,
          actor_id: actorId,
          action: "non_collect_overage_draw_booked",
          is_financial: true,
          amount_delta: shortfall,
          after_jsonb: { team_member_id: r.memberId, comm_slot: r.slot, shortfall, deduction: r.ded, gross: r.gross },
          reason: `${fmtUSD(shortfall)} non-collect overage booked as draw for ${members[r.memberId]?.full_name ?? r.memberId} (${slotLabel})`,
        });
      }
    }
  }

  // Jun 17, 2026: manual draw paydown from a pending card. Applies `amount` of
  // this deal's commission against the member's outstanding draws (FIFO oldest
  // first), recording repayment rows. Caller caps the amount at min(balance,
  // this deal's commission for that person).
  async function applyDrawPaydown(memberId: string, amount: number, dealId: string) {
    if (!memberId || amount <= 0) return;
    const actorId = member?.id ?? memberId; // applied_by is NOT NULL
    const { data: draws } = await supabase
      .from("commission_draws")
      .select("id, approved_amount, paid_at")
      .eq("team_member_id", memberId)
      .eq("approval_status", "approved")
      .not("paid_at", "is", null)
      .order("paid_at", { ascending: true });
    if (!draws || draws.length === 0) return;
    const drawIds = draws.map((d: { id: string }) => d.id);
    const { data: repays } = await supabase
      .from("commission_draw_repayments")
      .select("draw_id, amount")
      .in("draw_id", drawIds);
    const repaidByDraw: Record<string, number> = {};
    for (const r of (repays ?? []) as Array<{ draw_id: string; amount: number }>) {
      repaidByDraw[r.draw_id] = (repaidByDraw[r.draw_id] || 0) + Number(r.amount);
    }
    let remaining = amount;
    let totalApplied = 0;
    for (const draw of draws as Array<{ id: string; approved_amount: number | null }>) {
      if (remaining <= 0.005) break;
      const outstanding = Math.max(0, Number(draw.approved_amount ?? 0) - (repaidByDraw[draw.id] || 0));
      if (outstanding <= 0.005) continue;
      const apply = Math.min(remaining, outstanding);
      const { error: insErr } = await supabase.from("commission_draw_repayments").insert({
        draw_id: draw.id,
        deal_id: dealId,
        amount: apply,
        applied_by: actorId,
        notes: "Manual paydown from commission at approval",
      });
      if (insErr) { alert(`Error: ${formatSupabaseError(insErr)}`); break; }
      remaining -= apply;
      totalApplied += apply;
    }
    if (totalApplied > 0) {
      await supabase.from("finance_audit_log").insert({
        deal_id: dealId,
        actor_id: member?.id ?? null,
        action: "commission_repayment_applied",
        is_financial: true,
        amount_delta: -totalApplied,
        after_jsonb: { team_member_id: memberId, total_applied: totalApplied, source: "manual_paydown" },
        reason: `${fmtUSD(totalApplied)} of commission manually applied to outstanding draws`,
      });
      setDrawBalances((prev) => ({ ...prev, [memberId]: Math.max(0, (prev[memberId] ?? 0) - totalApplied) }));
      setReloadKey((k) => k + 1);
    }
  }

  async function approveAllPending() {
    // Oct 22, 2026 — only the deals that can be approved. Before, Approve All
    // sent everything, gated or not; now a deal on hold (roof crew payment,
    // first payment) stays in Pending and can't fail the batch.
    const ready = pending.filter((d) => !approvalBlockReason(d));
    const ids = ready.map((d) => d.id);
    if (!ids.length) return;
    setActing(new Set(ids));
    let now = new Date().toISOString();

    // Oct 8, 2026 — one commission_approve call = one approval batch: the RPC
    // flags every deal (old-flow deals also advance the stage), stores the
    // lines and makes ONE Books bill per person for the whole batch. All or
    // nothing: a deal the RPC refuses (already approved, not ready, a bill
    // already standing, the first payment not cleared) fails the batch with
    // its name.
    const { data, error } = await supabase.rpc("commission_approve", {
      p_deal_ids: ids,
      p_lines: payLinesFor(ids),
      p_source: "commission_board_approve_all",
    });
    if (error) {
      alert(`Error: ${formatSupabaseError(error)}`);
    } else {
      const res = (data ?? {}) as ApproveResult;
      now = res.approved_at ?? now;
      warnDealsWithoutBill(res);
      // Book non-collect overage shortfalls for each approved deal (mirrors the
      // per-deal approve path). Bulk approve does not run FIFO repayments today.
      for (const d of ready) {
        await bookNonCollectOverageDraws(d);
      }
      setDeals((prev) =>
        prev?.map((d) =>
          ids.includes(d.id)
            ? { ...d, commission_approved: true, commission_approved_at: now,
                ...(OLD_COMMISSION_STAGES.has(d.stage ?? "") ? { stage: "commission_approved" as const } : {}) }
            : d,
        ) ?? null,
      );
      setBillsKey((k) => k + 1);
    }
    setActing(new Set());
  }

  // RS pool approval (Jennifer's queue) is handled inside <RSPoolSection /> — it owns
  // its own per-deal + bulk approval UI and writes rs_pool_approved directly.

  // Sep 25, 2026 (Books Ship 2b) — the exact per-person lines being paid, taken
  // from the SAME groupByPerson math the board renders, so the stored ledger
  // (commission_payments) and Books see what Shannon saw when she clicked.
  // Merged per (deal, member, type): an Alex self-split yields two rm_split
  // halves for one person, and the DB keeps one live row per line. Zero-dollar
  // lines are dropped — nothing was paid on them.
  function payLinesFor(dealIds: string[]): PayLine[] {
    const subset = (deals ?? []).filter((d) => dealIds.includes(d.id));
    const merged = new Map<string, PayLine>();
    for (const g of groupByPerson(subset, members, canvasserByDeal)) {
      for (const tg of g.typeGroups) {
        for (const it of tg.items) {
          if (it.type === "trueup") continue; // ledger-only line, never paid through here
          const key = `${it.deal.id}|${g.personId}|${it.type}`;
          const prev = merged.get(key);
          if (prev) {
            prev.amount += it.amount; prev.gross += it.gross; prev.rev += it.rev;
            prev.fees = [...prev.fees, ...it.fees];
          } else {
            merged.set(key, { deal_id: it.deal.id, member_id: g.personId, line_type: it.type as SalesCommType,
              amount: it.amount, gross: it.gross, rate: it.rate, rev: it.rev, fees: it.fees });
          }
        }
      }
    }
    return [...merged.values()]
      .map((l) => ({ ...l, amount: round2(l.amount), gross: round2(l.gross), rev: round2(l.rev) }))
      .filter((l) => l.amount >= 0.005);
  }

  // A deal can be flag-paid with no line to record when its earner isn't on
  // the active roster (the Rico rule: an inactive member renders no line). Say
  // so out loud — nothing posts to Books for that deal.
  function warnDealsWithoutLines(res: MarkPaidResult) {
    const ids = res.deals_without_lines ?? [];
    if (!ids.length) return;
    const names = ids.map((id) => (deals ?? []).find((d) => d.id === id)?.homeowner_name ?? id);
    alert(`Marked paid, but no commission lines were recorded for ${names.join(", ")} — the earner isn't on the active roster, so nothing will post to Books. Reactivate them and re-pay, or book it in Books by hand.`);
  }

  // Oct 8, 2026 — the same rule at approval: a deal whose earner is off the
  // active roster yields no line, so no Books bill carries it; it sits on the
  // Approved tab with Shannon's legacy Mark paid and nothing posts to Books.
  function warnDealsWithoutBill(res: ApproveResult) {
    const ids = res.deals_without_lines ?? [];
    if (!ids.length) return;
    const names = ids.map((id) => (deals ?? []).find((d) => d.id === id)?.homeowner_name ?? id);
    alert(`Approved, but no commission lines were recorded for ${names.join(", ")} — the earner isn't on the active roster, so no Books bill was made for ${ids.length === 1 ? "it" : "them"}. Reactivate them and un-approve / re-approve, or pay by hand.`);
  }

  // Mark RS pool paid — Shannon (finance) action. Writes one rs_pool ledger
  // line per active pool member (commissionable × 1.5% × share — the same
  // math RSPoolPaidView renders) and flips rs_pool_paid in one transaction via
  // the rs_pool_mark_paid RPC, which also resolves rs_pool_paid_by.
  async function markRSPoolPaid(dealId: string) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    if (!deal) return;
    setActing((prev) => new Set(prev).add(`rspay-${dealId}`));
    const { data: pool, error: poolErr } = await supabase
      .from("rs_pool_members")
      .select("team_member_id, share")
      .eq("status", "active");
    if (poolErr) {
      alert(`Couldn't load the RS pool: ${formatSupabaseError(poolErr)}`);
      setActing((prev) => { const n = new Set(prev); n.delete(`rspay-${dealId}`); return n; });
      return;
    }
    // Oct 8, 2026 — the same split rs_pool_approve sends (lib/rsPoolLines).
    const lines = rsPoolLinesFor(dealId, getRSDealRev(deal), (pool ?? []) as Array<{ team_member_id: string; share: number | null }>);
    const { data, error } = await supabase.rpc("rs_pool_mark_paid", { p_deal_id: dealId, p_lines: lines });
    if (error) {
      alert(`Couldn't mark the RS pool paid: ${formatSupabaseError(error)}`);
    } else {
      const now = ((data ?? {}) as MarkPaidResult).paid_at ?? new Date().toISOString();
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId ? { ...d, rs_pool_paid: true, rs_pool_paid_at: now } : d,
        ) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`rspay-${dealId}`); return n; });
  }

  async function markPaid(dealId: string) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    // Jul 16, 2026 — hold-to-closure gate (new structure, credits ≥ $501).
    // Escape hatches: collecting the balance, a Shannon write-off, or (Oct 6,
    // 2026) Chad's audited "Release hold". The commission_mark_paid RPC
    // re-checks this server-side.
    const hold = deal ? payHoldReason(deal) : null;
    if (hold) {
      alert(`Payment held: ${hold}`);
      return;
    }
    setActing((prev) => new Set(prev).add(`pay-${dealId}`));
    const isOldFlow = deal ? OLD_COMMISSION_STAGES.has(deal.stage ?? "") : false;
    // Sep 25, 2026 (Books Ship 2b): the RPC stores every line's dollars, flips
    // commission_paid and writes the finance_audit_log row in ONE transaction
    // (the Jul 15 best-effort audit insert is now guaranteed, with amounts).
    const { data, error } = await supabase.rpc("commission_mark_paid", {
      p_deal_ids: [dealId],
      p_lines: payLinesFor([dealId]),
      p_source: "commission_board_mark_paid",
    });
    if (error) {
      alert(`Couldn't mark paid: ${formatSupabaseError(error)}`);
    } else {
      const res = (data ?? {}) as MarkPaidResult;
      const now = res.paid_at ?? new Date().toISOString();
      warnDealsWithoutLines(res);
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d, commission_paid: true, commission_paid_at: now,
                ...(isOldFlow ? { stage: "commission_paid" as const } : {}) }
            : d,
        ) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`pay-${dealId}`); return n; });
  }

  async function markGroupPaid(rawDealIds: string[]) {
    // Jul 16, 2026 — hold-to-closure gate: silently paying held deals in a bulk
    // click is exactly the "Pay All" incident class. Filter them out and say so.
    const heldIds = rawDealIds.filter((id) => {
      const d = (deals ?? []).find((x) => x.id === id);
      return d ? payHoldReason(d) !== null : false;
    });
    const dealIds = rawDealIds.filter((id) => !heldIds.includes(id));
    if (heldIds.length > 0 && dealIds.length === 0) {
      alert("All of these deals are held until their balance is collected in full (credits ≥ $501) — nothing was paid.");
      return;
    }
    if (heldIds.length > 0) {
      alert(`${heldIds.length} deal${heldIds.length === 1 ? " is" : "s are"} held until the balance is collected in full (credits ≥ $501) and ${heldIds.length === 1 ? "was" : "were"} skipped. Paying the other ${dealIds.length}.`);
    }
    if (!dealIds.length) return;
    const keys = dealIds.map((id) => `pay-${id}`);
    setActing(new Set(keys));

    // Sep 25, 2026 (Books Ship 2b): one RPC call, all-or-nothing — every line
    // on every deal in the group is stored with its dollars, the flags flip,
    // and one finance_audit_log row per deal lands (group_size + batch_id) in
    // the same transaction. If any deal fails validation, none are paid.
    const { data, error } = await supabase.rpc("commission_mark_paid", {
      p_deal_ids: dealIds,
      p_lines: payLinesFor(dealIds),
      p_source: "commission_board_pay_all",
    });
    if (error) {
      alert(`Couldn't pay the group: ${formatSupabaseError(error)}`);
    } else {
      const res = (data ?? {}) as MarkPaidResult;
      const now = res.paid_at ?? new Date().toISOString();
      warnDealsWithoutLines(res);
      setDeals((prev) =>
        prev?.map((d) =>
          dealIds.includes(d.id)
            ? { ...d, commission_paid: true, commission_paid_at: now,
                ...(OLD_COMMISSION_STAGES.has(d.stage ?? "") ? { stage: "commission_paid" as const } : {}) }
            : d,
        ) ?? null,
      );
    }
    setActing(new Set());
  }

  // Oct 6, 2026 — Chad releases (or restores) the ≥$501-credit pay hold.
  // Server enforces the approval tier, requires a reason, writes the audit row.
  async function releaseHold(dealId: string, release: boolean) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    let reason: string | null = null;
    if (release) {
      const held = deal ? payHoldReason(deal) : null;
      reason = window.prompt(
        `Release the pay hold on ${deal?.homeowner_name ?? "this deal"}?\n\n${held ?? ""}\n\nShannon will be able to pay the commission now. Reason (required):`,
      );
      if (reason === null) return;
      if (reason.trim().length < 5) { alert("Please give a reason (5+ characters)."); return; }
    } else if (!window.confirm("Put the pay hold back on this deal?")) {
      return;
    }
    setActing((prev) => new Set(prev).add(`hold-${dealId}`));
    const { error } = await supabase.rpc("commission_release_pay_hold", {
      p_deal_id: dealId,
      p_reason: reason,
      p_release: release,
    });
    if (error) {
      alert(`Couldn't ${release ? "release" : "restore"} the hold: ${formatSupabaseError(error)}`);
    } else {
      const now = new Date().toISOString();
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d,
                commission_hold_released_at: release ? now : null,
                commission_hold_released_by: release ? member?.id ?? null : null,
                commission_hold_release_reason: release ? reason!.trim() : null }
            : d,
        ) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`hold-${dealId}`); return n; });
  }

  // Oct 28, 2026 (Greg: "I need an override on commissions board") — Chad
  // overrides (or puts back) the first-payment hold on a deal. Server enforces
  // the approval tier, requires a reason, writes the audit row; the database's
  // commission_first_payment_hold answers null for the deal from then on, so
  // Approve (and Approve All) go through.
  async function overrideFirstPaymentHold(dealId: string, override: boolean) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    let reason: string | null = null;
    if (override) {
      const held = deal ? commissionFirstPaymentHold(deal as unknown as Record<string, unknown>) : null;
      reason = window.prompt(
        `Override the first-payment hold on ${deal?.homeowner_name ?? "this deal"}?\n\n${held?.message ?? ""}\n\nThe commission can be approved now, with ${held ? fmtUSD(held.short) : "money"} still out. Reason (required):`,
      );
      if (reason === null) return;
      if (reason.trim().length < 5) { alert("Please give a reason (5+ characters)."); return; }
    } else if (!window.confirm("Put the first-payment hold back on this deal?")) {
      return;
    }
    setActing((prev) => new Set(prev).add(`fp-override-${dealId}`));
    const { error } = await supabase.rpc("commission_override_first_payment_hold", {
      p_deal_id: dealId,
      p_reason: reason,
      p_override: override,
    });
    if (error) {
      alert(`Couldn't ${override ? "override" : "restore"} the hold: ${formatSupabaseError(error)}`);
    } else {
      const now = new Date().toISOString();
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d,
                commission_first_payment_override_at: override ? now : null,
                commission_first_payment_override_by: override ? member?.id ?? null : null,
                commission_first_payment_override_reason: override ? reason!.trim() : null }
            : d,
        ) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`fp-override-${dealId}`); return n; });
  }

  // Jul 16, 2026 — Chad marks a deal's 5★ homeowner review as verified (manual
  // Google check). Drives the 12/11% tiers on new-structure deals; toggling it
  // on an already-paid deal surfaces the true-up in ReviewTrueUpPanel.
  async function toggleFiveStar(dealId: string, next: boolean) {
    setActing((prev) => new Set(prev).add(`5star-${dealId}`));
    const now = new Date().toISOString();
    const payload = next
      ? { five_star_review_verified: true, five_star_review_verified_by: member?.id ?? null, five_star_review_verified_at: now }
      : { five_star_review_verified: false, five_star_review_verified_by: null, five_star_review_verified_at: null };
    const { error } = await supabase.from("deals").update(payload).eq("id", dealId);
    if (error) {
      alert(`Error: ${formatSupabaseError(error)}`);
    } else {
      void supabase.from("finance_audit_log").insert({
        deal_id: dealId,
        actor_id: member?.id ?? null,
        action: "five_star_review_verified",
        is_financial: false,
        after_jsonb: { verified: next, source: "commission_board" },
        reason: next
          ? "5★ homeowner review verified — unlocks the 11/12% commission tier on this deal."
          : "5★ review verification removed.",
      }).then(({ error: e }) => { if (e) console.warn("5★ audit log failed", e); });
      setDeals((prev) =>
        prev?.map((d) =>
          d.id === dealId
            ? { ...d,
                five_star_review_verified: next,
                five_star_review_verified_at: next ? now : null }
            : d,
        ) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`5star-${dealId}`); return n; });
  }

  // Jul 16, 2026 — Shannon marks the review true-up (the 1–2pt delta on a deal
  // paid at 10% before its 5★ verified) as paid out.
  async function markTrueUpPaid(dealId: string, amount: number) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    if (!deal) return;
    setActing((prev) => new Set(prev).add(`trueup-${dealId}`));
    // Sep 25, 2026 (Books Ship 2b): the per-person deltas become `trueup`
    // ledger lines; the RPC stamps five_star_trueup_paid_at/_by + the audit
    // row in one transaction. `amount` is the panel's displayed total — it
    // should equal the sum of the lines; log loudly if it ever drifts.
    const lines = trueUpDeltas(deal, members).map((l) => ({ member_id: l.personId, amount: round2(l.delta), rev: round2(getDealRev(deal)) }));
    const lineTotal = round2(lines.reduce((s, l) => s + l.amount, 0));
    if (Math.abs(lineTotal - amount) > 0.01) console.warn("true-up total mismatch", { dealId, amount, lineTotal });
    const { data, error } = await supabase.rpc("commission_trueup_mark_paid", { p_deal_id: dealId, p_lines: lines });
    if (error) {
      alert(`Couldn't pay the true-up: ${formatSupabaseError(error)}`);
    } else {
      const now = ((data ?? {}) as MarkPaidResult).paid_at ?? new Date().toISOString();
      setDeals((prev) =>
        prev?.map((d) => (d.id === dealId ? { ...d, five_star_trueup_paid_at: now } : d)) ?? null,
      );
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`trueup-${dealId}`); return n; });
  }

  // Sep 30, 2026 — Shannon pays the one-time 1-pt full-scope rate catch-up.
  async function markRateCatchupPaid(dealId: string) {
    const deal = (deals ?? []).find((d) => d.id === dealId);
    if (!deal) return;
    const row = rateCatchups([deal], members)[0];
    if (!row) return;
    setActing((prev) => new Set(prev).add(`catchup-${dealId}`));
    const lines = row.lines.map((l) => ({ member_id: l.personId, amount: round2(l.delta), rev: round2(getDealRev(deal)) }));
    const { data, error } = await supabase.rpc("commission_rate_catchup_mark_paid", { p_deal_id: dealId, p_lines: lines });
    if (error) {
      alert(`Couldn't pay the rate catch-up: ${formatSupabaseError(error)}`);
    } else {
      const now = ((data ?? {}) as MarkPaidResult).paid_at ?? new Date().toISOString();
      setDeals((prev) => prev?.map((d) => (d.id === dealId ? { ...d, full_scope_catchup_paid_at: now } : d)) ?? null);
    }
    setActing((prev) => { const n = new Set(prev); n.delete(`catchup-${dealId}`); return n; });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (err) return <div className="p-4 bg-red-50 text-red-700 rounded">{err}</div>;
  if (!deals) return <div className="p-8 text-slate-500">Loading commissions…</div>;

  const content = (
    <>
      <div className={"mb-4 flex items-start justify-between gap-3 flex-wrap" + (embedded ? " justify-end" : "")}>
        {!embedded && (
          <div>
            <h1 className="text-2xl font-semibold text-bardin-ink">Commission Approvals</h1>
            <p className="text-sm text-slate-500">
              Chad approves RM / TL / ASM → Jennifer approves RS Pool → each approval is a bill in Books → Shannon pays it there.
            </p>
          </div>
        )}
        {(canMarkPaid(role, isSuperUser) || canApprove(role, isSuperUser)) && (
          <div className="flex flex-col items-end gap-1 ml-auto">
            <button
              type="button"
              onClick={checkBooksNow}
              disabled={checkingBooks}
              title="Confirm what Books answered to the bills sent and pull what Books has paid (the cron does this every 5 minutes)"
              className="px-3 py-1.5 text-xs font-medium rounded border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-50"
            >
              {checkingBooks ? "Checking Books…" : "Check Books now"}
            </button>
            {booksCheck && (
              <span className={"text-[11px] " + (booksCheck.startsWith("Couldn't") ? "text-red-700" : "text-slate-500")}>{booksCheck}</span>
            )}
          </div>
        )}
      </div>

      {/* Oct 28, 2026 — "Send to Shannon": when Chad is done approving for the week, one email with every
          payout approved since the last report (Greg: "Just 1 with that weeks commissions on it for all"). */}
      {(canApprove(role, isSuperUser) || canMarkPaid(role, isSuperUser)) && <CommissionReportCard />}

      {/* KPI Cards — also act as filters; click to switch the tab below */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KpiCard
          label="Pending"
          value={fmtUSD(totals.pending, { short: true })}
          sub={`${fmtInt(counts.pending)} deals`}
          valueClass="text-amber-600"
          onClick={() => setTab("pending")}
          active={tab === "pending"}
        />
        <KpiCard
          label="Approved"
          value={fmtUSD(totals.approved, { short: true })}
          sub={`${fmtInt(counts.approved)} deals`}
          valueClass="text-blue-600"
          onClick={() => setTab("approved")}
          active={tab === "approved"}
        />
        <KpiCard
          label="Paid"
          value={fmtUSD(totals.paid, { short: true })}
          sub={`${fmtInt(counts.paid)} deals`}
          valueClass="text-emerald-600"
          onClick={() => setTab("paid")}
          active={tab === "paid"}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1" aria-label="Commission tabs">
          {TABS.map((t) => {
            const count = counts[t.key];
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={
                  "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 " +
                  (tab === t.key
                    ? "text-bardin-teal border-bardin-teal"
                    : "text-slate-500 border-transparent hover:text-slate-700")
                }
              >
                {t.label}
                {count > 0 && (
                  <span className={
                    "ml-1.5 text-xs px-1.5 py-0.5 rounded-full " +
                    (tab === t.key ? "bg-bardin-teal/10 text-bardin-teal" : "bg-slate-100 text-slate-500")
                  }>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Category filter pill bar — matches the SupplementsTab pattern.
          Slices the view by payment bucket: Sales (RM/TL/ASM), Roof Team (RS pool),
          or Crew (installer crew vendor payments). "All" stacks all three. */}
      <div className="bg-white rounded-xl shadow p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
            Filter by category — tap to focus
          </span>
          {category !== "all" ? (
            <button
              onClick={() => setCategory("all")}
              className="text-[11px] px-2.5 py-1 rounded-full bg-bardin-orange/10 text-bardin-orange font-bold hover:bg-bardin-orange/20 transition-colors"
            >
              Showing: {CATEGORIES.find((c) => c.key === category)?.label} · ✕ clear
            </button>
          ) : (
            <span className="text-[11px] text-slate-400">Showing all categories</span>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {CATEGORIES.map((c, i) => {
            const isActive = category === c.key;
            const isDim = category !== "all" && !isActive;
            const baseClass = "text-[11px] font-semibold whitespace-nowrap px-2.5 py-1 rounded transition-all flex-shrink-0";
            const stateClass = isActive
              ? "bg-bardin-orange text-white shadow"
              : isDim
              ? "bg-slate-100 text-slate-500 opacity-50 hover:opacity-100"
              : "bg-slate-100 text-slate-700 hover:bg-bardin-orange/10 hover:text-bardin-orange";
            return (
              <span key={c.key} className="flex items-center flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={baseClass + " " + stateClass}
                >
                  {c.label}
                </button>
                {i < CATEGORIES.length - 1 && (
                  <span className="text-slate-300 text-xs px-0.5">·</span>
                )}
              </span>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {tab === "pending" && category !== "canvassers" && (
        <PendingTab
          pending={pending}
          rsPoolPending={rsPoolPending}
          members={members}
          acting={acting}
          role={role}
          isSuperUser={isSuperUser}
          onApprove={approveCommission}
          onApproveAll={approveAllPending}
          approvalBlockReason={approvalBlockReason}
          onOverrideFirstPaymentHold={overrideFirstPaymentHold}
          plans={plans}
          tradeRows={tradeRows}
          onEdit={(dealId) => setEditingDealId(dealId)}
          onCrewApproved={() => setReloadKey((k) => k + 1)}
          onReloadAfterSplit={() => setReloadKey((k) => k + 1)}
          category={category}
          canvasserByDeal={canvasserByDeal}
          drawBalances={drawBalances}
          onApplyDrawPaydown={applyDrawPaydown}
          onToggleFiveStar={toggleFiveStar}
        />
      )}

      {/* Oct 29, 2026 — Greg: the subs' trades short of the 30% margin wait on Chad here. */}
      {tab === "pending" && (category === "all" || category === "sales") && <TradeMarginReviews onSettled={() => setReloadKey((k) => k + 1)} />}

      {/* Jul 16, 2026 — review true-ups: deals commission-paid at 10% before
          their 5★ review verified (marked on the deal Overview). Shannon pays
          the delta on the next payout run. */}
      {tab === "pending" && (category === "all" || category === "sales") && (
        <ReviewTrueUpPanel
          deals={deals}
          members={members}
          acting={acting}
          canPay={canMarkPaid(role, isSuperUser)}
          onMarkTrueUpPaid={markTrueUpPaid}
          onMarkCatchupPaid={markRateCatchupPaid}
        />
      )}

      {/* Ship 5: Commission edit modal — Chad's per-deal override surface */}
      {editingDealId && (() => {
        const d = deals.find((x) => x.id === editingDealId);
        if (!d) return null;
        // Sep 30, 2026 — one row per person per line, from the payout engine.
        return (
          <CommissionEditModal
            deal={d}
            lines={commissionEditLines(d, members, canvasserByDeal)}
            onClose={() => setEditingDealId(null)}
            onSaved={() => { setEditingDealId(null); setReloadKey((k) => k + 1); }}
          />
        );
      })()}
      {tab === "approved" && (category === "all" || category === "sales") && (
        <GroupedTab
          groups={approvedGroups}
          members={members}
          acting={acting}
          role={role}
          isSuperUser={isSuperUser}
          showPayButton
          onMarkPaid={markPaid}
          onMarkGroupPaid={markGroupPaid}
          onReleaseHold={releaseHold}
          bills={bills}
          onUnapprove={unapproveCommission}
          emptyMsg="No approved commissions awaiting payment."
        />
      )}
      {tab === "approved" && (category === "all" || category === "crew") && (
        <div className={category === "all" ? "mt-4" : ""}>
          {category === "all" && (
            <h2 className="text-sm font-semibold text-bardin-ink mb-3">
              Roof Crew (awaiting disbursement)
            </h2>
          )}
          <RoofCrewApprovedSection
            scope="crew"
            role={role}
            isSuperUser={isSuperUser}
            onPaid={() => setReloadKey((k) => k + 1)}
          />
        </div>
      )}
      {tab === "approved" && (category === "all" || category === "pm") && (
        <div className={category === "all" ? "mt-4" : ""}>
          {category === "all" && (
            <h2 className="text-sm font-semibold text-bardin-ink mb-3">
              PM Pay — awaiting disbursement
            </h2>
          )}
          <RoofCrewApprovedSection
            scope="pm"
            role={role}
            isSuperUser={isSuperUser}
            onPaid={() => setReloadKey((k) => k + 1)}
          />
        </div>
      )}
      {tab === "approved" && (category === "all" || category === "roofteam") && (
        <div className={category === "all" ? "mt-4" : ""}>
          {category === "all" && (
            <h2 className="text-sm font-semibold text-bardin-ink mb-3">
              Roof Team Pay (RS pool) — approved, awaiting payout
            </h2>
          )}
          <RSPoolApprovedView
            allDeals={deals}
            members={members}
            acting={acting}
            onMarkRSPoolPaid={markRSPoolPaid}
            canMarkPaid={canMarkPaid(role, isSuperUser)}
            bills={bills}
          />
        </div>
      )}
      {tab === "paid" && category !== "canvassers" && (
        <PaidView
          paidGroups={paidGroups}
          allDeals={deals}
          members={members}
          stored={storedPayments}
          payeeNames={payeeNames}
          acting={acting}
          role={role}
          isSuperUser={isSuperUser}
          onMarkPaid={markPaid}
          onMarkGroupPaid={markGroupPaid}
          category={category}
          dateRange={paidDateRange}
          setDateRange={setPaidDateRange}
          personId={paidPersonId}
          setPersonId={setPaidPersonId}
          customFrom={paidCustomFrom}
          setCustomFrom={setPaidCustomFrom}
          customTo={paidCustomTo}
          setCustomTo={setPaidCustomTo}
        />
      )}

      {/* Ship 3 (May 22, 2026) — weekly canvasser payouts. Approve here on Chad's
          tab alongside everyone else's commissions; Shannon marks paid → QBO 1099 bill.
          Renders on all three tabs filtered to the matching payout status. */}
      {(category === "all" || category === "canvassers") && (
        <CanvasserPayoutsSection
          tab={tab}
          role={role}
          isSuperUser={isSuperUser}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}

      {/* Jun 17, 2026 — TL $60k monthly override bonus. Generated month-end by the
          tl_monthly_bonus_close_out cron; Chad approves here, Shannon marks paid. */}
      {(category === "all" || category === "sales") && (
        <TLMonthlyBonusSection
          tab={tab}
          role={role}
          isSuperUser={isSuperUser}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}

      <p className="text-xs text-slate-500 mt-4">
        <strong>Agreements signed on/after Jul 20, 2026 (per-deal RM rate):</strong> ≥$20k full insurance scope (zero credits):
        11%, 12% once the 5★ is verified · every other job with credits ≤$500, under $20k included: 10%, 11% once the 5★ is verified.
        12% is the ceiling. Credits = misc + non-collect (ACV credits excluded); credits ≥$501 → 10% and payment held until the balance is
        collected in full. A 5★ review verified since Oct 19 pays its point as a 5-Star Review SPIFF with the weekly SPIFFs (an earlier review
        trues up on the next payout). Achieved-12% reps (James / Rico / Alex / Rob): flat achieved rate, credit rule only. <strong>Signed earlier:</strong> achieved per-person rate (10% base; 12% achieved).
        TL: 1% override (→ 2% month-end bonus once a rep clears $60k/mo commissionable). ASM: 4% override. RS Pool: 1.5%.
        Non-collect revenue and the trade price shortfalls Chad deducted share one bucket per deal: the first $500 is forgiven and what's over
        comes off RM + TL (+ ASM) — default 75/25, editable per deal above — stacking with the rate tiers. A homeowner-billed trade priced above
        the 30% floor pays the difference as the trade upside pool: to the RM unless Chad shares it (✎ Edit).
      </p>
    </>
  );

  if (embedded) return content;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {content}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Tab
// ---------------------------------------------------------------------------

function PendingTab({
  pending,
  rsPoolPending,
  members,
  acting,
  role,
  isSuperUser,
  onApprove,
  onApproveAll,
  approvalBlockReason,
  onOverrideFirstPaymentHold,
  plans,
  tradeRows,
  onEdit,
  onCrewApproved,
  onReloadAfterSplit,
  category,
  canvasserByDeal,
  drawBalances,
  onApplyDrawPaydown,
  onToggleFiveStar,
}: {
  pending: CommissionDeal[];
  rsPoolPending: CommissionDeal[];
  members: Record<string, TeamMember>;
  acting: Set<string>;
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onApprove: (id: string) => void;
  onApproveAll: () => void;
  approvalBlockReason: (deal: CommissionDeal) => ApprovalGate;
  onOverrideFirstPaymentHold: (dealId: string, override: boolean) => void;
  plans: Record<string, BuildPaymentPlan>;
  /** Oct 29, 2026 — each pending deal's priced trades (DealTradesStrip); empty for a viewer kept off prices. */
  tradeRows: Record<string, BoardTradeRow[]>;
  onEdit: (dealId: string) => void;
  onCrewApproved: () => void;
  onReloadAfterSplit: () => void;
  category: CategoryKey;
  canvasserByDeal: Record<string, CanvasserRef>;
  drawBalances: Record<string, number>;
  onApplyDrawPaydown: (memberId: string, amount: number, dealId: string) => Promise<void> | void;
  onToggleFiveStar: (dealId: string, next: boolean) => void;
}) {
  const showSales    = category === "all" || category === "sales";
  const showRoofTeam = category === "all" || category === "roofteam";
  const showCrew     = category === "all" || category === "crew";
  // Approve All sends only these; the rest wait on a gate (approveAllPending).
  const readyCount = pending.filter((d) => !approvalBlockReason(d)).length;
  const heldCount = pending.length - readyCount;
  return (
    <div className="space-y-6">
      {/* Section 1: RM / TL / ASM — Chad approves (Sales Pay) */}
      {showSales && (
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-bardin-ink">
              RM / TL / ASM Commissions
            </h2>
            <p className="text-xs text-slate-500">
              Chad reviews and approves. Confirms total revenue.
              {heldCount > 0 && <span className="text-red-600"> · {heldCount} on hold</span>}
            </p>
          </div>
          {readyCount > 0 && canApprove(role, isSuperUser) && (
            <button
              onClick={onApproveAll}
              title={heldCount > 0 ? `${heldCount} on hold won't be approved` : undefined}
              className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90"
            >
              Approve All ({readyCount})
            </button>
          )}
        </div>

        {pending.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
            No commissions pending Chad's approval.
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((d) => {
              const rev = getDealRev(d);
              const isSplit = !!d.split_rm_id;
              const commLines = dealCommLines(d, members, canvasserByDeal);
              // Oct 29, 2026 — the trade upside pool and what its lines pay (Chad's "<member>:upside" edits can drift
              // from the pool when a trade is repriced or cancelled after he split it).
              const upsidePool = getTradeUpside(d);
              const upsideLines = commLines.filter((l) => l.type === "trade_upside");
              const upsidePaid = round2(upsideLines.reduce((s, l) => s + l.gross, 0));
              const ownerName = d.owner_id ? members[d.owner_id]?.full_name ?? "—" : "—";
              const splitName = d.split_rm_id ? members[d.split_rm_id]?.full_name : null;
              const miscPenalty = Number(d.misc_credit_penalty) || 0;

              return (
                <div key={d.id} className="bg-white rounded-xl shadow p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <DealLink id={d.id} className="font-medium text-sm truncate">
                          {d.homeowner_name ?? "(no name)"}
                        </DealLink>
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                          Pending
                        </span>
                        {isSplit && (
                          <span className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Split</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {d.property_street ?? "—"}
                        {d.claim_number ? ` · #${d.claim_number}` : ""}
                        {d.total_squares ? ` · ${d.total_squares}sq` : ""}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        RM: {ownerName}{splitName ? ` + ${splitName}` : ""}
                        {d.build_completed_date ? ` · Built ${d.build_completed_date.slice(0, 10)}` : ""}
                      </div>
                      <DealAgeChips deal={d} className="mt-1" />
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(rev, { short: true })}</div>
                      <div className="text-xs text-slate-500">revenue</div>
                    </div>
                  </div>

                  {/* Commission breakdown — Sep 30, 2026 (Greg): itemized per
                      person straight from the payout engine (dealCommLines), so
                      each RM's own rate, per-half TL, split-aware ASM and Chad's
                      edits all show exactly as they'll pay. */}
                  {isRetailDeal(d) && (
                    <div className="mt-2 text-xs bg-teal-50 text-teal-800 rounded-lg px-3 py-2 flex items-start gap-2">
                      <span>🏷</span>
                      <span>
                        <span className="font-semibold">Retail deal</span> — pool {fmtUSD(rev)} ={" "}
                        {fmtUSD(Number(d.retail_contract_total) || 0)} contract −{" "}
                        {Number(d.retail_squares) || 0} sq × {fmtUSD(Number(d.retail_base_per_square) || 460)}/sq base
                        {Number(d.retail_at_cost_total) > 0 ? ` − ${fmtUSD(Number(d.retail_at_cost_total))} at-cost items` : ""}
                        {rawTradeUpside(d) > 0 ? ` + ${fmtUSD(rawTradeUpside(d))} trade upside (the homeowner's price above the 30% floor on the trades)` : ""}.
                        The pool goes to the RM until you split it — use ✎ Edit to give TL/ASM a flat share.
                      </span>
                    </div>
                  )}
                  {/* Oct 29, 2026 (Greg) — the trade upside pool on an insurance deal: paid like the retail pool. */}
                  {!isRetailDeal(d) && (upsidePool > 0 || upsideLines.length > 0) && (
                    <div className="mt-2 text-xs bg-teal-50 text-teal-800 rounded-lg px-3 py-2 flex items-start gap-2">
                      <span>🔧</span>
                      <span>
                        <span className="font-semibold">Trade upside pool {fmtUSD(upsidePool)}</span> = the customer's price above the
                        30% floor on the homeowner-billed trades (the trades are listed under the lines). The pool goes to the RM
                        {isSplit ? " — half each on this split —" : ""} until you split it: use ✎ Edit to give the TL / ASM a flat share.
                        {upsideLines.some((l) => l.overridden) && Math.abs(upsidePaid - upsidePool) > 0.005 && (
                          <span className="block mt-1 font-semibold text-amber-800">
                            ⚠ The Upside lines set by hand pay {fmtUSD(upsidePaid)}, not the pool's {fmtUSD(upsidePool)} — a trade's price
                            changed after the split. Use ✎ Edit to share the pool again.
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <CommLinesList lines={commLines} rev={rev} />
                  <DealTradesStrip rows={tradeRows[d.id] ?? []} />

                  {/* Jul 16, 2026 — new-structure tier banner + Chad's 5★ toggle.
                      Renders only on deals signed on/after Jul 20, 2026. */}
                  <NewStructureBanner
                    deal={d}
                    members={members}
                    canVerify={canApprove(role, isSuperUser)}
                    busy={acting.has(`5star-${d.id}`)}
                    onToggleFiveStar={onToggleFiveStar}
                  />

                  {miscPenalty > 0 && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                      Misc credit penalty: −{fmtUSD(miscPenalty)} (legacy ≥$501 rule — voided May 15, 2026; column dormant)
                    </div>
                  )}

                  {/* May 15, 2026 — Non-Collect Revenue banner; since Oct 29, 2026
                      the deal's bucket (non-collect + the trade shortfalls Chad
                      deducted, first $500 forgiven). Renders when something is
                      over the $500 (or a shortfall / grandfathered note). Chad can
                      edit the RM / TL / ASM split here before clicking Approve. */}
                  <NonCollectBanner
                    deal={d}
                    members={members}
                    canEdit={canApprove(role, isSuperUser)}
                    onSplitSaved={onReloadAfterSplit}
                  />

                  {/* Jun 17, 2026 — outstanding-draw paydown. Shows each earner's
                      outstanding draw balance and lets the approver apply any
                      portion of THIS deal's commission against it. */}
                  <DrawPaydownPanel
                    deal={d}
                    members={members}
                    drawBalances={drawBalances}
                    canvasserByDeal={canvasserByDeal}
                    canAct={canApprove(role, isSuperUser)}
                    onApply={onApplyDrawPaydown}
                  />

                  {/* May 22, 2026 — Canvasser carve-out banner. Renders on
                      canvasser-sourced deals: RM loses 5 pts → captured-revenue
                      pool credited to the canvasser. TL/ASM unaffected. The
                      RM box above shows GROSS; the deduction is applied to the
                      net RM payout in the Approved/Paid views. */}
                  {(() => {
                    const carve = getCanvasserCarveout(d, canvasserByDeal);
                    if (!carve) return null;
                    return (
                      <div className="mt-2 text-xs bg-teal-50 text-teal-800 rounded-lg px-3 py-2 flex items-start gap-2">
                        <span>🚪</span>
                        <span>
                          Canvasser lead (<span className="font-medium">{carve.canvasser.name}</span>) —
                          RM commission −{fmtUSD(carve.captured)} (5 pts → captured-revenue pool).
                          TL / ASM unaffected.
                        </span>
                      </div>
                    );
                  })()}

                  {/* Ship 5: gate + actions row. A gate (roof crew payment, or since
                      Oct 22, 2026 the first payment not cleared) = red banner +
                      disabled Approve. On a first-payment hold, the plan recorded
                      at build submission shows under it, and Chad can override
                      the hold (Oct 28, 2026; audited, reason required). An
                      overridden deal shows the override instead, with a way to
                      put the hold back. Edit is always visible per Greg's rule. */}
                  {(() => {
                    const gate = approvalBlockReason(d);
                    const plan = gate?.firstPayment ? plans[d.id] : undefined;
                    const overridden = d.commission_first_payment_override_at;
                    const overrideBusy = acting.has(`fp-override-${d.id}`);
                    const mayOverride = canApprove(role, isSuperUser);
                    return (
                      <>
                        {gate && (
                          <div className="mt-3 text-xs rounded-lg px-3 py-2 flex items-start gap-2 bg-red-50 text-red-700">
                            <span>⛔</span>
                            <span>{gate.message}</span>
                          </div>
                        )}
                        {overridden && (
                          <div className="mt-3 text-xs rounded-lg px-3 py-2 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900">
                            <span>⚠️</span>
                            <span>
                              First-payment hold overridden {overridden.slice(0, 10)}
                              {d.commission_first_payment_override_by && members[d.commission_first_payment_override_by]
                                ? ` by ${members[d.commission_first_payment_override_by].full_name}` : ""}
                              {d.commission_first_payment_override_reason ? ` — ${d.commission_first_payment_override_reason}` : ""}
                              {mayOverride && (
                                <>
                                  {" "}
                                  <button
                                    onClick={() => onOverrideFirstPaymentHold(d.id, false)}
                                    disabled={overrideBusy}
                                    className="underline hover:no-underline disabled:opacity-50"
                                    title="Put the first-payment hold back on this deal"
                                  >
                                    {overrideBusy ? "Restoring…" : "Put the hold back"}
                                  </button>
                                </>
                              )}
                            </span>
                          </div>
                        )}
                        {plan && (
                          <div className="mt-1.5 text-[11px] rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900">
                            💵 Plan at build submission: <span className="font-semibold">{PAYMENT_PLAN_LABELS[plan.plan]}</span>
                            {plan.note ? ` · "${plan.note}"` : ""}
                            {` · ${new Date(plan.created_at).toLocaleDateString()}`}
                          </div>
                        )}
                        {canApprove(role, isSuperUser) && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              onClick={() => onApprove(d.id)}
                              disabled={acting.has(d.id) || !!gate}
                              title={gate?.message}
                              className={
                                "px-4 py-1.5 text-xs font-medium rounded text-white " +
                                (gate
                                  ? "bg-slate-400 cursor-not-allowed"
                                  : "bg-bardin-orange hover:opacity-90 disabled:opacity-50")
                              }
                            >
                              {acting.has(d.id) ? "Approving…" : "Approve RM / TL / ASM"}
                            </button>
                            {gate?.firstPayment && mayOverride && (
                              <button
                                onClick={() => onOverrideFirstPaymentHold(d.id, true)}
                                disabled={overrideBusy}
                                className="px-3 py-1.5 text-xs font-medium rounded border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                title="Approve this commission with part of the first payment still out. Audited; a reason is required."
                              >
                                {overrideBusy ? "Overriding…" : "⚠️ Override hold"}
                              </button>
                            )}
                            <button
                              onClick={() => onEdit(d.id)}
                              className="px-3 py-1.5 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                            >
                              ✎ Edit %s / amounts
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      )}

      {/* Section 2: RS Pool — Jennifer approves (Roof Team Pay). Ship 11 — extracted into its
          own component which reads rs_pool_members and renders multi-RS splits
          + trainee activation flow. Single-RS legacy rendering removed. */}
      {showRoofTeam && (
        <RSPoolSection
          pending={rsPoolPending}
          members={members}
          role={role}
          isSuperUser={isSuperUser}
          onApproved={onCrewApproved /* triggers parent reload */}
        />
      )}

      {/* Section 3: Roof Crew (Risol Construction default) — Ship 5 (Crew Pay).
          Approve crew payment before RM commission can flow. Auto-bumps to $100/sq at ≥60 sq.
          Description on additional approved work appears in Risol's payment email from Books. */}
      {showCrew && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-bardin-ink">Roof Crew (Risol Construction default)</h2>
              <p className="text-xs text-slate-500">Approve before RM commission can flow on the same deal.</p>
            </div>
          </div>
          <RoofCrewSection
            deals={pending}
            role={role}
            isSuperUser={isSuperUser}
            onApproved={onCrewApproved}
          />
        </div>
      )}

      {/* PM pay needs no approval: the per-build fee and the biweekly base are
          written approved by their triggers, so they wait on the Approved tab. */}
      {category === "pm" && (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          PM pay doesn't need approval — the per-build fees and the biweekly base are approved
          automatically. Every other Friday they go to Books as one bill to Risol; pay it there as its
          own payment. They're on the Approved tab until Books reports them paid.
        </div>
      )}

      {/* Empty state when filter is active but no sections match */}
      {!showSales && !showRoofTeam && !showCrew && category !== "pm" && (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          No category selected.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paid View — date range + person filter wrapper around GroupedTab.
// Shows historical paid commissions; lets Greg/Shannon/Chad review past
// pay periods by date and drill into a specific RM or crew member.
// ---------------------------------------------------------------------------

interface RoofCrewPaidRow {
  id: string;
  /** null for non-job rows such as the PM biweekly base. */
  deal_id: string | null;
  vendor_id: string;
  vendor_name: string;
  total: number;
  squares: number;
  rate_per_sq: number;
  flat_fee: number;
  additional_work_amount: number;
  additional_work_description: string | null;
  paid_at: string | null;
  paid_method: string | null;
  paid_reference: string | null;
  homeowner_name: string | null;
  property_street: string | null;
  claim_number: string | null;
}

interface RSPoolMember {
  id: string;
  team_member_id: string;
  status: "training" | "active" | "retired";
  share: number | null;
}

// RS_POOL_RATE_LIVE (1.5%) lives in lib/rsPoolLines.ts since Oct 8, 2026 — one
// source for the paid view, the approval and the mark-paid.

function getRSDealRev(d: CommissionDeal): number {
  // Mirrors getDealRev — same May 15 fix. RS pool % is computed off the same
  // commissionable revenue figure as RM/TL/ASM, so the fallback must match.
  return Number(d.commissionable_revenue) || calcScope(d).commissionable;
}

function PaidView({
  paidGroups,
  allDeals,
  members,
  stored,
  payeeNames,
  acting,
  role,
  isSuperUser,
  onMarkPaid,
  onMarkGroupPaid,
  category,
  dateRange,
  setDateRange,
  personId,
  setPersonId,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
}: {
  paidGroups: PersonGroup[];
  allDeals: CommissionDeal[] | null;
  members: Record<string, TeamMember>;
  stored: StoredPayments;
  payeeNames: Record<string, string>;
  acting: Set<string>;
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onMarkPaid: (id: string) => void;
  onMarkGroupPaid: (ids: string[]) => void;
  category: CategoryKey;
  dateRange: DateRangeKey;
  setDateRange: (r: DateRangeKey) => void;
  personId: string;
  setPersonId: (id: string) => void;
  customFrom: string;
  setCustomFrom: (v: string) => void;
  customTo: string;
  setCustomTo: (v: string) => void;
}) {
  const showSales    = category === "all" || category === "sales";
  const showCrew     = category === "all" || category === "crew";
  const showPm       = category === "all" || category === "pm";
  const showRoofTeam = category === "all" || category === "roofteam";

  // Date range bounds. When custom range is in effect, use it; otherwise preset.
  const { from: dateFrom, to: dateTo } = useMemo(
    () => dateRangeFor(dateRange, customFrom, customTo),
    [dateRange, customFrom, customTo],
  );

  // Active RS pool members — used for both the dropdown options and the
  // RSPoolPaidView per-member splits. Realtime so composition changes flow in.
  const [rsPoolMembers, setRsPoolMembers] = useState<RSPoolMember[] | null>(null);
  const [rsPoolErr, setRsPoolErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!showRoofTeam) { setRsPoolMembers([]); return; }
    async function load() {
      const { data, error } = await supabase
        .from("rs_pool_members")
        .select("id, team_member_id, status, share")
        .eq("status", "active");
      if (cancelled) return;
      if (error) { setRsPoolErr(error.message); setRsPoolMembers([]); return; }
      setRsPoolMembers((data ?? []) as RSPoolMember[]);
    }
    load();
    return () => { cancelled = true; };
  }, [showRoofTeam]);
  useRealtimeTable("rs_pool_members", () => {
    supabase.from("rs_pool_members").select("id, team_member_id, status, share").eq("status", "active")
      .then(({ data }) => setRsPoolMembers((data ?? []) as RSPoolMember[]));
  });

  // ── Sales (RM/TL/ASM) — filter the existing PersonGroup list by date + person.
  // Date filter is applied at the LINE ITEM level (per-deal commission_paid_at),
  // then groups are recomputed. Person filter is just a name match on personId.
  const filteredSalesGroups = useMemo(() => {
    let groups = paidGroups;
    // Person filter
    if (personId !== "all") {
      groups = groups.filter((g) => g.personId === personId);
    }
    // Date filter — drop deals outside the range, then rebuild groups
    if (dateFrom || dateTo) {
      groups = groups
        .map((g) => {
          const filteredTypeGroups = g.typeGroups
            .map((tg) => {
              const items = tg.items.filter((it) => {
                const ts = it.paidAt ?? it.deal.commission_paid_at;
                if (!ts) return false;
                const t = new Date(ts);
                if (dateFrom && t < dateFrom) return false;
                if (dateTo && t > dateTo) return false;
                return true;
              });
              return { ...tg, items, total: items.reduce((s, i) => s + i.amount, 0) };
            })
            .filter((tg) => tg.items.length > 0);
          const totalComm = filteredTypeGroups.reduce((s, tg) => s + tg.total, 0);
          const allDealIds = [...new Set(filteredTypeGroups.flatMap((tg) => tg.items.map((i) => i.deal.id)))];
          return { ...g, typeGroups: filteredTypeGroups, totalComm, allDealIds };
        })
        .filter((g) => g.typeGroups.length > 0);
    }
    return groups;
  }, [paidGroups, personId, dateFrom, dateTo]);

  // Person dropdown options — combine sales people + crew vendors so the dropdown
  // works regardless of which category is active.
  // Always show whatever appears in the current paidGroups (fully unfiltered) +
  // crew vendor names (loaded separately below).
  const [crewPaid, setCrewPaid] = useState<RoofCrewPaidRow[] | null>(null);
  const [crewErr, setCrewErr] = useState<string | null>(null);

  // Load crew payments where status='paid' (history) — when Crew, PM Pay or All
  // is active. Both live in roof_crew_payments; splitPmPay sorts them below.
  useEffect(() => {
    let cancelled = false;
    if (!showCrew && !showPm) { setCrewPaid([]); return; }
    setCrewPaid(null);
    async function load() {
      const { data, error } = await supabase
        .from("roof_crew_payments")
        .select(`
          id, deal_id, vendor_id, total, squares, rate_per_sq, flat_fee,
          additional_work_amount, additional_work_description, paid_at,
          paid_method, paid_reference,
          deals(homeowner_name, property_street, claim_number),
          vendors!inner(name)
        `)
        .eq("status", "paid")
        .not("paid_at", "is", null)
        .order("paid_at", { ascending: false });
      if (cancelled) return;
      if (error) { setCrewErr(error.message); setCrewPaid([]); return; }
      const rows: RoofCrewPaidRow[] = (data ?? []).map((r: any) => ({
        id: r.id,
        deal_id: r.deal_id,
        vendor_id: r.vendor_id,
        vendor_name: r.vendors?.name ?? "Unknown vendor",
        total: Number(r.total ?? 0),
        squares: Number(r.squares ?? 0),
        rate_per_sq: Number(r.rate_per_sq ?? 0),
        flat_fee: Number(r.flat_fee ?? 0),
        additional_work_amount: Number(r.additional_work_amount ?? 0),
        additional_work_description: r.additional_work_description,
        paid_at: r.paid_at,
        paid_method: r.paid_method,
        paid_reference: r.paid_reference,
        homeowner_name: r.deals?.homeowner_name ?? null,
        property_street: r.deals?.property_street ?? null,
        claim_number: r.deals?.claim_number ?? null,
      }));
      setCrewPaid(rows);
    }
    load();
    return () => { cancelled = true; };
  }, [showCrew, showPm]);

  // Filter crew payments by date + vendor (vendor uses same personId dropdown,
  // prefixed with "v:" so it doesn't collide with team_member ids).
  const filteredCrew = useMemo(() => {
    if (!crewPaid) return null;
    let rows = crewPaid;
    if (personId.startsWith("v:")) {
      const vid = personId.slice(2);
      rows = rows.filter((r) => r.vendor_id === vid);
    } else if (personId !== "all" && !personId.startsWith("v:")) {
      // Person dropdown is showing a sales person → hide crew rows entirely
      rows = [];
    }
    if (dateFrom || dateTo) {
      rows = rows.filter((r) => {
        if (!r.paid_at) return false;
        const t = new Date(r.paid_at);
        if (dateFrom && t < dateFrom) return false;
        if (dateTo && t > dateTo) return false;
        return true;
      });
    }
    return rows;
  }, [crewPaid, personId, dateFrom, dateTo]);
  // The crew job money and the PM's pay, kept apart (null while loading).
  const crewSplit = useMemo(() => (filteredCrew ? splitPmPay(filteredCrew) : null), [filteredCrew]);

  // RS pool total (computed for grand total + dropdown). Mirrors RSPoolPaidView's
  // logic but without the sub-component drilldown.
  const rsPoolTotal = useMemo(() => {
    if (!showRoofTeam || !allDeals || !rsPoolMembers) return 0;
    let total = 0;
    for (const d of allDeals) {
      if (!d.rs_pool_paid || !d.rs_pool_paid_at) continue;
      const t = new Date(d.rs_pool_paid_at);
      if (dateFrom && t < dateFrom) continue;
      if (dateTo && t > dateTo) continue;
      // Crew vendor selected — RS rows shouldn't contribute
      if (personId.startsWith("v:")) continue;
      // Sep 25, 2026 — a pool paid through the ledger uses the stored per-member
      // amounts; older pools keep the live share math.
      const storedLines = storedRsPoolLines(d, stored);
      if (storedLines) {
        for (const ln of storedLines) {
          if (personId !== "all" && ln.memberId !== personId) continue;
          total += ln.amount;
        }
        continue;
      }
      const rev = getRSDealRev(d);
      const poolForDeal = rev * RS_POOL_RATE_LIVE;
      for (const pm of rsPoolMembers) {
        const share = Number(pm.share ?? 0);
        if (share <= 0) continue;
        // If user filtered to a specific RS member, only count them
        if (personId !== "all" && pm.team_member_id !== personId) continue;
        total += poolForDeal * share;
      }
    }
    return total;
  }, [showRoofTeam, allDeals, rsPoolMembers, dateFrom, dateTo, personId, stored]);

  // Build person dropdown — sales people from paidGroups + crew vendors from crewPaid + RS members.
  const personOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; group: "sales" | "crew" | "rs" }> = [];
    if (showSales) {
      for (const g of paidGroups) opts.push({ value: g.personId, label: g.name, group: "sales" });
    }
    if ((showCrew || showPm) && crewPaid) {
      const seen = new Set<string>();
      // Vendors with rows in the pills on view (PM Pay alone: vendors carrying PM pay).
      const scoped = showCrew && showPm ? crewPaid : crewPaid.filter((r) => isPmPayRow(r) === showPm);
      for (const r of scoped) {
        if (seen.has(r.vendor_id)) continue;
        seen.add(r.vendor_id);
        opts.push({ value: `v:${r.vendor_id}`, label: r.vendor_name, group: "crew" });
      }
    }
    if (showRoofTeam && rsPoolMembers) {
      const seen = new Set<string>();
      // Don't dupe people who are already in the sales list
      for (const o of opts) if (o.group === "sales") seen.add(o.value);
      for (const pm of rsPoolMembers) {
        if (seen.has(pm.team_member_id)) continue;
        const tm = members[pm.team_member_id];
        if (!tm) continue;
        seen.add(pm.team_member_id);
        opts.push({ value: pm.team_member_id, label: tm.full_name, group: "rs" });
      }
      // Former pool members with stored payouts stay selectable.
      for (const rows of stored.byDeal.values()) {
        for (const r of rows) {
          if (r.line_type !== "rs_pool" || seen.has(r.member_id)) continue;
          seen.add(r.member_id);
          opts.push({ value: r.member_id, label: payeeName(r.member_id, members, payeeNames), group: "rs" });
        }
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [paidGroups, crewPaid, rsPoolMembers, members, showSales, showCrew, showPm, showRoofTeam, stored, payeeNames]);

  // Totals across the visible filtered set
  const salesTotal = filteredSalesGroups.reduce((s, g) => s + g.totalComm, 0);
  const crewTotal  = (crewSplit?.crew ?? []).reduce((s, r) => s + r.total, 0);
  const pmTotal    = (crewSplit?.pm ?? []).reduce((s, r) => s + r.total, 0);
  const grandTotal = (showSales ? salesTotal : 0) + (showCrew ? crewTotal : 0) + (showPm ? pmTotal : 0) + (showRoofTeam ? rsPoolTotal : 0);

  return (
    <div className="space-y-4">
      {/* Oct 19, 2026 — the commission statement emails (one per person per pay
          day, sent by the database when a payout is recorded), then the monthly /
          quarterly / annual statements sent on the 1st. Finance only. */}
      {canMarkPaid(role, isSuperUser) && (showSales || showRoofTeam) && <CommissionStatementsCard />}
      {canMarkPaid(role, isSuperUser) && (showSales || showRoofTeam) && <CommissionStatementsCard kind="period" />}

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow p-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
            Paid history filters
          </span>
          {(personId !== "all" || dateRange !== "all" || customFrom || customTo) && (
            <button
              onClick={() => { setPersonId("all"); setDateRange("all"); setCustomFrom(""); setCustomTo(""); }}
              className="text-[11px] px-2.5 py-1 rounded-full bg-bardin-orange/10 text-bardin-orange font-bold hover:bg-bardin-orange/20 transition-colors"
            >
              Clear filters · ✕
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Date range — preset pills + custom calendar inputs */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Date range
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {DATE_RANGE_OPTIONS.map((o) => {
                const isActive = dateRange === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      setDateRange(o.key);
                      // Switching to a preset clears custom inputs so they don't lurk
                      if (o.key !== "custom") { setCustomFrom(""); setCustomTo(""); }
                    }}
                    className={
                      "text-[11px] font-semibold whitespace-nowrap px-2.5 py-1 rounded transition-all " +
                      (isActive
                        ? "bg-bardin-orange text-white shadow"
                        : "bg-slate-100 text-slate-700 hover:bg-bardin-orange/10 hover:text-bardin-orange")
                    }
                  >
                    {o.label}
                  </button>
                );
              })}
              {/* Custom pill — activates when either custom date is set */}
              <button
                type="button"
                onClick={() => setDateRange("custom")}
                className={
                  "text-[11px] font-semibold whitespace-nowrap px-2.5 py-1 rounded transition-all " +
                  (dateRange === "custom"
                    ? "bg-bardin-orange text-white shadow"
                    : "bg-slate-100 text-slate-700 hover:bg-bardin-orange/10 hover:text-bardin-orange")
                }
              >
                Custom
              </button>
            </div>
            {/* Custom date inputs — only render when Custom is selected */}
            {dateRange === "custom" && (
              <div className="flex items-center gap-1.5 text-[11px]">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded border border-slate-200 px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
                  aria-label="From date"
                />
                <span className="text-slate-400">→</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded border border-slate-200 px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
                  aria-label="To date"
                />
              </div>
            )}
          </div>
          {/* Person dropdown — RM or crew vendor */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Person / Vendor
            </div>
            <select
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="w-full text-sm rounded border border-slate-200 px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
            >
              <option value="all">All people</option>
              {showSales && personOptions.some((o) => o.group === "sales") && (
                <optgroup label="Sales (RM / TL / ASM)">
                  {personOptions.filter((o) => o.group === "sales").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              )}
              {(showCrew || showPm) && personOptions.some((o) => o.group === "crew") && (
                <optgroup label={showCrew ? "Crew vendors" : "Vendor"}>
                  {personOptions.filter((o) => o.group === "crew").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              )}
              {showRoofTeam && personOptions.some((o) => o.group === "rs") && (
                <optgroup label="RS Pool members">
                  {personOptions.filter((o) => o.group === "rs").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Grand total banner */}
      <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between text-sm">
        <span className="text-slate-600 font-medium">
          {dateRange === "all" ? "All time" : DATE_RANGE_OPTIONS.find((o) => o.key === dateRange)?.label}
          {personId !== "all" && (
            <> · <span className="text-bardin-ink font-semibold">{personOptions.find((o) => o.value === personId)?.label ?? "—"}</span></>
          )}
        </span>
        <span className="font-semibold text-bardin-ink">
          Total paid: {fmtUSD(grandTotal, { short: true })}
        </span>
      </div>

      {/* Sales paid section */}
      {showSales && (
        <div>
          {category === "all" && (
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 px-1">
              Sales Pay (RM / TL / ASM) — {fmtUSD(salesTotal, { short: true })}
            </div>
          )}
          <GroupedTab
            groups={filteredSalesGroups}
            members={members}
            acting={acting}
            role={role}
            isSuperUser={isSuperUser}
            showPayButton={false}
            onMarkPaid={onMarkPaid}
            onMarkGroupPaid={onMarkGroupPaid}
            emptyMsg={
              paidGroups.length === 0
                ? "No paid commissions yet."
                : "No paid commissions match the current filters."
            }
            hideTotalBanner
          />
        </div>
      )}

      {/* Crew paid section */}
      {showCrew && (
        <div>
          {category === "all" && (
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 px-1 mt-4">
              Crew Pay (vendor payments) — {fmtUSD(crewTotal, { short: true })}
            </div>
          )}
          {crewErr ? (
            <div className="bg-red-50 text-red-700 rounded-xl p-3 text-sm">
              Couldn't load crew history — {crewErr}
            </div>
          ) : !crewSplit ? (
            <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
              Loading crew history…
            </div>
          ) : (
            <CrewPaidGroups rows={crewSplit.crew} kind="crew" />
          )}
        </div>
      )}

      {/* PM pay paid history — Ramiro's per-build fees + biweekly base. Same
          roof_crew_payments rows the crew history reads, split by isPmPayRow. */}
      {showPm && (
        <div>
          {category === "all" && (
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 px-1 mt-4">
              PM Pay — {fmtUSD(pmTotal, { short: true })}
            </div>
          )}
          {crewErr ? (
            <div className="bg-red-50 text-red-700 rounded-xl p-3 text-sm">
              Couldn't load PM pay history — {crewErr}
            </div>
          ) : !crewSplit ? (
            <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
              Loading PM pay history…
            </div>
          ) : (
            <CrewPaidGroups rows={crewSplit.pm} kind="pm" />
          )}
        </div>
      )}

      {/* RS Pool paid history */}
      {showRoofTeam && (
        <div>
          {category === "all" && (
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 px-1 mt-4">
              Roof Team Pay (RS pool) — {fmtUSD(rsPoolTotal, { short: true })}
            </div>
          )}
          <RSPoolPaidView
            allDeals={allDeals}
            members={members}
            stored={stored}
            payeeNames={payeeNames}
            personId={personId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            poolMembers={rsPoolMembers}
            poolErr={rsPoolErr}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RS Pool Paid View — groups paid RS-pool deals by member, showing each
// member's share. Also surfaces approved-but-unpaid deals with a Mark Paid
// button (Shannon/finance/admin only). Splits are computed live from the
// current rs_pool_members table; pool composition changes are rare enough
// that snapshotting at payment time isn't worth a separate table for v1.
// ---------------------------------------------------------------------------

function RSPoolPaidView({
  allDeals,
  members,
  stored,
  payeeNames,
  personId,
  dateFrom,
  dateTo,
  poolMembers,
  poolErr,
}: {
  allDeals: CommissionDeal[] | null;
  members: Record<string, TeamMember>;
  stored: StoredPayments;
  payeeNames: Record<string, string>;
  personId: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  poolMembers: RSPoolMember[] | null;
  poolErr: string | null;
}) {
  if (poolErr) {
    return <div className="bg-red-50 text-red-700 rounded-xl p-3 text-sm">Couldn't load RS pool — {poolErr}</div>;
  }
  if (!allDeals || !poolMembers) {
    return <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">Loading RS pool history…</div>;
  }

  // Filter pool deals: paid (rs_pool_paid=true) within the active date range,
  // and approved-but-unpaid (rs_pool_approved=true, rs_pool_paid=false) which
  // get the Mark Paid button.
  const paidDeals = allDeals.filter((d) => {
    if (!d.rs_pool_paid) return false;
    const ts = d.rs_pool_paid_at;
    if (!ts) return false;
    const t = new Date(ts);
    if (dateFrom && t < dateFrom) return false;
    if (dateTo && t > dateTo) return false;
    return true;
  });
  // Build per-member groups from paid deals. Sep 25, 2026: a pool paid through
  // the ledger renders its STORED per-member rows (what actually went out, even
  // to a member who has since left the pool); older pools keep the live math
  // over the current composition.
  type MemberLine = { deal: CommissionDeal; share: number; amount: number; stored: boolean };
  type MemberGroup = { memberId: string; name: string; total: number; lines: MemberLine[] };

  const memberGroupsMap = new Map<string, MemberGroup>();
  const addLine = (memberId: string, line: MemberLine) => {
    const g = memberGroupsMap.get(memberId) ?? {
      memberId,
      name: payeeName(memberId, members, payeeNames),
      total: 0,
      lines: [],
    };
    g.lines.push(line);
    g.total += line.amount;
    memberGroupsMap.set(memberId, g);
  };
  for (const d of paidDeals) {
    const storedLines = storedRsPoolLines(d, stored);
    if (storedLines) {
      for (const ln of storedLines) addLine(ln.memberId, { deal: d, share: ln.share, amount: ln.amount, stored: true });
      continue;
    }
    const rev = getRSDealRev(d);
    const poolTotal = rev * RS_POOL_RATE_LIVE;
    for (const pm of poolMembers) {
      const share = Number(pm.share ?? 0);
      if (share <= 0) continue;
      if (!members[pm.team_member_id]) continue;
      addLine(pm.team_member_id, { deal: d, share, amount: poolTotal * share, stored: false });
    }
  }

  // Person filter applies to RS members too (no "v:" prefix needed — they use raw team_member_id)
  let memberGroups = [...memberGroupsMap.values()].sort((a, b) => b.total - a.total);
  if (personId !== "all" && !personId.startsWith("v:")) {
    memberGroups = memberGroups.filter((g) => g.memberId === personId);
  } else if (personId.startsWith("v:")) {
    // Crew vendor selected — hide RS rows entirely
    memberGroups = [];
  }

  return (
    <div className="space-y-2">
      {memberGroups.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          {paidDeals.length === 0
            ? "No RS pool deals paid yet."
            : "No RS pool history matches the current filters."}
        </div>
      ) : (
        <RSMemberGroups groups={memberGroups} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RS Pool Approved View (Oct 6, 2026) — approved-but-unpaid pool deals now live
// on the Approved tab (they used to render under Paid as "awaiting payout",
// which read as approvals skipping straight to paid). Itemized two ways:
// per roof-team member (what each person is owed) and per deal (where Shannon
// pays — rs_pool_paid is a DEAL-level flag, so payout stays one deal at a time).
// ---------------------------------------------------------------------------

function RSPoolApprovedView({
  allDeals,
  members,
  acting,
  onMarkRSPoolPaid,
  canMarkPaid,
  bills,
}: {
  allDeals: CommissionDeal[] | null;
  members: Record<string, TeamMember>;
  acting: Set<string>;
  onMarkRSPoolPaid: (dealId: string) => void;
  canMarkPaid: boolean;
  /** Oct 8, 2026 — the Books bills behind the approved deals (rs_pool kind). */
  bills?: BillIndex;
}) {
  const [poolMembers, setPoolMembers] = useState<RSPoolMember[] | null>(null);
  const [poolErr, setPoolErr] = useState<string | null>(null);
  const loadPool = useCallback(async () => {
    const { data, error } = await supabase
      .from("rs_pool_members")
      .select("id, team_member_id, status, share")
      .eq("status", "active");
    if (error) { setPoolErr(error.message); return; }
    setPoolErr(null);
    setPoolMembers((data ?? []) as RSPoolMember[]);
  }, []);
  useEffect(() => { void loadPool(); }, [loadPool]);
  useRealtimeTable("rs_pool_members", () => { void loadPool(); });

  if (poolErr) {
    return <div className="bg-red-50 text-red-700 rounded-xl p-3 text-sm">Couldn't load RS pool — {poolErr}</div>;
  }
  if (!allDeals || !poolMembers) {
    return <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">Loading approved roof team pay…</div>;
  }

  const approved = allDeals.filter((d) => d.rs_pool_approved && !d.rs_pool_paid);
  if (approved.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
        No approved roof team pay waiting. New approvals land here until Shannon marks them paid.
      </div>
    );
  }

  const active = poolMembers.filter((pm) => Number(pm.share ?? 0) > 0 && members[pm.team_member_id]);
  type Line = { deal: CommissionDeal; share: number; amount: number };
  const groups = new Map<string, { memberId: string; name: string; total: number; lines: Line[] }>();
  let grand = 0;
  for (const d of approved) {
    const poolTotal = getRSDealRev(d) * RS_POOL_RATE_LIVE;
    grand += poolTotal;
    for (const pm of active) {
      const share = Number(pm.share ?? 0);
      const g = groups.get(pm.team_member_id) ?? {
        memberId: pm.team_member_id,
        name: members[pm.team_member_id]?.full_name ?? "—",
        total: 0,
        lines: [],
      };
      g.lines.push({ deal: d, share, amount: poolTotal * share });
      g.total += poolTotal * share;
      groups.set(pm.team_member_id, g);
    }
  }
  const memberGroups = [...groups.values()].sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Owed by roof team member
          </div>
          <div className="text-sm font-semibold text-bardin-ink">
            {fmtUSD(grand)} <span className="text-xs font-normal text-slate-500">· {approved.length} {approved.length === 1 ? "deal" : "deals"}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {memberGroups.map((g) => (
            <div key={g.memberId} className="rounded-lg bg-cyan-50 px-3 py-2">
              <div className="text-xs text-cyan-800 truncate">{g.name}</div>
              <div className="text-base font-semibold text-bardin-ink">{fmtUSD(g.total)}</div>
            </div>
          ))}
        </div>
      </div>

      <RSMemberGroups groups={memberGroups} awaiting />

      <div className="bg-white rounded-xl shadow p-3 border-l-4 border-amber-400">
        <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
          Pay by deal · each payout covers every member's share on that deal
        </div>
        <ul className="divide-y divide-slate-50">
          {approved.map((d) => {
            const poolTotal = getRSDealRev(d) * RS_POOL_RATE_LIVE;
            return (
              <li key={d.id} className="py-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <DealLink id={d.id} className="font-medium">{d.homeowner_name ?? "(no name)"}</DealLink>
                    <div className="text-xs text-slate-500 truncate">
                      {d.property_street ?? "—"}
                      {d.rs_pool_approved_at ? ` · approved ${new Date(d.rs_pool_approved_at).toLocaleDateString()}` : ""}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {active.map((pm) => `${members[pm.team_member_id]?.full_name?.split(" ")[0] ?? "—"} ${fmtUSD(poolTotal * Number(pm.share ?? 0))}`).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-bardin-ink">{fmtUSD(poolTotal)}</div>
                    {(() => {
                      // Oct 8, 2026 — the pool approved through rs_pool_approve
                      // is a Books bill per member: Shannon pays it in Books.
                      // Pools approved before that keep Mark Pool Paid.
                      const poolBills = bills
                        ? [...new Map(active.map((pm) => liveBillFor(bills, "deals", d.id, { memberId: pm.team_member_id, kind: "rs_pool" })).filter((b): b is BooksPayoutBill => !!b).map((b) => [b.id, b] as const)).values()]
                        : [];
                      if (poolBills.length === 0) return null;
                      const first = poolBills[0];
                      const allPaid = poolBills.every((b) => b.status === "paid");
                      const label = poolBills.length === 1
                        ? billChipText(first)
                        : allPaid
                          ? `Paid in Books — ${poolBills.length} bills (one per member)`
                          : `${poolBills.length} bills in Books (one per member) — ${billChipText(first)}`;
                      return (
                        <div
                          title={poolBills.map((b) => `${members[b.member_id ?? ""]?.full_name ?? "—"}: ${billChipText(b)}`).join("\n")}
                          className={"mt-1 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded max-w-[240px] truncate " + BILL_CHIP_CLASS[billChipTone(first)]}
                        >
                          {label}
                        </div>
                      );
                    })()}
                    {canMarkPaid && !(bills && active.some((pm) => liveBillFor(bills, "deals", d.id, { memberId: pm.team_member_id, kind: "rs_pool" }))) && (
                      <button
                        onClick={() => onMarkRSPoolPaid(d.id)}
                        disabled={acting.has(`rspay-${d.id}`)}
                        className="mt-1 px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {acting.has(`rspay-${d.id}`) ? "…" : "Mark Pool Paid"}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function RSMemberGroups({ groups, awaiting = false }: { awaiting?: boolean; groups: Array<{ memberId: string; name: string; total: number; lines: Array<{ deal: CommissionDeal; share: number; amount: number; stored?: boolean }> }> }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const isOpen = expanded.has(g.memberId);
        return (
          <div key={g.memberId} className="bg-white rounded-xl shadow overflow-hidden">
            <button
              onClick={() => toggle(g.memberId)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <div className="text-left min-w-0">
                  <div className="font-medium text-bardin-ink text-sm truncate">{g.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-cyan-50 text-cyan-700">
                      {awaiting ? "RS Pool · approved" : "RS Pool"}
                    </span>
                    <span className="text-xs text-slate-400">
                      · {g.lines.length} {g.lines.length === 1 ? "deal" : "deals"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(g.total, { short: true })}</div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100">
                {g.lines.map((line, idx) => (
                  <div
                    key={`${g.memberId}-${line.deal.id}-${idx}`}
                    className={
                      "px-6 py-2.5 flex items-center justify-between text-sm " +
                      (idx > 0 ? "border-t border-slate-50" : "")
                    }
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        <DealLink id={line.deal.id}>{line.deal.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {line.deal.property_street ?? "—"}
                        {line.deal.claim_number ? ` · #${line.deal.claim_number}` : ""}
                        {` · ${(line.share * 100).toFixed(1)}% share`}
                      </div>
                      <div className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                        <span>
                          {line.deal.rs_pool_paid_at ? `Paid ${new Date(line.deal.rs_pool_paid_at).toLocaleDateString()}` : ""}
                          {` · Rev: ${fmtUSD(getRSDealRev(line.deal), { short: true })}`}
                        </span>
                        {awaiting ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-700">approved · awaiting payout</span>
                        ) : (
                          <PaidProvenanceChip item={{ stored: line.stored === true, legacyPaid: line.stored !== true, paidAt: null }} />
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-semibold text-bardin-ink">{fmtUSD(line.amount)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Crew paid view — groups by vendor, expandable to per-job breakdown.
function CrewPaidGroups({ rows, kind = "crew" }: { rows: RoofCrewPaidRow[]; kind?: "crew" | "pm" }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isPm = kind === "pm";

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
        {isPm ? "No PM pay matches the current filters." : "No crew payments match the current filters."}
      </div>
    );
  }

  // Group by vendor
  const byVendor = new Map<string, { vendorName: string; rows: RoofCrewPaidRow[]; total: number }>();
  for (const r of rows) {
    const g = byVendor.get(r.vendor_id) ?? { vendorName: r.vendor_name, rows: [], total: 0 };
    g.rows.push(r);
    g.total += r.total;
    byVendor.set(r.vendor_id, g);
  }
  const groups = [...byVendor.entries()]
    .map(([id, g]) => ({ id, ...g }))
    .sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const isOpen = expanded.has(g.id);
        return (
          <div key={g.id} className="bg-white rounded-xl shadow overflow-hidden">
            <button
              onClick={() => toggle(g.id)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <div className="text-left min-w-0">
                  <div className="font-medium text-bardin-ink text-sm truncate">{g.vendorName}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className={"text-[10px] px-1.5 py-0.5 rounded font-medium " + (isPm ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700")}>
                      {isPm ? "PM pay" : "Crew"}
                    </span>
                    <span className="text-xs text-slate-400">
                      · {g.rows.length} {isPm ? (g.rows.length === 1 ? "payment" : "payments") : (g.rows.length === 1 ? "job" : "jobs")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(g.total, { short: true })}</div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100">
                {g.rows.map((r, idx) => (
                  <div
                    key={r.id}
                    className={
                      "px-6 py-2.5 flex items-center justify-between text-sm " +
                      (idx > 0 ? "border-t border-slate-50" : "")
                    }
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {r.deal_id ? (
                          <DealLink id={r.deal_id}>{r.homeowner_name ?? "(no name)"}</DealLink>
                        ) : (
                          <span>{r.additional_work_description ?? "Non-job payment"}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {r.deal_id ? (r.property_street ?? "—") : "Not job-specific"}
                        {r.claim_number ? ` · #${r.claim_number}` : ""}
                        {isPm ? ` · ${pmPayLabel(r)}` : (
                          <>
                            {r.squares > 0 ? ` · ${r.squares}sq @ ${fmtUSD(r.rate_per_sq)}/sq` : ""}
                            {r.flat_fee > 0 ? ` + ${fmtUSD(r.flat_fee)} flat` : ""}
                            {r.additional_work_amount > 0 ? ` + ${fmtUSD(r.additional_work_amount)} extra` : ""}
                          </>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {r.paid_at ? `Paid ${new Date(r.paid_at).toLocaleDateString()}` : ""}
                        {r.paid_method ? ` · ${r.paid_method}` : ""}
                        {r.paid_reference ? ` · ${r.paid_reference}` : ""}
                      </div>
                      {!isPm && r.additional_work_description && (
                        <div className="text-xs text-slate-500 italic mt-0.5">
                          "{r.additional_work_description}"
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-semibold text-bardin-ink">{fmtUSD(r.total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouped Tab (Approved / Paid) — grouped by person with expandable breakdown
// ---------------------------------------------------------------------------

function GroupedTab({
  groups,
  members: _members,
  acting,
  role,
  isSuperUser,
  showPayButton,
  onMarkPaid,
  onMarkGroupPaid,
  onReleaseHold,
  bills,
  onUnapprove,
  emptyMsg,
  hideTotalBanner = false,
}: {
  groups: PersonGroup[];
  members: Record<string, TeamMember>;
  acting: Set<string>;
  role: TeamRole | undefined;
  isSuperUser: boolean;
  showPayButton: boolean;
  onMarkPaid: (id: string) => void;
  onMarkGroupPaid: (ids: string[]) => void;
  onReleaseHold?: (id: string, release: boolean) => void;
  /** Oct 8, 2026 — the Books bills behind these deals (Approved tab only). */
  bills?: BillIndex;
  /** Oct 8, 2026 — approval tier: withdraw a deal's approval (and its bill). */
  onUnapprove?: (id: string) => void;
  emptyMsg: string;
  hideTotalBanner?: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  // Jul 15, 2026: "Pay All" now requires an inline confirm. commission_paid is a
  // DEAL-level flag, and a TL/ASM person group contains override lines on OTHER
  // reps' deals — so one un-confirmed click on an ASM group paid the entire
  // approved bucket (4 deals, 4 different RMs) in a single write.
  const [confirmPayKey, setConfirmPayKey] = useState<string | null>(null);

  function togglePerson(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleType(key: string) {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
        {emptyMsg}
      </div>
    );
  }

  const grandTotal = groups.reduce((s, g) => s + g.totalComm, 0);

  return (
    <div className="space-y-2">
      {/* Grand total banner — suppressed when PaidView wraps us with its own banner */}
      {!hideTotalBanner && (
        <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between text-sm">
          <span className="text-slate-600 font-medium">
            {groups.length} {groups.length === 1 ? "person" : "people"}
          </span>
          <span className="font-semibold text-bardin-ink">
            Total: {fmtUSD(grandTotal, { short: true })}
          </span>
        </div>
      )}

      {groups.map((g) => {
        const key = g.personId;
        const isOpen = expanded.has(key);

        // Summary chips: e.g. "RM · Trade upside · TL Override · ASM Override"
        const typeSummary = g.typeGroups.map((tg) => {
          if (tg.type === "rm" || tg.type === "rm_split") return "RM";
          if (tg.type === "trade_upside") return "Trade upside";
          if (tg.type === "tl_override") return "TL Override";
          if (tg.type === "trueup") return "True-up";
          return "ASM Override";
        });
        const uniqueSummary = [...new Set(typeSummary)];

        // Oct 8, 2026 — the live Books bill(s) carrying this person's lines on
        // these deals (one bill per approval batch). A group with a bill is
        // paid IN BOOKS: Pay All is hidden; a deal without a bill (approved
        // before Oct 8) keeps the per-deal Paid button.
        const groupBills: BooksPayoutBill[] = [];
        if (bills) {
          const seen = new Set<string>();
          for (const id of g.allDealIds) {
            const b = liveBillFor(bills, "deals", id, { memberId: g.personId, kind: "commission" });
            if (b && !seen.has(b.id)) { seen.add(b.id); groupBills.push(b); }
          }
        }
        const groupHasBill = groupBills.length > 0;

        return (
          <div key={key} className="bg-white rounded-xl shadow overflow-hidden">
            {/* Person header — always visible */}
            <button
              onClick={() => togglePerson(key)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <div className="text-left min-w-0">
                  <div className="font-medium text-bardin-ink text-sm truncate">{g.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {uniqueSummary.map((s) => (
                      <span key={s} className={
                        "text-[10px] px-1.5 py-0.5 rounded font-medium " +
                        (s === "RM" ? "bg-bardin-teal/10 text-bardin-teal"
                          : s === "Trade upside" ? "bg-teal-50 text-teal-700"
                          : s === "TL Override" ? "bg-amber-50 text-amber-700"
                          : s === "True-up" ? "bg-sky-50 text-sky-700"
                          : "bg-purple-50 text-purple-700")
                      }>
                        {s}
                      </span>
                    ))}
                    <span className="text-xs text-slate-400">
                      · {g.allDealIds.length} {g.allDealIds.length === 1 ? "deal" : "deals"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {groupBills.length > 0 && (
                  <div className="flex flex-col items-end gap-0.5 max-w-[260px]">
                    {groupBills.map((b) => (
                      <span
                        key={b.id}
                        title={billChipText(b)}
                        className={"inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded truncate max-w-full " + BILL_CHIP_CLASS[billChipTone(b)]}
                      >
                        {billChipText(b)}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-right">
                  <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(g.totalComm, { short: true })}</div>
                </div>
                {showPayButton && canMarkPaid(role, isSuperUser) && !groupHasBill && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmPayKey((prev) => (prev === key ? null : key));
                    }}
                    className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Pay All
                  </button>
                )}
              </div>
            </button>

            {/* Pay All inline confirm — spells out the deal-level blast radius */}
            {confirmPayKey === key && (() => {
              // An override line — or (Oct 29, 2026) a trade upside share Chad gave this person on a deal they
              // don't hold — means other reps' deals are in the group.
              const hasOverrideLines = g.typeGroups.some(
                (tg) => tg.type === "tl_override" || tg.type === "asm_override"
                  || (tg.type === "trade_upside" && tg.items.some((it) => it.deal.owner_id !== g.personId && it.deal.split_rm_id !== g.personId)),
              );
              // Jul 16, 2026 — hold-to-closure: split the group into payable vs
              // held deals so a bulk click can never pay a held one.
              const dealById = new Map<string, CommissionDeal>();
              for (const tg of g.typeGroups) for (const it of tg.items) dealById.set(it.deal.id, it.deal);
              const heldIds = g.allDealIds.filter((id) => { const dd = dealById.get(id); return dd ? payHoldReason(dd) !== null : false; });
              const payableIds = g.allDealIds.filter((id) => !heldIds.includes(id));
              return (
                <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                  <p className="text-xs font-semibold text-amber-800">
                    Mark {payableIds.length} {payableIds.length === 1 ? "deal" : "deals"} fully paid?
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    This marks <strong>every commission line</strong> on {payableIds.length === 1 ? "this deal" : "these deals"} as paid
                    — RM, trade upside, TL override, and ASM override for everyone on them, not just {g.name}&apos;s share.
                    {hasOverrideLines && (
                      <> This group includes override lines on other reps&apos; deals, so their commissions move to Paid too.</>
                    )}
                    {" "}Only confirm once the payments have actually gone out.
                  </p>
                  {heldIds.length > 0 && (
                    <p className="text-xs text-red-700 mt-1.5">
                      🔒 {heldIds.length} {heldIds.length === 1 ? "deal is" : "deals are"} held until the balance is collected in full
                      (credits ≥ $501) and will be <strong>skipped</strong>:{" "}
                      {heldIds.map((id) => dealById.get(id)?.homeowner_name ?? id).join(", ")}.
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => {
                        setConfirmPayKey(null);
                        onMarkGroupPaid(payableIds);
                      }}
                      disabled={payableIds.length === 0}
                      className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ✓ Confirm — pay {payableIds.length} {payableIds.length === 1 ? "deal" : "deals"}
                    </button>
                    <button
                      onClick={() => setConfirmPayKey(null)}
                      className="px-3 py-1 text-xs font-medium rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Expanded: commission type sub-groups */}
            {isOpen && (
              <div className="border-t border-slate-100">
                {g.typeGroups.map((tg) => {
                  const typeKey = `${g.personId}-${tg.type}`;
                  const typeOpen = expandedTypes.has(typeKey);

                  return (
                    <div key={typeKey}>
                      {/* Type sub-header */}
                      <button
                        onClick={() => toggleType(typeKey)}
                        className="w-full px-4 py-2.5 flex items-center justify-between bg-slate-50/60 hover:bg-slate-100/60 transition border-b border-slate-100"
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className={`w-3 h-3 text-slate-400 transition-transform ${typeOpen ? "rotate-90" : ""}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <span className={
                            "text-xs px-1.5 py-0.5 rounded font-medium " + COMM_TYPE_COLORS[tg.type]
                          }>
                            {tg.label}
                          </span>
                          <span className="text-xs text-slate-400">
                            {tg.items.length} {tg.items.length === 1 ? "deal" : "deals"}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-bardin-ink">
                          {fmtUSD(tg.total, { short: true })}
                        </span>
                      </button>

                      {/* Deal rows within this type */}
                      {typeOpen && tg.items.map((item, idx) => (
                        <div
                          key={`${item.deal.id}-${tg.type}-${idx}`}
                          className={
                            "px-6 py-2.5 flex items-center justify-between text-sm " +
                            (idx > 0 ? "border-t border-slate-50" : "")
                          }
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">
                              <DealLink id={item.deal.id}>{item.deal.homeowner_name ?? "(no name)"}</DealLink>
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {item.deal.property_street ?? "—"}
                              {item.deal.claim_number ? ` · #${item.deal.claim_number}` : ""}
                              {item.deal.total_squares ? ` · ${item.deal.total_squares}sq` : ""}
                            </div>
                            <div className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                              <span>
                                Rev: {fmtUSD(getDealRev(item.deal), { short: true })}
                                {item.deal.build_completed_date ? ` · Built ${item.deal.build_completed_date.slice(0, 10)}` : ""}
                              </span>
                              <PaidProvenanceChip item={item} />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <div className="font-semibold text-bardin-ink">{fmtUSD(item.amount)}</div>
                            </div>
                            {showPayButton && (canMarkPaid(role, isSuperUser) || canApprove(role, isSuperUser)) && (() => {
                              // Jul 16, 2026 — hold-to-closure: held deals show a
                              // lock instead of the Paid button. Oct 6, 2026 —
                              // Chad's tier gets "Release" next to the lock.
                              const hold = payHoldReason(item.deal);
                              const canRelease = !!onReleaseHold && canApprove(role, isSuperUser);
                              const holdBusy = acting.has(`hold-${item.deal.id}`);
                              if (hold) {
                                return (
                                  <span className="flex items-center gap-1.5">
                                    <span
                                      title={hold}
                                      className="px-2 py-1 text-xs font-medium rounded bg-red-50 text-red-700 border border-red-200 cursor-help"
                                    >
                                      🔒 Held
                                    </span>
                                    {canRelease && (
                                      <button
                                        onClick={() => onReleaseHold!(item.deal.id, true)}
                                        disabled={holdBusy}
                                        title="Release the hold so Shannon can pay this commission now"
                                        className="px-2 py-1 text-xs font-medium rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                      >
                                        {holdBusy ? "…" : "🔓 Release"}
                                      </button>
                                    )}
                                  </span>
                                );
                              }
                              // Oct 8, 2026 — a Books bill carries this line:
                              // Shannon pays it in Books, so no Paid button.
                              const rowBill = bills ? liveBillFor(bills, "deals", item.deal.id, { memberId: g.personId, kind: "commission" }) : null;
                              const released = item.deal.commission_hold_released_at;
                              const releasedChip = released ? (
                                <button
                                  onClick={canRelease ? () => onReleaseHold!(item.deal.id, false) : undefined}
                                  disabled={!canRelease || holdBusy}
                                  title={`Hold released ${released.slice(0, 10)}${item.deal.commission_hold_release_reason ? ` — ${item.deal.commission_hold_release_reason}` : ""}${canRelease ? " (tap to put the hold back)" : ""}`}
                                  className="px-2 py-1 text-xs font-medium rounded border border-amber-200 bg-amber-50 text-amber-700 disabled:cursor-help"
                                >
                                  🔓 Released
                                </button>
                              ) : null;
                              if (rowBill) {
                                return (
                                  <span className="flex items-center gap-1.5">
                                    {releasedChip}
                                    <span
                                      title={billChipText(rowBill)}
                                      className={"px-2 py-1 text-[10px] font-semibold rounded max-w-[220px] truncate " + BILL_CHIP_CLASS[billChipTone(rowBill)]}
                                    >
                                      {billChipText(rowBill)}
                                    </span>
                                  </span>
                                );
                              }
                              if (!canMarkPaid(role, isSuperUser)) return releasedChip;
                              return (
                                <span className="flex items-center gap-1.5">
                                {releasedChip}
                                <button
                                  onClick={() => onMarkPaid(item.deal.id)}
                                  disabled={acting.has(`pay-${item.deal.id}`)}
                                  className="px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  {acting.has(`pay-${item.deal.id}`) ? "…" : "Paid"}
                                </button>
                                </span>
                              );
                            })()}
                            {showPayButton && onUnapprove && canApprove(role, isSuperUser) && (
                              <button
                                onClick={() => onUnapprove(item.deal.id)}
                                disabled={acting.has(`unapprove-${item.deal.id}`)}
                                title="Take the approval back: the Books bill is withdrawn (refused once Books has paid it) and the deal returns to Pending"
                                className="px-2 py-1 text-xs font-medium rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                              >
                                {acting.has(`unapprove-${item.deal.id}`) ? "…" : "Un-approve…"}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------


const COMM_ROLE_CHIP: Record<CommType, { label: string; cls: string }> = {
  rm: { label: "RM", cls: "bg-bardin-teal/10 text-bardin-teal" },
  rm_split: { label: "RM", cls: "bg-bardin-teal/10 text-bardin-teal" },
  trade_upside: { label: "Upside", cls: "bg-teal-50 text-teal-700" },
  tl_override: { label: "TL", cls: "bg-amber-50 text-amber-700" },
  asm_override: { label: "ASM", cls: "bg-purple-50 text-purple-700" },
  trueup: { label: "True-up", cls: "bg-sky-50 text-sky-700" },
};

/** Sep 30, 2026 — the pending card's itemized commission list: one row per
 *  person per line (Alex Poma · RM 12% of his half · $1,376.75). */
function CommLinesList({ lines, rev }: { lines: DealCommLine[]; rev: number }) {
  if (lines.length === 0) {
    return <div className="mt-3 text-xs text-slate-500">No commission lines — no RM on this deal.</div>;
  }
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const pct = (r: number) => `${+(r * 100).toFixed(2)}%`;
  function basis(l: DealCommLine): string {
    if (l.overridden) return "set by hand";
    if (l.type === "trade_upside") {
      const pool = getTradeUpside(l.deal);
      const share = pool > 0 ? l.rev / pool : 1;
      return share > 0.999 ? "trade upside pool" : share > 0.499 && share < 0.501 ? "½ of the trade upside pool" : `${Math.round(share * 100)}% of the trade upside pool`;
    }
    if (l.rate <= 0) return "retail pool";
    const share = rev > 0 ? l.rev / rev : 1;
    const of = share > 0.999 ? "revenue" : share > 0.499 && share < 0.501 ? "½ revenue" : `${Math.round(share * 100)}% of revenue`;
    return `${pct(l.rate)} × ${fmtUSD(l.rev)} (${of})`;
  }
  return (
    <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 divide-y divide-slate-100 text-xs">
      {lines.map((l, i) => {
        const chip = COMM_ROLE_CHIP[l.type];
        const fees = l.fees.reduce((s, f) => s + f.amount, 0);
        return (
          <div key={`${l.personId}-${l.type}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${chip.cls}`}>{chip.label}</span>
              <span className="font-medium text-bardin-ink">{l.name}</span>
              <span className={l.overridden ? "text-amber-700" : "text-slate-500"}>{basis(l)}</span>
              {fees > 0.005 && (
                <span className="text-rose-600" title={l.fees.map((f) => `${f.label} −${fmtUSD(f.amount)}`).join(" · ")}>
                  − {fmtUSD(fees)} {l.fees.length === 1 ? l.fees[0].label.toLowerCase() : "deductions"}
                </span>
              )}
            </div>
            <div className="font-semibold text-bardin-ink flex-shrink-0">{fmtUSD(l.amount)}</div>
          </div>
        );
      })}
      <div className="flex items-center justify-between px-3 py-1.5 text-slate-500">
        <span>Total commission</span>
        <span className="font-semibold text-slate-700">{fmtUSD(total)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DealTradesStrip — Oct 29, 2026
//
// The deal's priced trades on the pending card: work · payer · floor · customer
// · what it means for the money (upside to the pool, or short of the floor and
// Chad's answer). Display only: the money itself is deals.trade_upside and
// deals.trade_margin_loss, which every viewer reads. The rows come through
// trade_item_prices (RLS trade_can_see_prices: a viewer kept off prices gets no
// rows and sees no strip). Rule 4 (Greg): the floor shows, never the sub's price.
// ---------------------------------------------------------------------------

export interface BoardTradeRow {
  trade_id: string;
  deal_id: string;
  work: TradeWork;
  vendor: string | null;
  billed_to: BilledTo | null;
  status: string;
  created_at: string;
  /** trade_item_prices.customer_label (D5: "Gutters: detach and reset, 199 ft"), when the row has one. */
  label: string | null;
  /** The sub's price — never printed here; null while the quote is pending. */
  agreed_total: number | null;
  customer_total: number | null;
  margin_target_pct: number | null;
  margin_review: MarginReview;
  /** The homeowner's add-on (Oct 29, migration 20261029g): waiting on it, a homeowner-billed trade is off the deal's
   *  money (trade_sync_deal) until signed. */
  addendum_status?: string | null;
}

/** The floor and what the customer's price means against it — the same math as trade_sync_deal: floor =
 *  priceAtMargin(sub cost, target); a quote-pending trade has no floor yet and no upside until it is priced
 *  (D4); only a homeowner-billed trade feeds the pool (D1). */
export function boardTradeMoney(
  r: Pick<BoardTradeRow, "agreed_total" | "customer_total" | "margin_target_pct" | "billed_to" | "margin_review">,
): { floor: number | null; customer: number | null; above: number; short: number; note: string; tone: "pool" | "short" | "flat" } {
  const floor = r.agreed_total !== null && r.agreed_total > 0 ? priceAtMargin(r.agreed_total, r.margin_target_pct ?? 30) : null;
  const customer = r.customer_total;
  if (floor === null) return { floor, customer, above: 0, short: 0, note: "waiting on the sub's price — no floor yet", tone: "flat" };
  const charged = customer === null ? 0 : Math.max(0, customer);
  const above = round2(Math.max(0, charged - floor));
  const short = round2(Math.max(0, floor - charged));
  if (above > 0) {
    if (r.billed_to === "homeowner") return { floor, customer, above, short, note: `+${fmtMoney2(above)} upside → the pool`, tone: "pool" };
    if (r.billed_to === "insurance") return { floor, customer, above, short, note: `+${fmtMoney2(above)} above the floor · in the insurance scope, no pool`, tone: "flat" };
    return { floor, customer, above, short, note: `+${fmtMoney2(above)} above the floor`, tone: "flat" };
  }
  if (short > 0) {
    const answer = r.margin_review === "deducted" ? " · deducted by Chad → the bucket"
      : r.margin_review === "allowed" ? " · allowed by Chad, no recourse"
      : r.margin_review === "needs_review" ? " · needs Chad" : "";
    return { floor, customer, above, short, note: `−${fmtMoney2(short)} short of the floor${customer === null ? " (nothing charged)" : ""}${answer}`, tone: "short" };
  }
  return { floor, customer, above, short, note: "at the floor", tone: "flat" };
}

/** The approval gate for a deal whose homeowner-billed trade still waits on the homeowner's add-on signature (Oct 30,
 *  2026): null when none does. Chad approves once it's signed, or once the trade is cancelled. */
export function openAddendumBlock(rows: Array<Pick<BoardTradeRow, "billed_to" | "addendum_status" | "status" | "label" | "work">>): string | null {
  const open = rows.filter((r) => r.billed_to === "homeowner" && LIVE_TRADE_STATUSES.has(r.status) && isAddendumOpen(r.addendum_status));
  if (open.length === 0) return null;
  const what = open.map((r) => r.label ?? WORK_LABELS[r.work] ?? r.work).join(", ");
  return `The homeowner hasn't signed the add-on for ${what} yet. Approve once it's signed (its price and upside then reach the commission), or cancel the trade.`;
}

// The trades trade_sync_deal counts (D6: status pending / scheduled / complete; in_progress is unused).
const LIVE_TRADE_STATUSES = new Set(["pending", "scheduled", "complete"]);

/** The priced, live trades of these deals, by deal, oldest first — the trades.ts join (trade_items!inner). */
async function fetchBoardTradeRows(dealIds: string[]): Promise<Record<string, BoardTradeRow[]>> {
  const out: Record<string, BoardTradeRow[]> = {};
  for (let i = 0; i < dealIds.length; i += 150) {
    const { data, error } = await supabase
      .from("trade_item_prices")
      // "*" so the strip works with or without the customer_label column (D5) on the row.
      .select("*,trade:trade_items!inner(id,deal_id,trade_type,vendor,billed_to,status,created_at)")
      .in("trade.deal_id", dealIds.slice(i, i + 150));
    if (error) throw error;
    for (const raw of (data ?? []) as Array<Record<string, unknown>>) {
      const t = raw.trade as Record<string, unknown> | null;
      if (!t || typeof t.deal_id !== "string" || !LIVE_TRADE_STATUSES.has(String(t.status))) continue;
      const row: BoardTradeRow = {
        trade_id: String(raw.trade_id),
        deal_id: t.deal_id,
        work: String(t.trade_type) as TradeWork,
        vendor: t.vendor == null ? null : String(t.vendor),
        billed_to: t.billed_to == null ? null : (String(t.billed_to) as BilledTo),
        status: String(t.status),
        created_at: String(t.created_at ?? ""),
        label: typeof raw.customer_label === "string" && raw.customer_label.trim() ? raw.customer_label.trim() : null,
        agreed_total: numOrNull(raw.agreed_total),
        customer_total: numOrNull(raw.customer_total),
        margin_target_pct: numOrNull(raw.margin_target_pct),
        margin_review: (raw.margin_review == null ? "none" : String(raw.margin_review)) as MarginReview,
        addendum_status: typeof raw.addendum_status === "string" ? raw.addendum_status : null,
      };
      (out[row.deal_id] ?? (out[row.deal_id] = [])).push(row);
    }
  }
  for (const rows of Object.values(out)) rows.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return out;
}

function DealTradesStrip({ rows }: { rows: BoardTradeRow[] }) {
  if (rows.length === 0) return null;
  const toneCls = { pool: "text-teal-700 font-semibold", short: "text-rose-700 font-semibold", flat: "text-slate-500" } as const;
  return (
    <div className="mt-2 rounded-lg border border-slate-100 bg-white divide-y divide-slate-100 text-[11px]">
      <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Trades on this job · the 30% floor and what the customer pays
      </div>
      {rows.map((r) => {
        const m = boardTradeMoney(r);
        return (
          <div key={r.trade_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5">
            <span className="font-medium text-bardin-ink">{r.label ?? WORK_LABELS[r.work] ?? r.work}</span>
            {r.vendor && <span className="text-slate-500">· {r.vendor}</span>}
            <span className="text-slate-500">· {r.billed_to ? `paid by ${(BILLED_TO_LABELS[r.billed_to] ?? r.billed_to).toLowerCase()}` : "payer not set"}</span>
            <span className="text-slate-500">· floor {m.floor !== null ? fmtMoney2(m.floor) : "—"}</span>
            <span className="text-slate-500">· customer {m.customer !== null ? fmtMoney2(m.customer) : "not set"}</span>
            <span className={toneCls[m.tone]}>· {m.note}</span>
            {r.billed_to === "homeowner" && isAddendumOpen(r.addendum_status) && (
              <span className="text-amber-700 font-semibold">· the homeowner's add-on isn't signed yet: off the deal's money until it is</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draw paydown (Jun 17, 2026)
//
// Per-person NET commission on a deal (mirrors groupByPerson: gross − non-collect
// deduction − canvasser carve-out), used to cap how much of this deal's
// commission can be applied against the person's outstanding draws.
// ---------------------------------------------------------------------------

function dealEarners(
  d: CommissionDeal,
  members: Record<string, TeamMember>,
  canvasserByDeal: Record<string, CanvasserRef>,
): Array<{ memberId: string; name: string; slot: string; net: number }> {
  const slotLabel: Record<CommType, string> = { rm: "RM", rm_split: "RM split", trade_upside: "Trade upside", tl_override: "TL", asm_override: "ASM", trueup: "True-up" };
  const raw = dealCommLines(d, members, canvasserByDeal).map((l) => ({ memberId: l.personId, name: l.name, slot: slotLabel[l.type], net: l.amount }));
  // Merge if the same person earns in multiple slots on the deal.
  const merged: Record<string, { memberId: string; name: string; slot: string; net: number }> = {};
  for (const e of raw) {
    if (merged[e.memberId]) { merged[e.memberId].net += e.net; merged[e.memberId].slot += ` + ${e.slot}`; }
    else merged[e.memberId] = { ...e };
  }
  return Object.values(merged);
}

function DrawPaydownPanel({
  deal,
  members,
  drawBalances,
  canvasserByDeal,
  canAct,
  onApply,
}: {
  deal: CommissionDeal;
  members: Record<string, TeamMember>;
  drawBalances: Record<string, number>;
  canvasserByDeal: Record<string, CanvasserRef>;
  canAct: boolean;
  onApply: (memberId: string, amount: number, dealId: string) => Promise<void> | void;
}) {
  const earners = dealEarners(deal, members, canvasserByDeal).filter(
    (e) => (drawBalances[e.memberId] ?? 0) > 0.01,
  );
  if (earners.length === 0) return null;
  return (
    <div className="mt-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded text-xs">
      <div className="font-semibold text-purple-900 mb-1">💵 Outstanding draws — apply this deal's commission</div>
      <div className="space-y-1.5">
        {earners.map((e) => (
          <DrawPaydownRow
            key={e.memberId}
            earner={e}
            balance={drawBalances[e.memberId] ?? 0}
            dealId={deal.id}
            canAct={canAct}
            onApply={onApply}
          />
        ))}
      </div>
    </div>
  );
}

function DrawPaydownRow({
  earner,
  balance,
  dealId,
  canAct,
  onApply,
}: {
  earner: { memberId: string; name: string; slot: string; net: number };
  balance: number;
  dealId: string;
  canAct: boolean;
  onApply: (memberId: string, amount: number, dealId: string) => Promise<void> | void;
}) {
  const max = Math.min(balance, earner.net);
  const [amt, setAmt] = useState(max > 0 ? max.toFixed(2) : "0.00");
  const [busy, setBusy] = useState(false);
  const n = Math.max(0, Number(amt) || 0);
  const valid = n > 0 && n <= max + 0.005;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-purple-900 font-medium">{earner.name}</span>
      <span className="text-purple-500">({earner.slot})</span>
      <span className="text-purple-700">draw {fmtUSD(balance)} · nets {fmtUSD(earner.net)} here</span>
      {canAct && earner.net > 0.005 ? (
        <>
          <span className="text-purple-800">$</span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={amt}
            disabled={busy}
            onChange={(e) => setAmt(e.target.value)}
            className="w-24 text-right border border-purple-300 rounded px-2 py-1 bg-white"
          />
          <button
            disabled={busy || !valid}
            onClick={async () => { setBusy(true); await onApply(earner.memberId, n, dealId); setBusy(false); }}
            className="px-2 py-1 text-[11px] font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {busy ? "Applying…" : "Apply to draw"}
          </button>
          <span className="text-purple-400">max {fmtUSD(max)}</span>
        </>
      ) : earner.net <= 0.005 ? (
        <span className="text-purple-400 italic">no commission on this deal to apply</span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewStructureBanner — Jul 16, 2026
//
// Pending-card sub-component for deals under the new commission structure
// (install agreement signed on/after Jul 20, 2026). Explains the per-deal
// tier each RM half pays at, flags the ≥$501-credit pay hold, and gives Chad
// the 5★-review toggle that unlocks the review's point.
// ---------------------------------------------------------------------------

function NewStructureBanner({
  deal,
  members,
  canVerify,
  busy,
  onToggleFiveStar,
}: {
  deal: CommissionDeal;
  members: Record<string, TeamMember>;
  canVerify: boolean;
  busy: boolean;
  onToggleFiveStar: (dealId: string, next: boolean) => void;
}) {
  if (!isNewCommStructure(deal)) return null;
  const owner = deal.owner_id ? members[deal.owner_id] : undefined;
  const split = deal.split_rm_id ? members[deal.split_rm_id] : undefined;
  const ownerInfo = dealRateInfo(deal, owner);
  const splitInfo = split ? dealRateInfo(deal, split) : null;
  const verified = deal.five_star_review_verified === true;
  const credits = ownerInfo.credits;
  const pct = (r: number) => `${Math.round(r * 100)}%`;

  const pts = (info: DealRateInfo) => Math.round((info.fullRate - info.preReviewRate) * 100);
  function rateLine(name: string, info: DealRateInfo): string {
    if (info.heldToClosure) return `${name}: ${pct(info.rate)}`;
    if (info.achievedRate) return `${name}: ${pct(info.rate)} (achieved rate — flat)`;
    if (info.reviewPaysAsSpiff) {
      return info.reviewPending
        ? `${name}: ${pct(info.rate)} · a verified 5★ review adds ${pts(info)} pt as a 5-Star Review SPIFF`
        : `${name}: ${pct(info.rate)} · the 5★ point (+${pts(info)} pt) pays as a 5-Star Review SPIFF`;
    }
    if (info.reviewPending) return `${name}: ${pct(info.rate)} now → ${pct(info.fullRate)} once the 5★ review is verified`;
    return `${name}: ${pct(info.rate)}`;
  }
  const anySpiff = ownerInfo.reviewPaysAsSpiff || !!splitInfo?.reviewPaysAsSpiff;

  return (
    <div className={
      "mt-2 text-xs rounded-lg px-3 py-2 " +
      (ownerInfo.heldToClosure ? "bg-red-50 text-red-800 border border-red-200" : "bg-sky-50 text-sky-900 border border-sky-200")
    }>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-semibold">
          📐 New commission structure <span className="font-normal opacity-70">(agreement signed on/after Jul 20)</span>
        </span>
        {canVerify && !ownerInfo.heldToClosure && (
          <button
            onClick={() => onToggleFiveStar(deal.id, !verified)}
            disabled={busy}
            title={verified ? "Remove the 5★ verification" : "Confirm you checked Google and the homeowner left a 5★ review"}
            className={
              "px-2 py-0.5 text-[11px] font-semibold rounded border transition-colors disabled:opacity-50 " +
              (verified
                ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                : "bg-white border-sky-300 text-sky-700 hover:bg-sky-100")
            }
          >
            {busy ? "…" : verified ? "★ 5★ review verified" : "☆ Mark 5★ review verified"}
          </button>
        )}
      </div>
      <div className="mt-1 space-y-0.5">
        <div>
          Credits (misc + non-collect): <span className="font-semibold">{fmtUSD(credits)}</span>
          {credits > NEW_STRUCTURE_CREDIT_LIMIT ? " — over $500" : credits > 0 ? " — ≤ $500" : " — full insurance scope"}
        </div>
        <div>{rateLine(owner?.full_name ?? "RM", ownerInfo)}{splitInfo ? ` · ${rateLine(split?.full_name ?? "Split RM", splitInfo)}` : ""}</div>
        {ownerInfo.heldToClosure && (
          <div className="font-semibold">
            🔒 Approval is allowed, but payment is HELD until the balance is collected in full
            {deal.post_build_balance_remaining !== null && deal.post_build_balance_remaining !== undefined
              ? ` (${fmtUSD(Number(deal.post_build_balance_remaining))} outstanding).`
              : " (final invoice not set yet)."}
          </div>
        )}
        {anySpiff && (ownerInfo.reviewPending ? (
          <div className="opacity-80">
            No verified 5★ yet. When the review is verified, its commission point pays as a 5-Star Review SPIFF with the
            weekly SPIFFs (the Tuesday after) — the line here stays at the rate shown.
          </div>
        ) : (
          <div className="opacity-80">
            5★ verified {deal.five_star_review_verified_at ? deal.five_star_review_verified_at.slice(0, 10) : ""} — the point
            pays as a 5-Star Review SPIFF with the weekly SPIFFs, not on this line.
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewTrueUpPanel — Jul 16, 2026
//
// Lists deals commission-PAID at 10% before their 5★ review was verified:
//  · "owed" — 5★ verified after payment: Shannon pays the 1–2pt delta on the
//    next payout run and marks it here (writes five_star_trueup_paid_at).
// Oct 19, 2026: only reviews verified before the 5-Star Review SPIFF started —
// a later review's point goes out with the weekly SPIFFs instead.
// Renders nothing until the new structure produces its first case.
// ---------------------------------------------------------------------------

function ReviewTrueUpPanel({
  deals,
  members,
  acting,
  canPay,
  onMarkTrueUpPaid,
  onMarkCatchupPaid,
}: {
  deals: CommissionDeal[];
  members: Record<string, TeamMember>;
  acting: Set<string>;
  canPay: boolean;
  onMarkTrueUpPaid: (dealId: string, amount: number) => void;
  onMarkCatchupPaid: (dealId: string) => void;
}) {
  // Oct 6, 2026 — the "awaiting review" worklist was removed: 5★ reviews are
  // marked on the deal Overview now (FiveStarReviewCard), so this panel only
  // shows money actually owed.
  const owed = reviewTrueUps(deals, members).filter((r) => r.status === "owed");
  const catchups = rateCatchups(deals, members);
  if (owed.length === 0 && catchups.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-bardin-ink">⭐ Review true-ups</h2>
        <p className="text-xs text-slate-500">
          Deals paid below their full rate — the difference goes out on the next payout. Blue = the one-time 1-pt catch-up from the Sep 30 change (full-scope deals now pay 11% before the review).
        </p>
      </div>
      <div className="space-y-2">
        {catchups.map(({ deal, lines }) => {
          const total = lines.reduce((s, l) => s + l.delta, 0);
          return (
            <div key={`c-${deal.id}`} className="bg-white rounded-xl shadow p-4 border-l-4 border-sky-500">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <DealLink id={deal.id} className="font-medium text-sm">
                    {deal.homeowner_name ?? "(no name)"}
                  </DealLink>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {deal.property_street ?? "—"} · full insurance scope, paid at 10% on {deal.commission_paid_at?.slice(0, 10)} —
                    the rate is now 11% before the review
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {lines.map((l) => `${l.name}: +${fmtUSD(l.delta)}`).join(" · ")}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-sky-700">+{fmtUSD(total)}</div>
                    <div className="text-[11px] text-slate-500">1-pt rate catch-up</div>
                  </div>
                  {canPay && (
                    <button
                      onClick={() => onMarkCatchupPaid(deal.id)}
                      disabled={acting.has(`catchup-${deal.id}`)}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                    >
                      {acting.has(`catchup-${deal.id}`) ? "…" : "Catch-up paid"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {owed.map(({ deal, lines }) => {
          const total = lines.reduce((s, l) => s + l.delta, 0);
          return (
            <div key={deal.id} className="bg-white rounded-xl shadow p-4 border-l-4 border-emerald-500">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <DealLink id={deal.id} className="font-medium text-sm">
                    {deal.homeowner_name ?? "(no name)"}
                  </DealLink>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {deal.property_street ?? "—"} · 5★ verified{" "}
                    {deal.five_star_review_verified_at ? deal.five_star_review_verified_at.slice(0, 10) : ""} — after commission was paid
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {lines.map((l) => `${l.name}: +${fmtUSD(l.delta)}`).join(" · ")}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-emerald-700">+{fmtUSD(total)}</div>
                    <div className="text-[11px] text-slate-500">true-up owed</div>
                  </div>
                  {canPay && (
                    <button
                      onClick={() => onMarkTrueUpPaid(deal.id, total)}
                      disabled={acting.has(`trueup-${deal.id}`)}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {acting.has(`trueup-${deal.id}`) ? "…" : "True-up paid"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NonCollectBanner — May 15, 2026; 3-way dollar editor Jun 17, 2026; the
// blended bucket Oct 29, 2026
//
// Pending-card sub-component. Renders the deal's non-collect bucket (non-collect
// revenue + the trade shortfalls Chad deducted — getNonCollectBucket) when
// something is over the first $500: the overage and the per-role deduction
// breakdown (RM / TL / ASM), plus an inline editor where Chad-tier users enter
// explicit DOLLAR amounts per role. The amounts must total the overage. Roles
// not on the deal are hidden and contribute $0. When no explicit amounts are
// saved, the display falls back to the legacy RM/TL percentage split (ASM = $0).
// A shortfall within the $500 shows a quiet note (nothing deducted); a deal
// whose non-collect is grandfathered says so. Nothing renders for a small
// non-collect alone, as before.
// ---------------------------------------------------------------------------

function NonCollectBanner({
  deal,
  members,
  canEdit,
  onSplitSaved,
}: {
  deal: CommissionDeal;
  members: Record<string, TeamMember>;
  canEdit: boolean;
  onSplitSaved: () => void;
}) {
  // Current applied breakdown (explicit amounts if set, else legacy split).
  const bd = getNonCollectDeductionBreakdown(deal, members);
  const bucket = getNonCollectBucket(deal);
  const overage = bd.overage;
  // Retail (Oct 29, 2026): a trade shortfall is the only pool and it all comes off the RM (the TL / ASM lines are
  // flat amounts Chad set by hand), so there is nothing to split.
  const retail = isRetailDeal(deal);
  // TL resolves per RM half (Jul 21, 2026) — first TL shown by name; the gross
  // cap below sums all TL bases so the editor cap matches the ledger total.
  const tlBases = dealTlBases(deal, members);
  const tlIds = Object.keys(tlBases);
  const hasTl = !retail && tlIds.length > 0;
  const tlName = hasTl ? tlIds.map((id) => members[id]?.full_name ?? "TL").join(" + ") : null;
  const hasAsm = !retail && dealHasAsm(deal, members);
  const asmId = dealAsmId(deal, members); // owner's ASM, split-RM fallback (Jul 21, 2026)
  const asmName = asmId ? members[asmId]?.full_name ?? "ASM" : null;
  const tradeLoss = bucket.tradeLoss;
  // Non-collect over the allowance that does NOT count (non_collect_deduct_active off): say so, as before.
  const grandfathered = !retail && !bucket.ncCounts && bucket.nc > NON_COLLECT_ALLOWANCE;
  // Per-role gross commission, shown as a cap hint so a role isn't over-deducted
  // into the negative. Owner's per-deal rate (Jul 16, 2026).
  // Gross caps straight from the payout engine (per-person rates + Chad's edits).
  const engineLines = dealCommLines(deal, members);
  const sumGross = (k: CommLineKind) => engineLines.filter((l) => commLineKind(l.type) === k).reduce((s, l) => s + l.gross, 0);
  const rmGross = sumGross("rm");
  const tlGross = sumGross("tl");
  const asmGross = sumGross("asm");

  const [editing, setEditing] = useState(false);
  const [rmStr, setRmStr] = useState(bd.rm.toFixed(2));
  const [tlStr, setTlStr] = useState(bd.tl.toFixed(2));
  const [asmStr, setAsmStr] = useState(bd.asm.toFixed(2));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const rmN = Math.max(0, Number(rmStr) || 0);
  const tlN = hasTl ? Math.max(0, Number(tlStr) || 0) : 0;
  const asmN = hasAsm ? Math.max(0, Number(asmStr) || 0) : 0;
  const allocated = rmN + tlN + asmN;
  const remaining = overage - allocated;
  const balanced = Math.abs(remaining) < 0.005;

  function openEditor() {
    setRmStr(bd.rm.toFixed(2));
    setTlStr(bd.tl.toFixed(2));
    setAsmStr(bd.asm.toFixed(2));
    setErr(null);
    setEditing(true);
  }

  function allToRm() {
    setRmStr(overage.toFixed(2));
    setTlStr("0.00");
    setAsmStr("0.00");
  }

  async function save() {
    if (!balanced) {
      setErr(`RM + TL + ASM must total ${fmtUSD(overage)} (currently ${fmtUSD(allocated)}).`);
      return;
    }
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from("deals")
      .update({
        non_collect_rm_amount: rmN,
        non_collect_tl_amount: hasTl ? tlN : 0,
        non_collect_asm_amount: hasAsm ? asmN : 0,
      })
      .eq("id", deal.id);
    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setEditing(false);
    onSplitSaved();
  }

  if (overage <= 0) {
    // Nothing comes off. A small non-collect alone stays quiet (as before); a shortfall Chad deducted says it
    // sits within the $500, and grandfathered non-collect says it doesn't count — informational only.
    if (!grandfathered && tradeLoss <= 0) return null;
    return (
      <div className="mt-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs">
        {grandfathered && (
          <>
            <div className="font-medium text-slate-700">
              📋 Non-Collect Revenue: {fmtUSD(bucket.nc)} — grandfathered
            </div>
            <div className="text-slate-500 mt-1">
              Non-collect deduction is not active on this deal — it doesn't count toward the bucket; RM / TL / ASM paid at gross on it.
            </div>
          </>
        )}
        {tradeLoss > 0 && (
          <div className={grandfathered ? "text-slate-700 mt-1" : "font-medium text-slate-700"}>
            🔧 Trade shortfalls {fmtUSD(tradeLoss)} deducted by Chad
            {bucket.ncCounts && bucket.nc > 0 ? ` + non-collect ${fmtUSD(bucket.nc)} = ${fmtUSD(bucket.bucket)}` : ""} — within the first $500 forgiven, so nothing comes off the commissions.
          </div>
        )}
      </div>
    );
  }

  // Plain render function (NOT a <Component/>) — returns host elements so the
  // <input> keeps focus across keystrokes instead of remounting each render.
  const renderInput = (
    label: string, gross: number, val: string, onChange: (s: string) => void,
  ) => {
    const n = Math.max(0, Number(val) || 0);
    const over = n > gross + 0.005;
    return (
      <div className="flex items-center gap-2">
        <label className="text-xs text-amber-900 font-medium w-28 shrink-0">{label}</label>
        <span className="text-amber-800">$</span>
        <input
          type="number"
          min={0}
          step={0.01}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 text-xs text-right border border-amber-300 rounded px-2 py-1 bg-white"
          disabled={saving}
        />
        <span className={`text-[11px] ${over ? "text-red-700 font-medium" : "text-amber-700"}`}>
          of {fmtUSD(gross)} gross{over ? " — exceeds gross" : ""}
        </span>
      </div>
    );
  };

  return (
    <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-amber-900">📋 {nonCollectHeadline(bucket)}</div>
          {grandfathered && (
            <div className="text-amber-800 mt-0.5">
              Non-collect {fmtUSD(bucket.nc)} is grandfathered on this deal (deduction not active) and doesn't count; the shortfall does.
            </div>
          )}
          <div className="text-amber-800 mt-1 flex flex-wrap gap-x-3">
            <span>RM: −{fmtUSD(bd.rm)}</span>
            {hasTl && <span>TL ({tlName}): −{fmtUSD(bd.tl)}</span>}
            {hasAsm && <span>ASM ({asmName}): −{fmtUSD(bd.asm)}</span>}
            {!hasTl && !hasAsm && <span className="text-amber-700 italic">{retail ? "Retail deal — 100% to RM." : "No TL/ASM on this deal — 100% to RM."}</span>}
          </div>
        </div>
        {canEdit && (hasTl || hasAsm) && !editing && (
          <button
            onClick={openEditor}
            className="px-2 py-1 text-[11px] font-medium rounded bg-amber-200 text-amber-900 hover:bg-amber-300 shrink-0"
            title="Edit per-role dollar amounts (RM / TL / ASM must total the overage)"
          >
            ✎ Edit amounts
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2 space-y-1.5">
          <div className="text-[11px] text-amber-800">
            Enter the dollar amount deducted from each role. They must total what's over the $500 allowance: <span className="font-semibold">{fmtUSD(overage)}</span>.
          </div>
          {renderInput("RM", rmGross, rmStr, setRmStr)}
          {hasTl && renderInput(`TL (${tlName})`, tlGross, tlStr, setTlStr)}
          {hasAsm && renderInput(`ASM (${asmName})`, asmGross, asmStr, setAsmStr)}
          <div className={`text-[11px] font-medium ${balanced ? "text-emerald-700" : "text-red-700"}`}>
            Allocated {fmtUSD(allocated)} of {fmtUSD(overage)} · {balanced ? "balanced ✓" : `${remaining > 0 ? "under" : "over"} by ${fmtUSD(Math.abs(remaining))}`}
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-0.5">
            <button
              onClick={save}
              disabled={saving || !balanced}
              className="px-2 py-1 text-[11px] font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={allToRm}
              disabled={saving}
              className="px-2 py-1 text-[11px] font-medium rounded bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-50"
              title="Put the full overage on the RM"
            >
              All to RM
            </button>
            <button
              onClick={() => { setEditing(false); setErr(null); }}
              disabled={saving}
              className="px-2 py-1 text-[11px] font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              Cancel
            </button>
            {err && <span className="text-[11px] text-red-700">⚠ {err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  valueClass,
  onClick,
  active,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  /** When provided, the card becomes a clickable filter button. */
  onClick?: () => void;
  /** When true, draws the active-tab ring + tinted background. */
  active?: boolean;
}) {
  const baseClass = "bg-white rounded-xl shadow p-4 transition-all";
  const interactiveClass = onClick
    ? " text-left w-full cursor-pointer hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
    : "";
  const activeClass = active ? " ring-2 ring-bardin-orange shadow-md bg-bardin-orange/5" : "";
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
        {active && (
          <span className="text-[10px] font-bold text-bardin-orange uppercase tracking-wide">Filtering</span>
        )}
      </div>
      <div className={"text-2xl font-semibold mt-1 " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={baseClass + interactiveClass + activeClass}>
        {inner}
      </button>
    );
  }
  return <div className={baseClass + activeClass}>{inner}</div>;
}
